// ============================================================
// SheetMind PRO v15 — PREMIUM UI + TEMPLATE SYSTEM
// Updated: Save/Update logic, Free 4-table+24h cooldown,
//          bKash Premium with Admin Approval flow
// ============================================================
import { initializeApp } from "firebase/app";
import { runAICommand, parseColorCommands, parseFindReplace, parseAutoSerial, parseMergeColumns, parseSplitColumn, parseConditionalRule, parseValidateCommand, parseDaysBetween, parsePercentOfTotal, parseGroupCount } from "./aiCommandEngine";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  onSnapshot,
  serverTimestamp,
  getDocs,
  increment,
} from "firebase/firestore";
import {
  getDatabase,
  ref,
  set,
  onValue,
  onDisconnect,
  serverTimestamp as rtServerTimestamp,
  push,
  remove,
  query,
  limitToLast,
} from "firebase/database";
import { jsPDF } from "jspdf";
import emailjs from "@emailjs/browser";
import { matchHelpQuestion, HELP_SUGGESTIONS } from "./helpBot";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";


// ============================================================
// FIREBASE CONFIG
// ============================================================
const firebaseConfig = {
  apiKey: "AIzaSyBFqaj-vl_W0sBAde_-XdbJPtKYI_tA3Wk",
  databaseURL: "https://smart-sheet-pro-default-rtdb.firebaseio.com",
  authDomain: "smart-sheet-pro.firebaseapp.com",
  projectId: "smart-sheet-pro",
  storageBucket: "smart-sheet-pro.firebasestorage.app",
  messagingSenderId: "1005248265803",
  appId: "1:1005248265803:web:5b173115e8198633cdd7ce",
  measurementId: "G-NCY4VFHVV5",
};

// ============================================================
// CONSTANTS
// ============================================================
const ADMIN_EMAIL = "iftia5061@gmail.com";
const EMAILJS_SERVICE_ID  = "service_0zyq2ca";
const EMAILJS_TEMPLATE_ID = "template_dwjzz2e";
const EMAILJS_SUPPORT_TEMPLATE_ID = "template_filjp2c";
const EMAILJS_PUBLIC_KEY  = "cWcBcKRYNCSAPyFQc";

const FREE_TABLE_LIMIT = 4;
const BKASH_NUMBER = "01825453585";
const CHECKOUT_FREE_LINK = "";
const CHECKOUT_MONTHLY_LINK = "https://ifti.gumroad.com/l/ydknwu";
const CHECKOUT_YEARLY_LINK = "https://ifti.gumroad.com/l/sfzmsb";
const CHECKOUT_DESKTOP_LINK = "https://ifti.gumroad.com/l/zckhcg";
const CHECKOUT_PHONE_LINK = "https://ifti.gumroad.com/l/qpmsr";

const CHECKOUT_WEB_LINK = CHECKOUT_MONTHLY_LINK;
const CHECKOUT_APP_LINK = CHECKOUT_DESKTOP_LINK;
const SUPPORT_LINK = "mailto:iftia5061@gmail.com?subject=SheetMind%20Payment%20Support";
const AUTOSAVE_DELAY = 1500;
const AI_FREE_TABLE_LIMIT = 2;
const AI_TABLE_ENDPOINT = "/api/ai-table";
const ENABLE_AI_TABLE = true;
const FREE_COLLAB_LIMIT = 3; // Free plan max collaborators
const TRIAL_DAYS = 14; // New signups get all premium features free for this many days
// ↓ REPLACE with your real AdSense Publisher ID
// Line 51-57 এ এই অংশটা replace করো:
const ADSENSE_PUB_ID = "ca-pub-5628645711343874";

// ↓ Paste your YouTube video ID here once uploaded (the part after "v=" in the URL)
const DEMO_VIDEO_ID = "ZGat5utwCcc";
const AD_SLOTS = {
  sidebar: "2562908490",
  banner:  "7361245890",
  inFeed:  "4128956703",
};

// ============================================================
// FIREBASE INIT
// ============================================================
let app, auth, db, rtdb, provider;
try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  rtdb = getDatabase(app);
  provider = new GoogleAuthProvider();
} catch (e) {
  console.error("Firebase init error:", e);
}

// ============================================================
// CELL OBJECT HELPERS
// ============================================================
const makeCellObj = (value = "", overrides = {}) => ({
  value: String(value ?? ""),
  color: "",
  bgColor: "",
  fontSize: "",
  fontWeight: "",
  ...overrides,
});
const cellVal = (cell) => {
  if (cell == null) return "";
  if (typeof cell === "object") return String(cell.value ?? "");
  return String(cell);
};
const migrateRow = (row, columns) => {
  const migrated = {};
  for (const col of columns) {
    const existing = row[col];
    if (existing && typeof existing === "object" && "value" in existing) {
      migrated[col] = existing;
    } else {
      migrated[col] = makeCellObj(existing ?? "");
    }
  }
  return migrated;
};
const makeBlankRow = (columns, id) => {
  const row = {};
  for (const col of columns) {
    row[col] = makeCellObj(col === "ID" ? id : "");
  }
  return row;
};

// ============================================================
// TAB FACTORY
// ============================================================
const makeNewTab = (title, columns, rowCount = 20) => {
  const id = Date.now() + Math.random();
  const cols = columns || ["ID", "Name", "Email", "Status"];
  return {
    id,
    title,
    columns: cols,
    rows: Array.from({ length: rowCount }, (_, i) => makeBlankRow(cols, i + 1)),
    columnTypes: {},
    columnFormats: {},
    tabColor: "",
  };
};

// ============================================================
// TEMPLATE DEFINITIONS
// ============================================================
const TEMPLATES = [
  {
    id: "budget",
    icon: "💰",
    name: "Monthly Budget",
    description: "Track income, expenses and savings",
    color: "#10b981",
    columns: ["ID", "Category", "Description", "Budgeted", "Actual", "Variance", "Status"],
    sampleRows: [
      ["1","Income","Salary","","","",""],
      ["2","Income","Freelance","","","",""],
      ["3","Housing","Rent","","","",""],
      ["4","Housing","Utilities","","","",""],
      ["5","Food","Groceries","","","",""],
      ["6","Food","Dining Out","","","",""],
      ["7","Transport","Fuel","","","",""],
      ["8","Entertainment","Streaming","","","",""],
      ["9","Health","Insurance","","","",""],
      ["10","Savings","Emergency Fund","","","",""],
    ],
  },
  {
    id: "inventory",
    icon: "📦",
    name: "Inventory Tracker",
    description: "Manage products, stock and suppliers",
    color: "#6366f1",
    columns: ["ID", "Product Name", "SKU", "Category", "Stock", "Reorder Level", "Unit Price", "Supplier"],
    sampleRows: [
      ["1","Product A","SKU-001","Electronics","","20","",""],
      ["2","Product B","SKU-002","Clothing","","15","",""],
      ["3","Product C","SKU-003","Food","","50","",""],
    ],
  },
  {
    id: "schedule",
    icon: "📅",
    name: "Project Schedule",
    description: "Plan tasks, deadlines and assignees",
    color: "#f59e0b",
    columns: ["ID", "Task", "Assignee", "Start Date", "Due Date", "Priority", "Progress", "Status"],
    sampleRows: [
      ["1","Research","","","","High","0%","Pending"],
      ["2","Design","","","","High","0%","Pending"],
      ["3","Development","","","","Medium","0%","Pending"],
      ["4","Testing","","","","Medium","0%","Pending"],
      ["5","Deployment","","","","Low","0%","Pending"],
    ],
  },
  {
    id: "crm",
    icon: "🤝",
    name: "CRM / Contacts",
    description: "Manage leads, clients and deals",
    color: "#ec4899",
    columns: ["ID", "Name", "Company", "Email", "Phone", "Stage", "Deal Value", "Last Contact"],
    sampleRows: [
      ["1","","","","","Lead","",""],
      ["2","","","","","Prospect","",""],
      ["3","","","","","Customer","",""],
    ],
  },
  {
    id: "payroll",
    icon: "💳",
    name: "Payroll Sheet",
    description: "Salaries, bonuses and deductions",
    color: "#22d3ee",
    columns: ["ID", "Name", "Position", "Basic Salary", "Bonus", "Deductions", "Net Salary", "Status"],
    sampleRows: Array.from({length: 8}, (_, i) => [String(i+1),"","","","","","",""]),
  },
  {
    id: "student",
    icon: "🎓",
    name: "Student Grades",
    description: "Track students, marks and grades",
    color: "#8b5cf6",
    columns: ["ID", "Name", "Roll", "Class", "Math", "Science", "English", "Total", "Grade"],
    sampleRows: Array.from({length: 10}, (_, i) => [String(i+1),"","","","","","","",""]),
  },
  {
    id: "blank",
    icon: "✨",
    name: "Blank Sheet",
    description: "Start fresh with a clean canvas",
    color: "#64748b",
    columns: ["ID", "Name", "Email", "Status"],
    sampleRows: Array.from({length: 8}, (_, i) => [String(i+1),"","",""]),
  },
];

// ============================================================
// REAL GOOGLE ADSENSE COMPONENT
// ============================================================
function AdSense({ slot, format = "auto", style = {}, isDark, label = "Advertisement" }) {
  const adRef = useRef(null);
  useEffect(() => {
    try {
      if (window.adsbygoogle && adRef.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {}
  }, [slot]);

  return (
    <div
      className={`relative rounded-2xl overflow-hidden border ${
        isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-slate-50"
      }`}
      style={style}
    >
      <span
        className={`absolute top-1.5 left-3 text-[8px] font-black uppercase tracking-widest z-10 ${
          isDark ? "text-slate-600" : "text-slate-400"
        }`}
      >
        {label}
      </span>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: "block", ...style }}
        data-ad-client={ADSENSE_PUB_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}

function SidebarAd({ isDark }) {
  return (
    <AdSense
      slot={AD_SLOTS.sidebar}
      format="vertical"
      isDark={isDark}
      style={{ minHeight: 280, width: "100%", paddingTop: 20 }}
    />
  );
}

function BannerAd({ isDark }) {
  return (
    <AdSense
      slot={AD_SLOTS.banner}
      format="horizontal"
      isDark={isDark}
      style={{ minHeight: 90, width: "100%", paddingTop: 20 }}
    />
  );
}

function InFeedAd({ isDark }) {
  return (
    <AdSense
      slot={AD_SLOTS.inFeed}
      format="fluid"
      isDark={isDark}
      label="Sponsored"
      style={{ minHeight: 120, width: "100%", paddingTop: 18 }}
    />
  );
}

// ============================================================
// TEMPLATE GALLERY MODAL
// ============================================================
function TemplateGallery({ onSelect, onClose, isDark }) {
  const [hovered, setHovered] = useState(null);
  const bg = isDark ? "bg-[#080d1a]" : "bg-slate-50";
  const card = isDark ? "bg-[#0f1929] border-slate-800" : "bg-white border-slate-200";
  const text = isDark ? "text-slate-100" : "text-slate-900";
  const sub = isDark ? "text-slate-500" : "text-slate-400";

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div
        className={`${bg} ${text} w-full max-w-3xl rounded-[2rem] border ${
          isDark ? "border-slate-800" : "border-slate-200"
        } shadow-2xl overflow-hidden`}
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <div className={`px-8 pt-8 pb-6 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-black uppercase tracking-tight">
                <span className="bg-gradient-to-r from-indigo-400 to-sky-400 bg-clip-text text-transparent">
                  Template Gallery
                </span>
              </h2>
              <p className={`text-xs mt-1 ${sub}`}>
                Choose a template to start instantly — all pre-configured
              </p>
            </div>
            <button
              onClick={onClose}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                isDark
                  ? "bg-slate-800 hover:bg-slate-700 text-slate-400"
                  : "bg-slate-100 hover:bg-slate-200 text-slate-600"
              }`}
            >
              ✕
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-8" style={{ scrollbarWidth: "thin" }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {TEMPLATES.map((tmpl) => (
              <button
                key={tmpl.id}
                onMouseEnter={() => setHovered(tmpl.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSelect(tmpl)}
                className={`${card} border rounded-[1.5rem] p-5 text-left transition-all duration-200 group relative overflow-hidden`}
                style={{
                  transform: hovered === tmpl.id ? "translateY(-3px)" : "none",
                  boxShadow: hovered === tmpl.id ? `0 12px 32px ${tmpl.color}22` : "none",
                  borderColor: hovered === tmpl.id ? tmpl.color + "55" : undefined,
                }}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: `linear-gradient(to right, ${tmpl.color}, ${tmpl.color}44)` }}
                />
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center text-2xl mb-4"
                  style={{ backgroundColor: tmpl.color + "20" }}
                >
                  {tmpl.icon}
                </div>
                <h3 className={`font-black text-sm uppercase tracking-wide ${text} mb-1`}>{tmpl.name}</h3>
                <p className={`text-[11px] ${sub} leading-relaxed`}>{tmpl.description}</p>
                <div className="flex items-center gap-1.5 mt-3">
                  {tmpl.columns.slice(0, 4).map((col, i) => (
                    <span
                      key={i}
                      className="text-[8px] font-black px-2 py-0.5 rounded-full uppercase"
                      style={{ backgroundColor: tmpl.color + "18", color: tmpl.color }}
                    >
                      {col}
                    </span>
                  ))}
                  {tmpl.columns.length > 4 && (
                    <span className={`text-[8px] font-black ${sub}`}>+{tmpl.columns.length - 4}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CHART COMPONENTS
// ============================================================
function MiniBarChart({ data, labels, color = "#6366f1" }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!canvasRef.current || !data.length) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const max = Math.max(...data, 1);
    const barW = (W - 20) / data.length - 4;
    data.forEach((val, i) => {
      const h = (val / max) * (H - 30);
      const x = 10 + i * (barW + 4);
      const y = H - 25 - h;
      const grad = ctx.createLinearGradient(x, y, x, H - 25);
      grad.addColorStop(0, color + "ff");
      grad.addColorStop(1, color + "44");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x, y, barW, h, 3);
      ctx.fill();
      if (labels[i]) {
        ctx.fillStyle = "#94a3b8";
        ctx.font = "9px monospace";
        ctx.textAlign = "center";
        ctx.fillText(String(labels[i]).slice(0, 5), x + barW / 2, H - 8);
      }
    });
  }, [data, labels, color]);
  return <canvas ref={canvasRef} width={280} height={120} className="w-full" />;
}

function MiniPieChart({ data, labels }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const COLORS = ["#6366f1","#22d3ee","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899"];
    if (!canvasRef.current || !data.length) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const total = data.reduce((a, b) => a + b, 0);
    if (!total) return;
    let start = -Math.PI / 2;
    const cx = W / 2 - 20, cy = H / 2, r = Math.min(cx, cy) - 10;
    data.forEach((val, i) => {
      const slice = (val / total) * 2 * Math.PI;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, start, start + slice);
      ctx.closePath();
      ctx.fillStyle = COLORS[i % COLORS.length];
      ctx.fill();
      ctx.strokeStyle = "#0f172a";
      ctx.lineWidth = 2;
      ctx.stroke();
      start += slice;
    });
    labels.slice(0, 5).forEach((lbl, i) => {
      ctx.fillStyle = COLORS[i % COLORS.length];
      ctx.fillRect(W - 85, 10 + i * 18, 10, 10);
      ctx.fillStyle = "#94a3b8";
      ctx.font = "9px monospace";
      ctx.textAlign = "left";
      ctx.fillText(String(lbl).slice(0, 8), W - 70, 19 + i * 18);
    });
  }, [data, labels]);
  return <canvas ref={canvasRef} width={280} height={130} className="w-full" />;
}

// ============================================================
// BLOG DATA
// ============================================================
const BLOG_POSTS = [
  {
    slug: "ai-spreadsheet-automation-tips",
    title: "5 Ways to Use AI to Automate Your Spreadsheet Work",
    excerpt: "Discover practical ways AI-powered tools like SheetMind can cut hours off your weekly data entry and reporting tasks.",
    date: "2026-04-12",
    readTime: "5 min read",
    content: [
      "Manual spreadsheet work is one of the biggest time drains in small offices and freelance businesses. Every week, hours disappear into copying numbers between sheets, formatting tables, and double-checking totals by hand. AI-powered tools are changing that by taking over the repetitive parts of the job so people can focus on decisions instead of data entry.",
      "The first big win is automatic table generation. Instead of building a spreadsheet structure from scratch, you can describe what you need in plain language and let the AI suggest columns, data types, and starting rows. This is especially useful for recurring formats like invoices, inventory logs, or weekly sales reports.",
      "Second, AI can help clean messy data. Inconsistent date formats, duplicate entries, and typos are common in spreadsheets that have been edited by multiple people over time. AI-assisted tools can flag these issues automatically, saving the tedious process of scanning row by row.",
      "Third, smart autocomplete speeds up data entry itself. As you type, the system can suggest likely values based on patterns already in your sheet, which is a small change that adds up to real time savings across hundreds of rows.",
      "Fourth, AI can generate quick summaries of your data without writing a single formula. Asking a plain question like 'what was our highest expense category last month' can return an answer instantly, instead of requiring a pivot table or chart.",
      "Finally, automation extends to formatting and exports. Once your data is ready, AI tools can apply consistent styling and prepare clean Excel or PDF exports for clients, removing one more manual step from your workflow.",
      "Tools like SheetMind combine several of these capabilities directly inside a browser-based spreadsheet, so you do not need separate software for each task. As AI features mature, the gap between manual spreadsheet work and automated data management will keep narrowing, making it easier for small teams to operate at the speed of much larger ones.",
    ],
  },
  {
    slug: "spreadsheet-for-small-business",
    title: "Choosing the Right Spreadsheet Tool for a Small Business",
    excerpt: "A practical comparison of what small business owners should look for when picking spreadsheet software in 2026.",
    date: "2026-03-28",
    readTime: "4 min read",
    content: [
      "Small business owners often start with whatever spreadsheet tool is already installed on their computer, without comparing alternatives. As the business grows, the limitations of that choice become obvious: no real-time collaboration, no cloud backup, and clunky sharing with clients or employees.",
      "The first thing to evaluate is cloud accessibility. A spreadsheet that only lives on one computer is a liability — if that machine is lost, stolen, or simply not nearby, the business loses access to critical records like inventory, payroll, or client lists. Cloud-based tools solve this by keeping data backed up automatically and accessible from any device.",
      "Second, consider collaboration. Many small businesses involve more than one person touching the same records, whether it is a manager updating inventory while a bookkeeper checks payroll. Tools that support real-time multi-user editing with permission controls prevent the common problem of conflicting versions emailed back and forth.",
      "Third, look at export flexibility. Even if your daily work happens inside a spreadsheet app, clients, accountants, and government forms often require Excel or PDF files. A good tool should let you export cleanly without reformatting everything by hand.",
      "Fourth, cost matters, but so does the upgrade path. Many businesses start with free tiers and only need to pay once their usage grows past a certain point. Look for tools with a clear, affordable upgrade path rather than an all-or-nothing pricing model.",
      "Finally, ease of use matters more than feature count for most small businesses. A tool packed with advanced functions is not helpful if your team avoids using it because it feels complicated. Simpler, faster interfaces tend to get adopted more consistently across a small team.",
      "Weighing these factors against your specific workflow — inventory, payroll, client records, or project tracking — will help you choose a spreadsheet tool that actually gets used day to day, instead of one that gets abandoned after the first few weeks.",
    ],
  },
  {
    slug: "excel-vs-cloud-spreadsheets",
    title: "Excel vs Cloud Spreadsheets: What's the Real Difference?",
    excerpt: "Excel isn't going anywhere, but cloud spreadsheet tools solve problems that traditional desktop software simply can't.",
    date: "2026-03-10",
    readTime: "4 min read",
    content: [
      "Microsoft Excel has been the default spreadsheet tool for decades, and for good reason — it is powerful, familiar, and deeply integrated into many businesses. But cloud-based spreadsheet tools have grown for a specific set of reasons that are worth understanding before deciding which fits your work better.",
      "The most obvious difference is access. A file saved locally in Excel lives on one machine unless manually copied or emailed elsewhere. Cloud spreadsheets are accessible from any device with a browser, which matters for anyone who works across a laptop, phone, or multiple office computers.",
      "Real-time collaboration is the second major difference. While newer versions of Excel support some shared editing through OneDrive, cloud-native tools are generally built around simultaneous multi-user editing from day one, with permission systems that control exactly who can view or edit specific tables.",
      "Backup and version safety also differ. Local Excel files can be lost to a crashed hard drive or accidental deletion unless you have a separate backup routine. Cloud tools save automatically and continuously, which removes one common point of failure for small businesses without dedicated IT support.",
      "On the other hand, Excel still wins for extremely complex formula work, large-scale financial modeling, and offline use in places with unreliable internet. Cloud tools are generally better suited to day-to-day operational data — inventory, client records, schedules — rather than heavy financial analysis.",
      "For many small teams, the practical answer is not choosing one over the other completely, but using cloud spreadsheet tools for collaborative, everyday record-keeping, while reserving Excel for deep analytical work that benefits from its mature formula engine.",
    ],
  },
];

function BlogList({ onSelectPost, isDark }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {BLOG_POSTS.map((post) => (
        <button
          key={post.slug}
          onClick={() => onSelectPost(post.slug)}
          className="flex h-full flex-col rounded-[1.5rem] border border-[#c8d7ec] bg-white p-5 text-left shadow-[0_18px_45px_rgba(43,76,126,0.09)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_65px_rgba(43,76,126,0.14)]"
        >
          <p className="mb-2 text-[9px] font-black uppercase tracking-[0.24em] text-[#8aa0bd]">
            {post.date} • {post.readTime}
          </p>
          <h3 className="mb-2 text-sm font-black uppercase tracking-tight text-[#172033]">{post.title}</h3>
          <p className="text-xs leading-relaxed text-[#5f6f89]">{post.excerpt}</p>
          <span className="mt-4 text-[10px] font-black uppercase tracking-widest text-[#2457d6]">Read article →</span>
        </button>
      ))}
    </div>
  );
}

function BlogPost({ slug, onBack, isDark }) {
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return null;
  return (
    <div className="rounded-[1.5rem] border border-[#c8d7ec] bg-white p-6 shadow-[0_18px_45px_rgba(43,76,126,0.09)] sm:p-8">
      <button onClick={onBack} className="mb-5 text-[10px] font-black uppercase tracking-widest text-[#2457d6]">← Back to blog</button>
      <p className="mb-2 text-[9px] font-black uppercase tracking-[0.24em] text-[#8aa0bd]">{post.date} • {post.readTime}</p>
      <h1 className="mb-5 text-xl font-black uppercase tracking-tight text-[#172033] sm:text-2xl">{post.title}</h1>
      <div className="space-y-4">
        {post.content.map((para, i) => (
          <p key={i} className="text-sm leading-relaxed text-[#5f6f89]">{para}</p>
        ))}
      </div>
      <div className="mt-6">
        <InFeedAd isDark={isDark} />
      </div>
    </div>
  );
}

function BlogPage({ onBackToHome, isDark }) {
  const [selectedSlug, setSelectedSlug] = useState(null);
  return (
    <div className="min-h-screen bg-[#eef4ff] text-[#172033] font-sans" style={{ fontFamily: "'DM Mono','Fira Mono',monospace" }}>
      <main className="mx-auto w-full max-w-[1480px] px-4 pb-10 pt-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={onBackToHome} className="text-[10px] font-black uppercase tracking-widest text-[#2457d6]">← Back to SheetMind</button>
        </div>
        <section className="rounded-[2rem] border border-[#c8d7ec] bg-white/78 p-4 shadow-[0_30px_95px_rgba(43,76,126,0.14)] backdrop-blur-xl sm:p-6 lg:p-8">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.34em] text-[#3978d8]">SheetMind Blog</p>
          <h1 className="mb-6 text-2xl font-black uppercase tracking-tight text-[#172033] sm:text-3xl">
            Tips, guides, and updates
          </h1>
          {selectedSlug ? (
            <BlogPost slug={selectedSlug} onBack={() => setSelectedSlug(null)} isDark={isDark} />
          ) : (
            <BlogList onSelectPost={setSelectedSlug} isDark={isDark} />
          )}
        </section>
      </main>
    </div>
  );
}

const DEMO_COLUMNS_INIT = ["Item", "Quantity", "Unit Price", "Status"];
const DEMO_ROWS_INIT = [
  { Item: "Office Chairs", Quantity: "12", "Unit Price": "85", Status: "In Stock" },
  { Item: "Laptops", Quantity: "5", "Unit Price": "650", Status: "Low Stock" },
  { Item: "Desk Lamps", Quantity: "20", "Unit Price": "15", Status: "In Stock" },
  { Item: "Notebooks", Quantity: "0", "Unit Price": "3", Status: "Out of Stock" },
];

function DemoPage({ onBackToHome, onSignup, isDark }) {
  const [columns, setColumns] = useState(DEMO_COLUMNS_INIT);
  const [rows, setRows] = useState(DEMO_ROWS_INIT);
  const [showGatePrompt, setShowGatePrompt] = useState(false);

  const updateCell = (rowIdx, col, value) => {
    const next = rows.map((r, i) => (i === rowIdx ? { ...r, [col]: value } : r));
    setRows(next);
  };

  const addRow = () => {
    const blank = {};
    columns.forEach((c) => (blank[c] = ""));
    setRows([...rows, blank]);
  };

  const addColumn = () => {
    const name = "New Column " + (columns.length + 1);
    setColumns([...columns, name]);
    setRows(rows.map((r) => ({ ...r, [name]: "" })));
  };

  const deleteColumn = (colName) => {
    if (columns.length <= 1) return;
    setColumns(columns.filter((c) => c !== colName));
    setRows(rows.map((r) => { const nr = { ...r }; delete nr[colName]; return nr; }));
  };

  const gated = (fn) => () => { setShowGatePrompt(true); };

  const [introPhase, setIntroPhase] = useState(() => {
    try { return sessionStorage.getItem("ssIntroSeen") ? "gone" : "visible"; }
    catch (e) { return "visible"; }
  });

  useEffect(() => {
    if (introPhase !== "visible") return;
    const dismiss = () => setIntroPhase("leaving");
    const autoTimer = setTimeout(dismiss, 3200);
    window.addEventListener("wheel", dismiss, { passive: true });
    window.addEventListener("touchstart", dismiss, { passive: true });
    window.addEventListener("keydown", dismiss);
    window.addEventListener("click", dismiss);
    return () => {
      clearTimeout(autoTimer);
      window.removeEventListener("wheel", dismiss);
      window.removeEventListener("touchstart", dismiss);
      window.removeEventListener("keydown", dismiss);
      window.removeEventListener("click", dismiss);
    };
  }, [introPhase]);

  useEffect(() => {
    if (introPhase !== "leaving") return;
    try { sessionStorage.setItem("ssIntroSeen", "1"); } catch (e) {}
    const t = setTimeout(() => setIntroPhase("gone"), 700);
    return () => clearTimeout(t);
  }, [introPhase]);

  return (
    <>
    {introPhase !== "gone" && (
      <div
        className={`fixed inset-0 z-[999] flex flex-col items-center justify-center transition-all duration-700 ${introPhase === "leaving" ? "opacity-0 -translate-y-10 pointer-events-none" : "opacity-100"}`}
        style={{ background: "radial-gradient(ellipse 70% 60% at 50% 20%, rgba(99,102,241,0.22), transparent 60%), linear-gradient(160deg, #0a1628 0%, #0f1f38 55%, #0a1628 100%)" }}
      >
        <div style={{ perspective: "900px" }}>
          <div className="relative" style={{ width: "120px", height: "120px", transformStyle: "preserve-3d", animation: "ssCubeSpin 6s linear infinite", margin: "0 auto" }}>
            {[
              { t: "translateZ(60px)" },
              { t: "rotateY(180deg) translateZ(60px)" },
              { t: "rotateY(90deg) translateZ(60px)" },
              { t: "rotateY(-90deg) translateZ(60px)" },
              { t: "rotateX(90deg) translateZ(60px)" },
              { t: "rotateX(-90deg) translateZ(60px)" },
            ].map((face, i) => (
              <div key={i} className="absolute grid grid-cols-3 grid-rows-3 gap-[2px] rounded-lg border border-white/20 p-1.5" style={{ width: "120px", height: "120px", transform: face.t, background: "linear-gradient(135deg, rgba(99,102,241,0.45), rgba(15,31,56,0.9))" }}>
                {Array.from({ length: 9 }).map((_, c) => (
                  <div key={c} className="rounded-[2px]" style={{ background: (c + i) % 4 === 0 ? "rgba(244,183,64,0.6)" : "rgba(255,255,255,0.12)" }} />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex gap-[2px]" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
          {"SheetMind".split("").map((ch, i) => (
            <span
              key={i}
              className="text-3xl font-bold text-white sm:text-4xl"
              style={{ opacity: 0, animation: `ssLetterIn 0.5s ease-out ${0.3 + i * 0.05}s forwards` }}
            >
              {ch}
            </span>
          ))}
        </div>

        <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7fb1ff]" style={{ opacity: 0, animation: "ssLetterIn 0.6s ease-out 1.1s forwards" }}>
          Scroll or click to continue
        </p>

        <style>{`
          @keyframes ssCubeSpin {
            from { transform: rotateX(-15deg) rotateY(0deg); }
            to { transform: rotateX(-15deg) rotateY(360deg); }
          }
          @keyframes ssLetterIn {
            from { opacity: 0; transform: translateY(14px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    )}
    <div className="min-h-screen text-[#e7edf7]" style={{ fontFamily: "'Inter',sans-serif", background: "radial-gradient(ellipse 90% 40% at 50% -5%, rgba(36,87,214,0.20), transparent 60%), radial-gradient(ellipse 70% 45% at 100% 25%, rgba(244,183,64,0.07), transparent 60%), radial-gradient(ellipse 60% 40% at 0% 70%, rgba(99,102,241,0.10), transparent 60%), linear-gradient(180deg, #0a1628 0%, #0e1c33 35%, #0b1930 70%, #0a1628 100%)" }}>
      <main className="mx-auto w-full max-w-[1480px] px-4 pb-10 pt-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={onBackToHome} className="text-[10px] font-bold uppercase tracking-widest text-[#2457d6]">← Back to SheetMind</button>
          <button onClick={onSignup} className="rounded-xl bg-[#2457d6] px-4 py-2 text-[11px] font-semibold text-white transition-all hover:-translate-y-0.5">Sign up free</button>
        </div>

        <section className="rounded-[2rem] border border-[#dde6f5] bg-white/90 p-4 shadow-[0_30px_95px_rgba(43,76,126,0.14)] backdrop-blur-xl sm:p-6 lg:p-8">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.34em] text-[#2457d6]" style={{ fontFamily: "'DM Mono',monospace" }}>Live Demo — No Signup Required</p>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-[#0f1f38] sm:text-3xl" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            Try SheetMind right here
          </h1>
          <p className="mb-6 max-w-2xl text-[14px] leading-relaxed text-[#475569]">
            Edit cells, add rows or columns below. This demo runs only in your browser — nothing is saved.
            Sign up free to save your work, export to Excel/PDF, and invite collaborators.
          </p>

          <div className="mb-4 flex flex-wrap gap-2.5">
            <button onClick={addRow} className="rounded-xl border border-[#dde6f5] bg-[#f7fbff] px-4 py-2 text-[11px] font-semibold text-[#2457d6] transition-all hover:-translate-y-0.5 hover:bg-[#eef5ff]">+ Add Row</button>
            <button onClick={addColumn} className="rounded-xl border border-[#dde6f5] bg-[#f7fbff] px-4 py-2 text-[11px] font-semibold text-[#2457d6] transition-all hover:-translate-y-0.5 hover:bg-[#eef5ff]">+ Add Column</button>
            <button onClick={gated()} className="rounded-xl border border-[#dde6f5] bg-[#f7fbff] px-4 py-2 text-[11px] font-semibold text-[#2457d6] transition-all hover:-translate-y-0.5 hover:bg-[#eef5ff]">Export to PDF</button>
            <button onClick={gated()} className="rounded-xl border border-[#dde6f5] bg-[#f7fbff] px-4 py-2 text-[11px] font-semibold text-[#2457d6] transition-all hover:-translate-y-0.5 hover:bg-[#eef5ff]">Export to Excel</button>
            <button onClick={gated()} className="rounded-xl border border-[#dde6f5] bg-[#f7fbff] px-4 py-2 text-[11px] font-semibold text-[#2457d6] transition-all hover:-translate-y-0.5 hover:bg-[#eef5ff]">Invite Collaborator</button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-[#c8d7ec] shadow-[0_12px_35px_rgba(43,76,126,0.10)]">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #1a2f52 0%, #0f1f38 100%)" }}>
                  {columns.map((col, idx) => (
                    <th key={col} className={`px-4 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wide text-white ${idx !== columns.length - 1 ? "border-r border-white/10" : ""}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{col}</span>
                        {columns.length > 1 && (
                          <button onClick={() => deleteColumn(col)} className="flex h-5 w-5 items-center justify-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-white" title="Delete column">×</button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => (
                  <tr key={rowIdx} className={`transition-colors ${rowIdx % 2 === 0 ? "bg-white" : "bg-[#f7fbff]"} hover:bg-[#eef5ff]`}>
                    {columns.map((col, idx) => (
                      <td key={col} className={`px-1 py-1 ${idx !== columns.length - 1 ? "border-r border-[#eef2f8]" : ""} border-b border-[#eef2f8]`}>
                        <input
                          value={row[col] ?? ""}
                          onChange={(e) => updateCell(rowIdx, col, e.target.value)}
                          className="w-full rounded-lg border border-transparent bg-transparent px-3 py-2.5 text-[13px] text-[#172033] outline-none transition-all focus:border-[#2457d6]/50 focus:bg-white focus:shadow-[0_0_0_3px_rgba(36,87,214,0.1)]"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showGatePrompt && (
            <div className="mt-5 rounded-2xl border border-[#2457d6]/20 bg-[#f3f8ff] p-5 text-center">
              <p className="text-[13px] font-medium text-[#0f1f38]">
                That feature needs a free account. Sign up to export your sheet or invite teammates.
              </p>
              <button onClick={onSignup} className="mt-3 rounded-xl bg-[#2457d6] px-5 py-2 text-[11px] font-semibold text-white transition-all hover:-translate-y-0.5">
                Sign up free — it takes 10 seconds
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
    </>
  );
}

function ContactPage({ onBackToHome, isDark }) {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent("Support request from " + formData.name);
    const body = encodeURIComponent(formData.message + "\n\nReply to: " + formData.email);
    window.location.href = `mailto:iftia5061@gmail.com?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-[#eef4ff] text-[#172033] font-sans" style={{ fontFamily: "'DM Mono','Fira Mono',monospace" }}>
      <main className="mx-auto w-full max-w-[1480px] px-4 pb-10 pt-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={onBackToHome} className="text-[10px] font-black uppercase tracking-widest text-[#2457d6]">← Back to SheetMind</button>
        </div>
        <section className="rounded-[2rem] border border-[#c8d7ec] bg-white/78 p-4 shadow-[0_30px_95px_rgba(43,76,126,0.14)] backdrop-blur-xl sm:p-6 lg:p-8">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.34em] text-[#3978d8]">Get in Touch</p>
          <h1 className="mb-4 text-2xl font-black uppercase tracking-tight text-[#172033] sm:text-3xl">
            Contact SheetMind Support
          </h1>
          <p className="mb-6 max-w-2xl text-sm leading-relaxed text-[#5f6f89]">
            Have a question about your account, billing, or a feature request? Send us a message using the form
            below, or email us directly. We typically respond within one to two business days. You can also use
            the live chat widget in the bottom corner of the app for faster help with common questions.
          </p>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <form onSubmit={handleSubmit} className="rounded-2xl border border-[#d8e4f3] bg-[#f7fbff] p-5">
              <div className="mb-4">
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-[#36516f]">Name</label>
                <input
                  required
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-xl border border-[#c8d7ec] bg-white px-3 py-2 text-sm text-[#172033] outline-none focus:border-[#2457d6]"
                />
              </div>
              <div className="mb-4">
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-[#36516f]">Email</label>
                <input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-xl border border-[#c8d7ec] bg-white px-3 py-2 text-sm text-[#172033] outline-none focus:border-[#2457d6]"
                />
              </div>
              <div className="mb-4">
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-[#36516f]">Message</label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full rounded-xl border border-[#c8d7ec] bg-white px-3 py-2 text-sm text-[#172033] outline-none focus:border-[#2457d6]"
                />
              </div>
              <button
                type="submit"
                className="rounded-xl border border-[#3978d8]/20 bg-[#2457d6] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white transition-all hover:-translate-y-0.5"
              >
                Send Message
              </button>
              {sent && (
                <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-emerald-600">
                  Your email app should now be open. If not, email us directly below.
                </p>
              )}
            </form>

            <div className="rounded-2xl border border-[#d8e4f3] bg-[#f7fbff] p-5">
              <h3 className="mb-3 text-xs font-black uppercase tracking-wide text-[#2457d6]">Other ways to reach us</h3>
              <p className="mb-2 text-xs leading-relaxed text-[#5f6f89]">
                Email: <a href="mailto:iftia5061@gmail.com" className="text-[#2457d6] underline">iftia5061@gmail.com</a>
              </p>
              <p className="mb-2 text-xs leading-relaxed text-[#5f6f89]">
                Live chat: available inside the SheetMind app, bottom-right corner.
              </p>
              <p className="text-xs leading-relaxed text-[#5f6f89]">
                For billing or Premium activation questions, please include your account email so we can locate
                your subscription quickly.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

// ============================================================
// SCROLL-REVEAL HELPER
// ============================================================
function RevealOnScroll({ children, className }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transition: "opacity 0.8s ease, transform 0.8s ease" }}
    >
      {children}
    </div>
  );
}

// ============================================================
// SHOWCASE PAGE — plays before the landing page
// ============================================================
function ShowcasePage({ onContinue, isDark }) {
  const [typedPrompt, setTypedPrompt] = useState("");
  const fullPrompt = "Add my team members";
  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i++;
      setTypedPrompt(fullPrompt.slice(0, i));
      if (i >= fullPrompt.length) clearInterval(t);
    }, 60);
    return () => clearInterval(t);
  }, []);

  const [formulaTyped, setFormulaTyped] = useState("");
  const fullFormula = "=SUM(B2:B9)";
  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i++;
      setFormulaTyped(fullFormula.slice(0, i));
      if (i >= fullFormula.length) clearInterval(t);
    }, 90);
    return () => clearInterval(t);
  }, []);

  const teamNames = ["Ifti Ahmed", "Aminul Islam", "Mominul Islam"];
  const totalTeamChars = teamNames.join("").length;
  const [teamTypedCount, setTeamTypedCount] = useState(0);
  useEffect(() => {
    let i = 0;
    let typeTimer, eraseTimer, pauseTimer;
    const startTyping = () => {
      i = 0;
      typeTimer = setInterval(() => {
        i++;
        setTeamTypedCount(i);
        if (i >= totalTeamChars) {
          clearInterval(typeTimer);
          pauseTimer = setTimeout(startErasing, 1800);
        }
      }, 55);
    };
    const startErasing = () => {
      eraseTimer = setInterval(() => {
        i--;
        setTeamTypedCount(Math.max(i, 0));
        if (i <= 0) {
          clearInterval(eraseTimer);
          pauseTimer = setTimeout(startTyping, 500);
        }
      }, 25);
    };
    startTyping();
    return () => {
      clearInterval(typeTimer);
      clearInterval(eraseTimer);
      clearTimeout(pauseTimer);
    };
  }, []);
  const getTeamRowText = (rowIndex) => {
    const before = teamNames.slice(0, rowIndex).join("").length;
    const thisLen = teamNames[rowIndex].length;
    const shown = Math.max(0, Math.min(thisLen, teamTypedCount - before));
    return teamNames[rowIndex].slice(0, shown);
  };
  const isTeamRowTyping = (rowIndex) => {
    const before = teamNames.slice(0, rowIndex).join("").length;
    const after = teamNames.slice(0, rowIndex + 1).join("").length;
    return teamTypedCount >= before && teamTypedCount < after;
  };

  const sumRows = [820, 1150, 640, 990, 1220];
  const [sumHighlightIdx, setSumHighlightIdx] = useState(-1);
  const [runningTotal, setRunningTotal] = useState(0);
  useEffect(() => {
    let idx = 0;
    let total = 0;
    let stepTimer, pauseTimer;
    const runSum = () => {
      idx = 0;
      total = 0;
      setSumHighlightIdx(-1);
      setRunningTotal(0);
      stepTimer = setInterval(() => {
        if (idx >= sumRows.length) {
          clearInterval(stepTimer);
          pauseTimer = setTimeout(runSum, 2200);
          return;
        }
        total += sumRows[idx];
        setRunningTotal(total);
        setSumHighlightIdx(idx);
        idx++;
      }, 500);
    };
    runSum();
    return () => {
      clearInterval(stepTimer);
      clearTimeout(pauseTimer);
    };
  }, []);

  return (
    <div className="min-h-screen text-white" style={{ fontFamily: "'Inter',sans-serif", background: "linear-gradient(180deg, #0a1628 0%, #0f1f38 50%, #0a1628 100%)" }}>
      <main className="mx-auto w-full max-w-[900px] px-6 py-16">
        <div className="mb-24 grid items-center gap-8 text-center sm:grid-cols-[1fr_auto] sm:text-left">
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.34em] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>This is</p>
            <h1 className="text-4xl font-black tracking-tight sm:text-5xl" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>SHEETMIND</h1>
            <p className="mt-3 text-xl font-bold text-[#b8c6e0] sm:text-2xl" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Spreadsheets, reimagined</p>
          </div>
          <div className="mx-auto sm:mx-0" style={{ perspective: "900px" }}>
            <div className="relative" style={{ width: "110px", height: "110px", transformStyle: "preserve-3d", animation: "ssCubeSpin 6s linear infinite", margin: "0 auto" }}>
              {[
                { t: "translateZ(55px)" },
                { t: "rotateY(180deg) translateZ(55px)" },
                { t: "rotateY(90deg) translateZ(55px)" },
                { t: "rotateY(-90deg) translateZ(55px)" },
                { t: "rotateX(90deg) translateZ(55px)" },
                { t: "rotateX(-90deg) translateZ(55px)" },
              ].map((face, i) => (
                <div key={i} className="absolute grid grid-cols-3 grid-rows-3 gap-[2px] rounded-lg border border-white/20 p-1.5" style={{ width: "110px", height: "110px", transform: face.t, background: "linear-gradient(135deg, rgba(99,102,241,0.45), rgba(15,31,56,0.9))" }}>
                  {Array.from({ length: 9 }).map((_, c2) => (
                    <div key={c2} className="rounded-[2px]" style={{ background: (c2 + i) % 4 === 0 ? "rgba(244,183,64,0.6)" : "rgba(255,255,255,0.12)" }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <RevealOnScroll className="mb-32 grid items-center gap-10 sm:grid-cols-2">
          <div>
            <h2 className="mb-3 text-2xl font-bold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Type it, get a table</h2>
            <p className="text-[15px] leading-relaxed text-[#b8c6e0]">Describe what you need in plain English. SheetMind builds the columns and rows for you — no formulas to learn.</p>
          </div>
          <div className="sm-glass-card p-5">
            <div className="mb-4 rounded-lg border border-white/10 bg-[#0a1628] px-3 py-2 text-[13px] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>
              {typedPrompt}<span className="animate-pulse">|</span>
            </div>
            <div className="sm-table-3d overflow-hidden rounded-lg border border-white/10" style={{ perspective: "600px" }}>
              <div className="grid grid-cols-[28px_1fr] bg-white/[0.06] text-[9px] font-bold uppercase tracking-wide text-[#7fb1ff]">
                <div className="px-2 py-1.5">#</div>
                <div className="px-2 py-1.5">Name</div>
              </div>
              {teamNames.map((name, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-[28px_1fr] border-t border-white/10 text-[12px] text-white transition-all duration-300 ${isTeamRowTyping(i) ? "sm-row-active" : ""}`}
                >
                  <div className="px-2 py-2 text-[#7fb1ff]">{i + 1}</div>
                  <div className="px-2 py-2" style={{ fontFamily: "'DM Mono',monospace" }}>
                    {getTeamRowText(i)}
                    {isTeamRowTyping(i) && <span className="animate-pulse text-[#f4b740]">|</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </RevealOnScroll>

        <RevealOnScroll className="mb-32 grid items-center gap-10 sm:grid-cols-2">
          <div className="sm-glass-card relative order-2 p-5 sm:order-1" style={{ height: "180px" }}>
            <div className="grid h-full grid-cols-4 grid-rows-3 gap-1.5">
              {["Jan", "Feb", "Mar", "Apr", "$820", "$1,150", "$640", "$990", "Done", "Active", "Pending", "Done"].map((val, i) => (
                <div key={i} className="flex items-center justify-center rounded text-[9px] font-bold text-[#7fb1ff]/70" style={{ background: "rgba(255,255,255,0.06)", fontFamily: "'DM Mono',monospace" }}>
                  {val}
                </div>
              ))}
            </div>
            {[
              { color: "#f4b740", name: "Ifti", top: "10%", left: "20%", anim: "ssCursorFloat0" },
              { color: "#34d399", name: "Amin", top: "55%", left: "60%", anim: "ssCursorFloat1" },
              { color: "#818cf8", name: "Momin", top: "30%", left: "75%", anim: "ssCursorFloat2" },
            ].map((cur, i) => (
              <div key={i} className="absolute rounded border-2" style={{ borderColor: cur.color, top: cur.top, left: cur.left, width: "42px", height: "24px", animation: `${cur.anim} 3s ease-in-out infinite` }}>
                <span className="absolute -top-4 left-0 whitespace-nowrap rounded px-1 text-[8px] font-bold text-[#0a1628]" style={{ background: cur.color }}>{cur.name}</span>
              </div>
            ))}
          </div>
          <div className="order-1 sm:order-2">
            <h2 className="mb-3 text-2xl font-bold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Work together, live</h2>
            <p className="text-[15px] leading-relaxed text-[#b8c6e0]">See exactly where your teammates are working, in real time — with their own colored cursor, just like Google Docs.</p>
          </div>
        </RevealOnScroll>

        <RevealOnScroll className="mb-32 grid items-center gap-10 sm:grid-cols-2">
          <div>
            <h2 className="mb-3 text-2xl font-bold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Formulas that just work</h2>
            <p className="text-[15px] leading-relaxed text-[#b8c6e0]">Sum, average, filter — all the calculations you expect, running instantly as you type.</p>
          </div>
          <div className="sm-glass-card p-5">
            <div className="mb-4 rounded-lg border border-white/10 bg-[#0a1628] px-3 py-2 text-[13px] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>
              {formulaTyped}<span className="animate-pulse">|</span>
            </div>
            <div className="mb-3 space-y-1">
              {sumRows.map((val, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between rounded px-2 py-1 text-[12px] transition-all duration-300"
                  style={{
                    background: i <= sumHighlightIdx ? "rgba(244,183,64,0.18)" : "rgba(255,255,255,0.04)",
                    color: i <= sumHighlightIdx ? "#f4b740" : "#94a3b8",
                  }}
                >
                  <span>B{i + 2}</span>
                  <span style={{ fontFamily: "'DM Mono',monospace" }}>${val.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-3 text-center text-4xl font-bold text-[#f4b740]" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>${runningTotal.toLocaleString()}</div>
          </div>
        </RevealOnScroll>

        <RevealOnScroll className="mb-24 grid items-center gap-10 sm:grid-cols-2">
          <div className="sm-glass-card order-2 flex items-center justify-center gap-6 p-8 sm:order-1">
            <div className="rounded-xl border border-white/15 bg-[#10b981]/15 px-5 py-4 text-center text-[11px] font-bold text-[#34d399]" style={{ animation: "ssBob 2.4s ease-in-out infinite" }}>XLSX</div>
            <div className="rounded-xl border border-white/15 bg-[#ef4444]/15 px-5 py-4 text-center text-[11px] font-bold text-[#f87171]" style={{ animation: "ssBob 2.4s ease-in-out infinite 0.3s" }}>PDF</div>
          </div>
          <div className="order-1 sm:order-2">
            <h2 className="mb-3 text-2xl font-bold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Import and export in a click</h2>
            <p className="text-[15px] leading-relaxed text-[#b8c6e0]">Drop in your existing Excel or CSV files, and export finished work to Excel or PDF whenever you need to share it.</p>
          </div>
        </RevealOnScroll>

        <div className="text-center">
          <button onClick={onContinue} className="rounded-2xl bg-[#f4b740] px-8 py-4 text-[13px] font-bold text-[#0a1628] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(244,183,64,0.5)]">
            Continue to SheetMind →
          </button>
          <button onClick={onContinue} className="mt-4 block w-full text-center text-[11px] font-semibold text-[#7fb1ff] hover:text-white">Skip intro</button>
        </div>
      </main>

      <style>{`
        @keyframes ssCubeSpin {
          from { transform: rotateX(-15deg) rotateY(0deg); }
          to { transform: rotateX(-15deg) rotateY(360deg); }
        }
        @keyframes ssCursorFloat0 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(8px,-6px); } }
        @keyframes ssCursorFloat1 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-6px,8px); } }
        @keyframes ssCursorFloat2 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(6px,6px); } }
        @keyframes ssBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

        /* ===== Premium Glass / Mirror Card ===== */
        .sm-glass-card {
          position: relative;
          border-radius: 1.25rem;
          background: linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02));
          border: 1px solid rgba(255,255,255,0.16);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          box-shadow:
            0 20px 50px rgba(0,0,0,0.45),
            inset 0 1px 0 rgba(255,255,255,0.18),
            inset 0 0 50px rgba(99,102,241,0.07);
          overflow: hidden;
          transform-style: preserve-3d;
          transition: transform 0.5s ease, box-shadow 0.5s ease;
        }
        .sm-glass-card::before {
          content: "";
          position: absolute;
          top: -60%; left: -60%;
          width: 220%; height: 220%;
          background: linear-gradient(115deg, transparent 42%, rgba(255,255,255,0.14) 50%, transparent 58%);
          animation: ssGlassSheen 6s ease-in-out infinite;
          pointer-events: none;
        }
        .sm-glass-card:hover {
          transform: perspective(900px) rotateX(3deg) rotateY(-3deg) translateY(-6px);
          box-shadow:
            0 26px 60px rgba(99,102,241,0.28),
            inset 0 1px 0 rgba(255,255,255,0.22);
        }
        @keyframes ssGlassSheen {
          0%, 100% { transform: translate(-20%, -10%) rotate(8deg); opacity: 0.35; }
          50% { transform: translate(20%, 10%) rotate(8deg); opacity: 0.85; }
        }

        /* ===== 3D typing table ===== */
        .sm-table-3d {
          transform: rotateX(5deg);
          transform-style: preserve-3d;
          transition: transform 0.6s ease;
        }
        .sm-table-3d:hover { transform: rotateX(0deg); }
        .sm-row-active {
          background: linear-gradient(90deg, rgba(244,183,64,0.18), rgba(244,183,64,0.02));
          box-shadow: inset 3px 0 0 0 #f4b740, 0 0 16px rgba(244,183,64,0.2);
          animation: ssRowPulse 1.1s ease-in-out infinite;
        }
        @keyframes ssRowPulse {
          0%, 100% { box-shadow: inset 3px 0 0 0 #f4b740, 0 0 8px rgba(244,183,64,0.12); }
          50% { box-shadow: inset 3px 0 0 0 #f4b740, 0 0 20px rgba(244,183,64,0.32); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// LANDING PAGE
// ============================================================
function LandingPage({ onLogin, isDark, onSupportClick, onNavigate }) {
  const monthlyLink = CHECKOUT_MONTHLY_LINK;
  const yearlyLink = CHECKOUT_YEARLY_LINK;
  const desktopLink = CHECKOUT_DESKTOP_LINK;
  const phoneLink = CHECKOUT_PHONE_LINK;

  const plans = [
    {
      id: "free",
      name: "Free Start",
      price: "$0",
      period: "forever",
      tone: "light",
      description: "Start data entry today with no payment.",
      features: ["4 lifetime sheets", "PDF and Excel export", "Cloud save", "Google login"],
      action: "Start Free",
      onClick: onLogin,
    },
    {
      id: "monthly",
      name: "Monthly Pro",
      price: "$4",
      period: "per month",
      tone: "blue",
      badge: "Popular",
      description: "For freelancers and regular client work.",
      features: ["Unlimited sheets", "Premium workspace", "Ad-free use", "Priority updates"],
      action: "Choose Monthly",
      href: monthlyLink,
    },
    {
      id: "yearly",
      name: "Yearly Pro",
      price: "$50",
      period: "per year",
      tone: "gold",
      badge: "Best Value",
      description: "Best for business owners and teams.",
      features: ["12 months access", "Unlimited projects", "Best yearly price", "Business-ready tools"],
      action: "Choose Yearly",
      href: yearlyLink,
    },
    {
      id: "desktop",
      name: "Desktop App",
      price: "$20",
      period: "one time",
      tone: "slate",
      description: "Windows app for offline office data entry.",
      features: ["Windows installer", "Offline workflow", "Local files", "Excel and PDF export"],
      action: "Get Desktop",
      href: desktopLink,
    },
    {
      id: "phone",
      name: "Phone App",
      price: "$10",
      period: "one time",
      tone: "green",
      description: "Mobile app for quick field data work.",
      features: ["Android app", "Fast entry screen", "Pro tools included", "Free updates"],
      action: "Get Phone App",
      href: phoneLink,
    },
  ];

  const landingHighlights = [
    { label: "AI setup", value: "Prompt to table" },
    { label: "Collaboration", value: "Share with roles" },
    { label: "Exports", value: "Excel and PDF" },
    { label: "Payments", value: "Card or bKash" },
  ];

  const toneClass = {
    light: "border-[#c8d7ec] bg-white text-[#172033] shadow-[0_18px_48px_rgba(43,76,126,0.12)]",
    blue: "border-[#2563eb]/35 bg-gradient-to-br from-[#0b2f66] via-[#1156b7] to-[#0887c7] text-white shadow-[0_26px_70px_rgba(37,99,235,0.28)]",
    gold: "border-[#d99b22]/40 bg-gradient-to-br from-[#3d2a08] via-[#8a5b12] to-[#d99b22] text-white shadow-[0_26px_70px_rgba(217,155,34,0.26)]",
    slate: "border-[#64748b]/35 bg-gradient-to-br from-[#111827] via-[#263246] to-[#475569] text-white shadow-[0_26px_70px_rgba(15,23,42,0.25)]",
    green: "border-[#10b981]/35 bg-gradient-to-br from-[#053f34] via-[#087b63] to-[#16a34a] text-white shadow-[0_26px_70px_rgba(16,185,129,0.22)]",
  };

  const mutedText = (tone) => tone === "light" ? "text-[#5f6f89]" : "text-white/78";
  const featureText = (tone) => tone === "light" ? "text-[#36516f]" : "text-white/88";
  const buttonClass = (tone) => tone === "light"
    ? "border border-[#3978d8]/25 bg-[#f3f7ff] text-[#2457d6] hover:bg-[#e6efff]"
    : "bg-white/95 text-[#172033] hover:bg-white";

  const PlanCard = ({ plan, index }) => {
    const content = (
      <div
        className={`group relative flex h-full min-h-[315px] flex-col overflow-hidden rounded-[1.65rem] border p-5 transition-all duration-300 hover:-translate-y-3 hover:scale-[1.015] hover:shadow-[0_34px_90px_rgba(37,99,235,0.22)] ${toneClass[plan.tone]}`}
        style={{ animationDelay: `${index * 70}ms` }}
      >
        <div className="absolute inset-x-5 top-0 h-1 rounded-b-full bg-white/35 opacity-70" />
        {plan.badge && (
          <span className="absolute right-4 top-4 rounded-full bg-white/18 px-3 py-1 text-[8px] font-black uppercase tracking-[0.24em] text-white backdrop-blur">
            {plan.badge}
          </span>
        )}
        <p className={`mb-4 text-[10px] font-black uppercase tracking-[0.28em] ${plan.tone === "light" ? "text-[#3978d8]" : "text-white/70"}`}>
          {plan.name}
        </p>
        <div className="mb-3 flex items-end gap-2">
          <span className="text-4xl font-black tracking-tight">{plan.price}</span>
          <span className={`mb-1 text-[10px] font-black uppercase tracking-widest ${mutedText(plan.tone)}`}>{plan.period}</span>
        </div>
        <p className={`mb-5 min-h-[34px] text-[11px] leading-relaxed ${mutedText(plan.tone)}`}>{plan.description}</p>
        <div className="mb-6 space-y-2.5">
          {plan.features.map((feature) => (
            <p key={feature} className={`flex items-center gap-2 text-[11px] font-bold leading-relaxed ${featureText(plan.tone)}`}>
              <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${plan.tone === "light" ? "bg-[#2457d6]" : "bg-white/85"}`} />
              <span>{feature}</span>
            </p>
          ))}
        </div>
        <span className={`mt-auto flex w-full items-center justify-center rounded-xl px-4 py-3 text-[10px] font-black uppercase tracking-[0.22em] transition-all ${buttonClass(plan.tone)}`}>
          {plan.action}
        </span>
      </div>
    );

    if (plan.onClick) {
      return (
        <button key={plan.id} onClick={plan.onClick} className="block h-full text-left outline-none focus-visible:ring-4 focus-visible:ring-blue-300/50 rounded-[1.65rem]">
          {content}
        </button>
      );
    }

    return (
      <a key={plan.id} href={plan.href} className=" block h-full outline-none focus-visible:ring-4 focus-visible:ring-blue-300/50 rounded-[1.65rem]">
        {content}
      </a>
    );
  };

  const [introPhase, setIntroPhase] = useState(() => {
    try { return sessionStorage.getItem("ssIntroSeen") ? "gone" : "visible"; }
    catch (e) { return "visible"; }
  });

  useEffect(() => {
    if (introPhase !== "visible") return;
    const dismiss = () => setIntroPhase("leaving");
    const autoTimer = setTimeout(dismiss, 3200);
    window.addEventListener("wheel", dismiss, { passive: true });
    window.addEventListener("touchstart", dismiss, { passive: true });
    window.addEventListener("keydown", dismiss);
    window.addEventListener("click", dismiss);
    return () => {
      clearTimeout(autoTimer);
      window.removeEventListener("wheel", dismiss);
      window.removeEventListener("touchstart", dismiss);
      window.removeEventListener("keydown", dismiss);
      window.removeEventListener("click", dismiss);
    };
  }, [introPhase]);

  useEffect(() => {
    if (introPhase !== "leaving") return;
    try { sessionStorage.setItem("ssIntroSeen", "1"); } catch (e) {}
    const t = setTimeout(() => setIntroPhase("gone"), 700);
    return () => clearTimeout(t);
  }, [introPhase]);

  return (
    <>
    {introPhase !== "gone" && (
      <div
        className={`fixed inset-0 z-[999] flex flex-col items-center justify-center transition-all duration-700 ${introPhase === "leaving" ? "opacity-0 -translate-y-10 pointer-events-none" : "opacity-100"}`}
        style={{ background: "radial-gradient(ellipse 70% 60% at 50% 20%, rgba(99,102,241,0.22), transparent 60%), linear-gradient(160deg, #0a1628 0%, #0f1f38 55%, #0a1628 100%)" }}
      >
        <div style={{ perspective: "900px" }}>
          <div className="relative" style={{ width: "120px", height: "120px", transformStyle: "preserve-3d", animation: "ssCubeSpin 6s linear infinite", margin: "0 auto" }}>
            {[
              { t: "translateZ(60px)" },
              { t: "rotateY(180deg) translateZ(60px)" },
              { t: "rotateY(90deg) translateZ(60px)" },
              { t: "rotateY(-90deg) translateZ(60px)" },
              { t: "rotateX(90deg) translateZ(60px)" },
              { t: "rotateX(-90deg) translateZ(60px)" },
            ].map((face, i) => (
              <div key={i} className="absolute grid grid-cols-3 grid-rows-3 gap-[2px] rounded-lg border border-white/20 p-1.5" style={{ width: "120px", height: "120px", transform: face.t, background: "linear-gradient(135deg, rgba(99,102,241,0.45), rgba(15,31,56,0.9))" }}>
                {Array.from({ length: 9 }).map((_, c) => (
                  <div key={c} className="rounded-[2px]" style={{ background: (c + i) % 4 === 0 ? "rgba(244,183,64,0.6)" : "rgba(255,255,255,0.12)" }} />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex gap-[2px]" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
          {"SheetMind".split("").map((ch, i) => (
            <span
              key={i}
              className="text-3xl font-bold text-white sm:text-4xl"
              style={{ opacity: 0, animation: `ssLetterIn 0.5s ease-out ${0.3 + i * 0.05}s forwards` }}
            >
              {ch}
            </span>
          ))}
        </div>

        <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7fb1ff]" style={{ opacity: 0, animation: "ssLetterIn 0.6s ease-out 1.1s forwards" }}>
          Scroll or click to continue
        </p>

        <style>{`
          @keyframes ssCubeSpin {
            from { transform: rotateX(-15deg) rotateY(0deg); }
            to { transform: rotateX(-15deg) rotateY(360deg); }
          }
          @keyframes ssLetterIn {
            from { opacity: 0; transform: translateY(14px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    )}
    <div className="min-h-screen text-[#e7edf7]" style={{ fontFamily: "'Inter',sans-serif", background: "radial-gradient(ellipse 90% 40% at 50% -5%, rgba(36,87,214,0.20), transparent 60%), radial-gradient(ellipse 70% 45% at 100% 25%, rgba(244,183,64,0.07), transparent 60%), radial-gradient(ellipse 60% 40% at 0% 70%, rgba(99,102,241,0.10), transparent 60%), linear-gradient(180deg, #0a1628 0%, #0e1c33 35%, #0b1930 70%, #0a1628 100%)" }}>
      <main className="mx-auto w-full max-w-[1480px] px-4 pb-10 pt-4 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[2rem] border border-white/10 p-4 shadow-[0_40px_120px_rgba(10,22,40,0.35)] sm:p-6 lg:p-10" style={{ background: "radial-gradient(ellipse 70% 60% at 20% 0%, rgba(36,87,214,0.35), transparent 60%), radial-gradient(ellipse 60% 50% at 100% 10%, rgba(244,183,64,0.18), transparent 60%), linear-gradient(160deg, #0a1628 0%, #0f1f38 55%, #0a1628 100%)" }}>
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#2563eb] via-[#0ea5e9] to-[#f4b740]" />
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#2457d6]/20 blur-3xl" />
          <div className="pointer-events-none absolute -left-16 bottom-0 h-56 w-56 rounded-full bg-[#f4b740]/10 blur-3xl" />

          {/* ===== Pure-CSS 3D floating spreadsheet animation ===== */}
          <div className="pointer-events-none absolute -right-4 top-2 hidden lg:block" style={{ perspective: "1200px", width: "260px", height: "260px" }}>
            <div className="relative h-full w-full" style={{ transformStyle: "preserve-3d", animation: "ssSheetFloat3D 9s ease-in-out infinite" }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute inset-0 rounded-2xl border border-white/15"
                  style={{
                    background: i === 0
                      ? "linear-gradient(135deg, rgba(99,102,241,0.35), rgba(244,183,64,0.12))"
                      : "linear-gradient(135deg, rgba(36,87,214,0.28), rgba(15,31,56,0.65))",
                    transform: `translateZ(${-i * 26}px) translateY(${i * 10}px)`,
                    boxShadow: "0 30px 80px rgba(10,22,40,0.5)",
                  }}
                >
                  <div className="grid h-full w-full grid-cols-4 grid-rows-4 gap-[3px] p-3 opacity-70">
                    {Array.from({ length: 16 }).map((_, c) => (
                      <div
                        key={c}
                        className="rounded-[3px]"
                        style={{ background: (c + i) % 5 === 0 ? "rgba(244,183,64,0.55)" : "rgba(255,255,255,0.10)" }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <style>{`
              @keyframes ssSheetFloat3D {
                0%, 100% { transform: rotateX(18deg) rotateY(-22deg) rotateZ(0deg); }
                50% { transform: rotateX(10deg) rotateY(-8deg) rotateZ(3deg); }
              }
              @keyframes ssBadgeFloat {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-5px); }
              }
              @media (min-width: 1024px) {
                .sm-deck { display: flex; justify-content: center; align-items: flex-end; padding-top: 18px; padding-bottom: 6px; }
                .sm-deck > * {
                  width: 230px;
                  flex-shrink: 0;
                  margin-left: -150px;
                  transition: margin-left 0.5s cubic-bezier(.22,1,.36,1), transform 0.5s cubic-bezier(.22,1,.36,1), z-index 0s;
                  transform-origin: bottom center;
                }
                .sm-deck > *:first-child { margin-left: 0; }
                .sm-deck > *:nth-child(1) { z-index: 5; transform: rotate(-8deg) translateY(10px); }
                .sm-deck > *:nth-child(2) { z-index: 4; transform: rotate(-4deg) translateY(4px); }
                .sm-deck > *:nth-child(3) { z-index: 3; transform: rotate(0deg) translateY(0); }
                .sm-deck > *:nth-child(4) { z-index: 2; transform: rotate(4deg) translateY(4px); }
                .sm-deck > *:nth-child(5) { z-index: 1; transform: rotate(8deg) translateY(10px); }
                .sm-deck:hover > * { margin-left: 14px; transform: rotate(0deg) translateY(0); }
                .sm-deck:hover > *:first-child { margin-left: 0; }
                .sm-deck > *:hover { transform: translateY(-16px) scale(1.02) !important; z-index: 10 !important; }
              }
            `}</style>
          </div>

          <div className="relative mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.34em] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>SheetMind Plans</p>
              <h1 className="max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.75rem]" style={{ fontFamily: "'Space Grotesk',sans-serif", lineHeight: 1.08 }}>
                Choose how you want to work
              </h1>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#b8c6e0]">
                Pick a plan first. Free users can start instantly; premium users can unlock faster data-entry workflows for client, office, and business records.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 sm:items-end">
              <div className="sm-glass-card relative overflow-visible border-white/10 px-5 py-4 text-left backdrop-blur-sm sm:text-right" style={{ animation: "ssBadgeFloat 3.5s ease-in-out infinite" }}>
                <span className="absolute -right-1.5 -top-1.5 flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#f4b740] opacity-75" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-[#f4b740]" />
                </span>
                <p className="text-[9px] font-bold uppercase tracking-[0.24em] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>Built for</p>
                <p className="mt-1.5 text-[12px] font-semibold tracking-wide text-white">Freelancers, offices, teams</p>
              </div>
              <button onClick={() => onNavigate("demo")} className="group relative overflow-hidden rounded-2xl bg-[#f4b740] px-7 py-3.5 text-[13px] font-bold text-[#0a1628] shadow-[0_0_0_0_rgba(244,183,64,0.5)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(244,183,64,0.5)]" style={{ animation: "pulseGlow 2.4s ease-in-out infinite" }}>
                <span className="relative z-10 flex items-center gap-2">
                  ✨ Try it now — no signup required
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </span>
              </button>
              <style>{`
                @keyframes pulseGlow {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(244,183,64,0.45); }
                  50% { box-shadow: 0 0 0 10px rgba(244,183,64,0); }
                }
              `}</style>
            </div>
          </div>

          <div className="relative mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {landingHighlights.map((item) => (
              <div key={item.label} className="rounded-xl border border-white/10 bg-white/[0.055] px-4 py-3 backdrop-blur-sm">
                <p className="text-[8px] font-black uppercase tracking-[0.22em] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>
                  {item.label}
                </p>
                <p className="mt-1 truncate text-[12px] font-semibold text-white">
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          <div className="sm-deck relative grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {plans.map((plan, index) => <PlanCard key={plan.id} plan={plan} index={index} />)}
          </div>
        </section>

        <section className="sm-glass-card mt-6 p-6 sm:p-8">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.34em] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>About SheetMind</p>
          <h2 className="mb-4 text-2xl font-bold tracking-tight text-white sm:text-[1.75rem]" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            What is SheetMind and who is it for?
          </h2>
          <p className="mb-4 text-[15px] leading-relaxed text-[#b8c6e0]">
            SheetMind is a cloud-based, AI-assisted spreadsheet tool built for freelancers, small offices, and
            growing teams who need a faster alternative to traditional spreadsheet software. Instead of juggling
            complex formulas and clunky desktop programs, SheetMind lets you build, edit, and share tables directly
            in your browser, with changes saved automatically to the cloud so your data is never lost.
          </p>
          <p className="mb-7 text-[15px] leading-relaxed text-[#b8c6e0]">
            Whether you are tracking inventory, managing payroll, organizing a student list, running a small CRM,
            or preparing an invoice for a client, SheetMind gives you the structure of a spreadsheet with the
            simplicity of a modern app. Real-time collaboration means your team can work on the same table at the
            same time, and one-click exports to Excel or PDF make it easy to share finished work with anyone.
          </p>

          <h3 className="mb-4 text-lg font-semibold tracking-tight text-white" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>How SheetMind Works</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              {
                step: "1",
                title: "Sign in with Google",
                body: "Create your free account in seconds using your existing Google login — no separate password to remember.",
              },
              {
                step: "2",
                title: "Build your table",
                body: "Add columns, set data types, and start entering rows. Use templates to get started faster for common use cases.",
              },
              {
                step: "3",
                title: "Save, share, export",
                body: "Your work auto-saves to the cloud. Invite collaborators by email, or export to Excel and PDF when you are done.",
              },
            ].map((item) => (
              <div key={item.step} className="rounded-2xl border border-white/10 bg-white/[0.05] p-5 transition-all duration-300 hover:border-white/20 hover:bg-white/[0.09]">
                <span className="mb-3 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#f4b740] text-xs font-bold text-[#0a1628]" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                  {item.step}
                </span>
                <h4 className="mt-1 text-[13px] font-semibold tracking-tight text-white" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{item.title}</h4>
                <p className="mt-2 text-[13px] leading-relaxed text-[#b8c6e0]">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {[
            {
              title: "Data Entry Without Friction",
              body: "Create clean records, edit cells quickly, save work to the cloud, and export files when clients ask for Excel or PDF.",
            },
            {
              title: "Made For Client Work",
              body: "Use it for inventory, payroll, CRM, student lists, project schedules, invoices, and daily office reports.",
            },
            {
              title: "Simple Upgrade Path",
              body: "Start free, then move to monthly, yearly, desktop, or phone app plans when your work volume grows.",
            },
          ].map((item) => (
            <div key={item.title} className="sm-glass-card group p-6 transition-all duration-300 hover:-translate-y-1">
              <div className="mb-3 h-[3px] w-8 rounded-full bg-[#f4b740] transition-all duration-300 group-hover:w-12" />
              <h2 className="text-[15px] font-semibold tracking-tight text-white" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{item.title}</h2>
              <p className="mt-2.5 text-[13px] leading-relaxed text-[#b8c6e0]">{item.body}</p>
            </div>
          ))}
        </section>

        <section className="sm-glass-card mt-6 p-6 sm:p-8">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.34em] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>FAQ</p>
          <h2 className="mb-5 text-2xl font-bold tracking-tight text-white sm:text-[1.75rem]" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              {
                q: "Is SheetMind free to use?",
                a: "Yes. The Free Start plan lets you create up to 4 lifetime sheets with cloud save, PDF and Excel export, and Google login, with no payment required.",
              },
              {
                q: "Do I need to install anything?",
                a: "No installation is required for the web version. SheetMind runs entirely in your browser. Desktop and phone apps are also available if you prefer a native app experience.",
              },
              {
                q: "Can multiple people work on the same sheet?",
                a: "Yes. Premium plans support real-time collaboration, so you can invite teammates by email and edit the same table together.",
              },
              {
                q: "What file formats can I export to?",
                a: "You can export any table to Excel (.xlsx) or PDF directly from the app, making it easy to share finished work with clients or colleagues.",
              },
              {
                q: "How do I upgrade to a Premium plan?",
                a: "Choose Monthly or Yearly Pro from the pricing section above. International users can pay through Gumroad, and Bangladesh users can pay with bKash, with Premium activated after admin approval.",
              },
              {
                q: "What happens to my data if I cancel Premium?",
                a: "Your existing sheets remain accessible. You will simply move back to the Free plan limits going forward, and your saved data is not deleted.",
              },
              {
                q: "Is my data secure?",
                a: "Yes. SheetMind uses Google Firebase for authentication and cloud storage, with permission-based access control so only people you invite can view or edit a shared sheet.",
              },
            ].map((item, i) => (
              <details key={i} className="group rounded-2xl border border-white/10 bg-white/[0.05] p-4 transition-colors duration-200 [&[open]]:border-[#f4b740]/30 [&[open]]:bg-white/[0.08]">
                <summary className="flex cursor-pointer list-none items-center justify-between text-[14px] font-medium text-white">
                  {item.q}
                  <span className="ml-3 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#f4b740]/15 text-sm font-bold text-[#f4b740] transition-transform duration-200 group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-[13px] leading-relaxed text-[#b8c6e0]">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="sm-glass-card mt-6 overflow-hidden">
          <div className="min-h-[90px]">
            <BannerAd isDark={isDark} />
          </div>
        </section>

        <section className="sm-glass-card mt-6 p-6 text-center sm:p-7">
          <p className="mx-auto max-w-2xl text-[13px] leading-relaxed text-[#b8c6e0]">
            Bangladesh users can pay with bKash after choosing a premium plan. Admin approval activates Premium.
          </p>

          <div className="mx-auto mt-5 max-w-3xl rounded-2xl border border-white/10 bg-white/[0.04] p-4">
            <p className="mb-3 text-[9px] font-black uppercase tracking-[0.24em] text-[#7fb1ff]">Support</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button onClick={onSupportClick} className="inline-flex rounded-xl border border-[#f4b740]/30 bg-[#f4b740]/10 px-4 py-2 text-[11px] font-semibold text-[#f4b740] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#f4b740] hover:text-[#0a1628]">Payment Support</button>
              <button onClick={() => onNavigate("blog")} className="inline-flex rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2 text-[11px] font-medium text-[#b8c6e0] transition-all duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:text-white">Blog</button>
              <button onClick={() => onNavigate("contact")} className="inline-flex rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2 text-[11px] font-medium text-[#b8c6e0] transition-all duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:text-white">Contact</button>
            </div>
            <div className="mx-auto my-3 h-px w-full max-w-xs bg-white/10" />
            <p className="mb-3 text-[9px] font-black uppercase tracking-[0.24em] text-[#7fb1ff]">Legal</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <a href="/terms.html" target="_blank" rel="noopener" className="inline-flex rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2 text-[11px] font-medium text-[#b8c6e0] transition-all duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:text-white">Terms of Service</a>
              <a href="/privacy.html" target="_blank" rel="noopener" className="inline-flex rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2 text-[11px] font-medium text-[#b8c6e0] transition-all duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:text-white">Privacy Policy</a>
              <a href="/refund.html" target="_blank" rel="noopener" className="inline-flex rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2 text-[11px] font-medium text-[#b8c6e0] transition-all duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:text-white">Refund Policy</a>
            </div>
          </div>
        </section>

        {DEMO_VIDEO_ID && (
          <section className="mt-6 flex flex-col items-center">
            <p className="mb-3 text-center text-[10px] font-bold uppercase tracking-[0.3em] text-[#7fb1ff]" style={{ fontFamily: "'DM Mono',monospace" }}>Watch Demo · See SheetMind in action</p>
            <div className="sm-glass-card relative mx-auto w-full max-w-[380px] overflow-hidden p-1.5">
              <div className="relative w-full overflow-hidden rounded-xl" style={{ background: "#000" }}>
                <iframe
                  src={`https://www.youtube.com/embed/${DEMO_VIDEO_ID}`}
                  title="SheetMind Demo Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="block w-full"
                  style={{ border: "none", aspectRatio: "16 / 9" }}
                />
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
    </>
  );
}
// ============================================================

function PaymentSupportChat({ onClose, isDark }) {
  const SUPPORT_EMAIL = "iftia5061@gmail.com";
  const faqs = [
    {
      q: "I paid but Pro is not activated",
      steps: [
        "Wait 5-10 minutes after payment",
        "Reload the page (Ctrl+R)",
        "Login with the same Gmail used for payment",
        "If still not working, email us using the button below",
      ],
    },
    {
      q: "Payment was made with wrong Gmail",
      steps: [
        "Note your order/receipt ID from your Gumroad email",
        "Note your correct Gmail address",
        "Email us below — will be fixed within 24 hours",
      ],
    },
    {
      q: "I want a refund",
      steps: [
        "Refund requests accepted within 7 days of purchase",
        "Email us with your Transaction ID",
        "Refund processed within 3-5 business days",
      ],
    },
    {
      q: "Money was deducted but payment failed",
      steps: [
        "Take a screenshot of your bank statement",
        "Note your Transaction ID or reference number",
        "Email us below — will be checked immediately",
      ],
    },
    {
      q: "Desktop/Phone app is not working",
      steps: [
        "Uninstall and reinstall the app",
        "Check your internet connection",
        "Check if Windows Defender or Antivirus is blocking it",
        "If still not working, email us below",
      ],
    },
  ];
  const [selected, setSelected] = React.useState(null);
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <div className={`w-full max-w-md rounded-3xl border shadow-2xl flex flex-col ${isDark ? "bg-[#070f1e] border-[#1e3a5f]" : "bg-white border-slate-200"}`} style={{ maxHeight: "85vh" }}>
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? "border-[#1e3a5f]" : "border-slate-100"}`}>
          <div>
            <p className="text-sm font-black uppercase tracking-widest text-blue-400">Payment Support</p>
            <p className={`text-[10px] mt-0.5 ${isDark ? "text-slate-500" : "text-slate-400"}`}>Select your issue to see the solution</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-300 text-xl font-black">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
          {faqs.map((faq, i) => (
            <div key={i}>
              <button
                onClick={() => setSelected(selected === i ? null : i)}
                className={`w-full text-left px-4 py-3 rounded-2xl border transition-all flex items-center justify-between gap-2 ${selected === i ? "bg-blue-600 border-blue-500 text-white" : isDark ? "bg-[#0a1628] border-[#1e3a5f] text-slate-300 hover:border-blue-500/40" : "bg-slate-50 border-slate-200 text-slate-700 hover:border-blue-300"}`}
              >
                <span className="text-[12px] font-bold">{faq.q}</span>
                <span className="text-lg">{selected === i ? "▲" : "▼"}</span>
              </button>
              {selected === i && (
                <div className={`mx-2 px-4 py-3 rounded-b-2xl border border-t-0 ${isDark ? "bg-[#050d1f] border-[#1e3a5f]" : "bg-blue-50 border-blue-100"}`}>
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? "text-blue-400" : "text-blue-600"}`}>Solution:</p>
                  <div className="space-y-2">
                    {faq.steps.map((step, j) => (
                      <div key={j} className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[9px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">{j + 1}</span>
                        <p className={`text-[11px] ${isDark ? "text-slate-300" : "text-slate-600"}`}>{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className={`px-6 py-4 border-t ${isDark ? "border-[#1e3a5f]" : "border-slate-100"}`}>
          <p className={`text-[10px] text-center mb-3 ${isDark ? "text-slate-500" : "text-slate-400"}`}>If your issue is not resolved, contact us directly</p>
          <a href={`mailto:${SUPPORT_EMAIL}?subject=SheetMind Payment Support`} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-black uppercase tracking-widest transition-all">
            ✉ Email Us
          </a>
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ onClose, isDark, currentUser }) {
  const [allUsers,         setAllUsers]         = useState([]);
  const [pendingPayments,  setPendingPayments]  = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [payLoading,       setPayLoading]       = useState(true);
  const [stats,            setStats]            = useState({ totalUsers: 0, premiumUsers: 0, totalTables: 0, totalRevenue: 0 });
  const [activeTab,        setActiveTab]        = useState("overview");
  const [actionMsg,        setActionMsg]        = useState("");
  const [searchUser,       setSearchUser]       = useState("");
  const [filterPlan,       setFilterPlan]       = useState("all");

  // Colors — Deep Blue + Gold theme
  const bg     = isDark ? "bg-[#060e1c]"         : "bg-white";
  const card   = isDark ? "bg-[#08122a] border-yellow-900/20" : "bg-slate-50 border-slate-200";
  const text   = isDark ? "text-slate-100"        : "text-slate-900";
  const sub    = isDark ? "text-blue-400"         : "text-slate-500";
  const border = isDark ? "border-[#d4af3722]"   : "border-blue-200";
  const input  = isDark ? "bg-[#050d1f] border-[#d4af3730] text-slate-200 placeholder:text-blue-900" : "bg-blue-50 border-blue-300 text-slate-800";

  const pendingCount = pendingPayments.filter(p => p.status === "pending").length;

  // ── Fetch Users ──
  const fetchAllUsers = useCallback(async () => {
    if (!db) { setLoading(false); return; }
    try {
      const snap = await getDocs(collection(db, "users"));
      const list = [];
      let premiumCount = 0, tableCount = 0;
      snap.forEach(d => {
        const data = { ...d.data(), uid: d.id };
        list.push(data);
        if (data.isPremium || data.email === ADMIN_EMAIL) premiumCount++;
        tableCount += data.tableCount || 0;
      });
      list.sort((a, b) => (b.lastLogin?.seconds || 0) - (a.lastLogin?.seconds || 0));
      setAllUsers(list);
      setStats(prev => ({ ...prev, totalUsers: snap.size, premiumUsers: premiumCount, totalTables: tableCount }));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  // ── Fetch Payments ──
  const fetchPendingPayments = useCallback(async () => {
    if (!db) { setPayLoading(false); return; }
    try {
      const snap = await getDocs(collection(db, "payment_requests"));
      const list = [];
      let revenue = 0;
      snap.forEach(d => {
        const data = { ...d.data(), docId: d.id };
        list.push(data);
        if (data.status === "approved") revenue += data.amount || 0;
      });
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setPendingPayments(list);
      setStats(prev => ({ ...prev, totalRevenue: revenue }));
    } catch (err) { console.error(err); }
    finally { setPayLoading(false); }
  }, []);

  useEffect(() => { fetchAllUsers(); fetchPendingPayments(); }, [fetchAllUsers, fetchPendingPayments]);

  // ── Make Premium ──
  const handleMakePremium = async (u) => {
    if (!db) return;
    try {
      const expiry = new Date(); expiry.setMonth(expiry.getMonth() + 1);
      await setDoc(doc(db, "users", u.uid), {
        isPremium: true, plan: "pro",
        premiumPlan: "monthly", premiumExpiry: expiry,
        premiumGrantedAt: serverTimestamp(), premiumGrantedBy: "admin",
      }, { merge: true });
      await setDoc(doc(db, "spreadsheets", u.uid), { isPremium: true }, { merge: true });
      setActionMsg(`✅ ${u.email} is now Premium!`);
      fetchAllUsers(); setTimeout(() => setActionMsg(""), 3000);
    } catch (err) { setActionMsg("❌ " + err.message); }
  };

  // ── Revoke Premium ──
  const handleRevokePremium = async (u) => {
    if (!db) return;
    try {
      await setDoc(doc(db, "users", u.uid), { isPremium: false, plan: "free" }, { merge: true });
      await setDoc(doc(db, "spreadsheets", u.uid), { isPremium: false }, { merge: true });
      setActionMsg(`⚠️ Premium revoked for ${u.email}`);
      fetchAllUsers(); setTimeout(() => setActionMsg(""), 3000);
    } catch (err) { setActionMsg("❌ " + err.message); }
  };

  // ── Approve Payment ──
  const handleApprovePayment = async (payment) => {
    if (!db) return;
    try {
      const expiry = new Date(); expiry.setMonth(expiry.getMonth() + (payment.plan === "yearly" ? 12 : 1));
      await setDoc(doc(db, "users", payment.uid), {
        isPremium: true, plan: "pro",
        premiumPlan: payment.plan || "monthly", premiumExpiry: expiry,
        premiumGrantedAt: serverTimestamp(), premiumGrantedBy: "admin_bkash",
        paymentStatus: "approved",
      }, { merge: true });
      await setDoc(doc(db, "spreadsheets", payment.uid), { isPremium: true }, { merge: true });
      await setDoc(doc(db, "payment_requests", payment.docId), {
        status: "approved", approvedAt: serverTimestamp(), approvedBy: currentUser?.email,
      }, { merge: true });
      setActionMsg(`✅ ${payment.email} is now Premium!`);
      fetchPendingPayments(); fetchAllUsers(); setTimeout(() => setActionMsg(""), 4000);
    } catch (err) { setActionMsg("❌ " + err.message); }
  };

  // ── Reject Payment ──
  const handleRejectPayment = async (payment) => {
    if (!db) return;
    try {
      await setDoc(doc(db, "payment_requests", payment.docId), {
        status: "rejected", rejectedAt: serverTimestamp(), rejectedBy: currentUser?.email,
      }, { merge: true });
      setActionMsg(`❌ Payment rejected for ${payment.email}`);
      fetchPendingPayments(); setTimeout(() => setActionMsg(""), 3000);
    } catch (err) { setActionMsg("❌ " + err.message); }
  };

  // ── Filtered users ──
  const filteredUsers = allUsers.filter(u => {
    const matchSearch = !searchUser || u.email?.toLowerCase().includes(searchUser.toLowerCase()) || u.name?.toLowerCase().includes(searchUser.toLowerCase());
    const matchPlan = filterPlan === "all" || (filterPlan === "premium" && (u.isPremium || u.email === ADMIN_EMAIL)) || (filterPlan === "free" && !u.isPremium && u.email !== ADMIN_EMAIL);
    return matchSearch && matchPlan;
  });

  const tabs = [
    { id: "overview", label: "📊 Overview" },
    { id: "users",    label: "👥 Users",    badge: stats.totalUsers },
    { id: "payments", label: "💸 Payments", badge: pendingCount > 0 ? pendingCount : null, badgeRed: true },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className={`${bg} ${text} w-full max-w-3xl rounded-[2rem] border ${border} shadow-2xl max-h-[92vh] flex flex-col overflow-hidden`}>

        {/* ── Header ── */}
        <div className={`px-7 pt-7 pb-5 border-b ${border} flex-shrink-0`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-black uppercase tracking-tight bg-gradient-to-r from-blue-400 to-yellow-300 bg-clip-text text-transparent flex items-center gap-2">
                 Admin Dashboard
              </h2>
              <p className={`text-[10px] ${sub} mt-1 font-mono`}>{currentUser?.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { fetchAllUsers(); fetchPendingPayments(); }}
                className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase border ${border} ${sub} hover:text-yellow-400 hover:border-yellow-600/40 transition-all`}>
                Refresh
              </button>
              <button onClick={onClose} className="text-xl hover:opacity-60 transition-opacity">✕</button>
            </div>
          </div>

          {/* Action Message */}
          {actionMsg && (
            <div className={`mt-3 p-3 rounded-xl text-[11px] font-black border ${
              actionMsg.startsWith("✅") ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-red-500/10 text-red-400 border-red-500/20"
            }`}>{actionMsg}</div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                  activeTab === t.id
                    ? "bg-gradient-to-r from-yellow-500 to-amber-400 text-slate-900"
                    : `border ${border} ${sub} hover:text-yellow-400`
                }`}>
                {t.label}
                {t.badge != null && (
                  <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${t.badgeRed ? "bg-red-500 text-white" : "bg-blue-500/20 text-blue-400"}`}>
                    {t.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="overflow-y-auto flex-1 p-6">

          {/* ══ OVERVIEW TAB ══ */}
          {activeTab === "overview" && (
            <div className="space-y-5">

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Total Users",   value: loading ? "…" : stats.totalUsers,   icon: "👥", color: "text-sky-400",     bg: "bg-sky-500/10",     border: "border-sky-500/20" },
                  { label: "Premium",       value: loading ? "…" : stats.premiumUsers, icon: "⭐", color: "text-yellow-400",  bg: "bg-yellow-500/10",  border: "border-yellow-500/20" },
                  { label: "Total Tables",  value: loading ? "…" : stats.totalTables,  icon: "📊", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
                  { label: "Revenue (BDT)", value: loading ? "…" : `${stats.totalRevenue.toLocaleString()}`, icon: "💰", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
                ].map((s, i) => (
                  <div key={i} className={`p-4 rounded-2xl border ${s.border} ${s.bg} text-center`}>
                    <div className="text-2xl mb-1">{s.icon}</div>
                    <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
                    <div className={`text-[9px] font-black uppercase tracking-widest ${sub} mt-1`}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Recent Activity */}
              <div className={`p-5 rounded-2xl border ${border} ${isDark ? "bg-[#08122a]" : "bg-slate-50"}`}>
                <h3 className={`text-[10px] font-black uppercase tracking-widest ${sub} mb-4`}>📋 Recent Users</h3>
                <div className="space-y-2">
                  {loading ? (
                    <div className="flex justify-center py-4"><div className="w-6 h-6 border-2 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin"></div></div>
                  ) : allUsers.slice(0, 5).map((u, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <img src={u.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || "U")}&background=1e3a8a&color=d4af37`}
                        alt="" className="w-7 h-7 rounded-full flex-shrink-0"
                        onError={e => { e.target.src = `https://ui-avatars.com/api/?name=U&background=1e3a8a&color=d4af37`; }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-black truncate">{u.name || "Unknown"}</p>
                        <p className={`text-[9px] ${sub} font-mono truncate`}>{u.email}</p>
                      </div>
                      <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase flex-shrink-0 ${
                        u.isPremium || u.email === ADMIN_EMAIL ? "bg-yellow-500/20 text-yellow-400" : "bg-slate-500/20 text-slate-400"
                      }`}>{u.email === ADMIN_EMAIL ? "Admin" : u.isPremium ? "Pro" : "Free"}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending payments alert */}
              {pendingCount > 0 && (
                <div className={`p-4 rounded-2xl border border-red-500/30 bg-red-500/5 flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="text-[11px] font-black text-red-400">{pendingCount} Pending Payment{pendingCount > 1 ? "s" : ""}</p>
                      <p className={`text-[9px] ${sub}`}>Approve to activate Premium for users</p>
                    </div>
                  </div>
                  <button onClick={() => setActiveTab("payments")}
                    className="bg-red-500 hover:bg-red-400 text-white text-[9px] font-black px-3 py-1.5 rounded-xl uppercase transition-all">
                    Review →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ══ USERS TAB ══ */}
          {activeTab === "users" && (
            <div className="space-y-4">
              {/* Search + Filter */}
              <div className="flex gap-2">
                <input type="text" value={searchUser} onChange={e => setSearchUser(e.target.value)}
                  placeholder="🔍 Search by name or email..."
                  className={`flex-1 px-4 py-2.5 rounded-xl border outline-none text-[11px] transition-all ${input}`} />
                <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)}
                  className={`px-3 py-2.5 rounded-xl border outline-none text-[11px] font-black uppercase ${input}`}>
                  <option value="all">All</option>
                  <option value="premium">Premium</option>
                  <option value="free">Free</option>
                </select>
              </div>

              {loading ? (
                <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin"></div></div>
              ) : filteredUsers.length === 0 ? (
                <p className={`text-center py-8 ${sub} text-sm`}>No users found.</p>
              ) : (
                <div className="space-y-2">
                  {filteredUsers.map((u, i) => {
                    const isAdminUser = u.email === ADMIN_EMAIL;
                    const isPrem = u.isPremium || isAdminUser;
                    const joinDate = u.lastLogin ? new Date(u.lastLogin.seconds * 1000).toLocaleDateString() : "—";
                    const expiry = u.premiumExpiry ? new Date(u.premiumExpiry.seconds * 1000).toLocaleDateString() : null;
                    return (
                      <div key={i} className={`flex items-center gap-3 p-4 rounded-2xl border ${card}`}>
                        <img src={u.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || "U")}&background=1e3a8a&color=d4af37`}
                          alt="" className="w-9 h-9 rounded-full flex-shrink-0"
                          onError={e => { e.target.src = `https://ui-avatars.com/api/?name=U&background=1e3a8a&color=d4af37`; }} />
                        <div className="flex-1 min-w-0">
                          <p className="font-black text-sm truncate">{u.name || "Unknown"}</p>
                          <p className={`text-[9px] ${sub} font-mono truncate`}>{u.email}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase ${isPrem ? "bg-yellow-500/20 text-yellow-400" : "bg-slate-500/20 text-slate-400"}`}>
                              {isAdminUser ? "Admin" : isPrem ? "Pro" : "Free"}
                            </span>
                            <span className={`text-[8px] ${sub}`}>Login: {joinDate}</span>
                            {expiry && <span className={`text-[8px] ${sub}`}>Expires: {expiry}</span>}
                            <span className="bg-blue-500/20 text-blue-400 text-[8px] font-black px-2 py-0.5 rounded-full uppercase">{u.tableCount || 0} tables</span>
                          </div>
                        </div>
                        {!isAdminUser && (
                          isPrem
                            ? <button onClick={() => handleRevokePremium(u)} className="bg-red-500/15 hover:bg-red-500 text-red-400 hover:text-white text-[9px] font-black px-3 py-1.5 rounded-xl uppercase transition-all flex-shrink-0">Revoke</button>
                            : <button onClick={() => handleMakePremium(u)} className="bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-400 hover:to-amber-300 text-slate-900 text-[9px] font-black px-3 py-1.5 rounded-xl uppercase transition-all flex-shrink-0">⭐ Make Pro</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ══ PAYMENTS TAB ══ */}
          {activeTab === "payments" && (
            <div className="space-y-3">
              {payLoading ? (
                <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin"></div></div>
              ) : pendingPayments.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">💸</div>
                  <p className={`${sub} text-sm font-black uppercase`}>No payment requests yet</p>
                </div>
              ) : (
                pendingPayments.map((p, i) => (
                  <div key={i} className={`p-4 rounded-2xl border ${card}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-black text-sm truncate">{p.email || "Unknown"}</p>
                          <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase flex-shrink-0 ${
                            p.status === "approved" ? "bg-emerald-500/20 text-emerald-400" :
                            p.status === "rejected" ? "bg-red-500/20 text-red-400" :
                            "bg-amber-500/20 text-amber-400"
                          }`}>{p.status}</span>
                        </div>
                        <p className={`text-[10px] font-mono ${sub}`}>
                          TxID: <span className="text-yellow-400 font-black">{p.transactionId}</span>
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-[9px] ${sub}`}>
                            Amount: <span className="font-black text-emerald-400">{p.amount} BDT</span>
                          </span>
                          <span className={`text-[9px] ${sub}`}>
                            Plan: <span className="font-black uppercase text-blue-400">{p.plan}</span>
                          </span>
                          {p.createdAt && (
                            <span className={`text-[9px] ${sub}`}>
                              {new Date(p.createdAt.seconds * 1000).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      {p.status === "pending" && (
                        <div className="flex flex-col gap-1.5 flex-shrink-0">
                          <button onClick={() => handleApprovePayment(p)}
                            className="bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-400 hover:to-amber-300 text-slate-900 text-[9px] font-black px-3 py-1.5 rounded-xl uppercase transition-all">
                            ✓ Approve
                          </button>
                          <button onClick={() => handleRejectPayment(p)}
                            className="bg-red-500/15 hover:bg-red-500 text-red-400 hover:text-white text-[9px] font-black px-3 py-1.5 rounded-xl uppercase transition-all">
                            ✗ Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WelcomeModal({ onClose, isDark, userName }) {
  const [step, setStep] = useState(0);
  const bg = isDark
  ? "bg-[#0f2038]"
  : "bg-[#ffffff]";

const text = isDark
  ? "text-[#f1f6ff]"
  : "text-[#172033]";

const sub = isDark
  ? "text-[#a9bbd6]"
  : "text-[#5f6f89]";
 
  const steps = [
    {
      icon: "👋",
      title: `Welcome, ${userName || "Friend"}!`,
      desc: "SheetMind is your AI-powered smart spreadsheet. Let's take a quick tour so you know what's here!",
      color: "#d4af37",
    },
    {
      icon: "➕",
      title: "Create a Table",
      desc: "Click the '+' button at the top to create a new table. Give it a name and start adding columns.",
      color: "#6366f1",
    },
    {
      icon: "⚡",
      title: "AI Table Generator",
      desc: "Click 'AI TABLE' button and type something like 'Student list 20 rows' — AI will build the table instantly!",
      color: "#8b5cf6",
    },
    {
      icon: "📋",
      title: "Templates",
      desc: "Click 'TEMPLATES' to choose from ready-made sheets — Student, Salary, Inventory, CRM and more.",
      color: "#10b981",
    },
    {
      icon: "🔗",
      title: "Share & Collaborate",
      desc: "Click 'SHARE' to invite others. Add their email as Editor or Viewer — multiple people can work on the same table!",
      color: "#22d3ee",
    },
    {
      icon: "📤",
      title: "Export Your Data",
      desc: "Use 'EXPORT PDF' or 'EXPORT' button to download your table as PDF or Excel file anytime.",
      color: "#f59e0b",
    },
    {
      icon: "🚀",
      title: "You're All Set!",
      desc: "Free plan gives you 4 tables. Upgrade to Pro for unlimited tables, unlimited AI, and priority support.",
      color: "#d4af37",
    },
  ];
 
  const current = steps[step];
  const isLast  = step === steps.length - 1;
 
  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
      <div className={`${bg} ${text} w-full max-w-sm rounded-[2rem] border overflow-hidden`}
  style={{
    borderColor: isDark ? "#3f5f89" : "#c8d7ec",
    boxShadow: isDark
      ? "0 28px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)"
      : "0 28px 80px rgba(43,76,126,0.22), inset 0 1px 0 rgba(255,255,255,0.9)"
  }}>
 
        {/* Top color bar */}
        <div className="h-1.5 w-full transition-all duration-500"
          style={{ background: `linear-gradient(to right, ${current.color}, ${current.color}88)` }} />
 
        {/* Content */}
        <div className="p-8 text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 text-4xl transition-all duration-300"
            style={{ backgroundColor: current.color + "18", border: `2px solid ${current.color}40` }}>
            {current.icon}
          </div>
 
          <h2 className="text-xl font-black uppercase tracking-tight mb-3"
            style={{ color: current.color }}>
            {current.title}
          </h2>
 
          <p className={`text-[12px] ${sub} leading-relaxed mb-6`}>
            {current.desc}
          </p>
 
          {/* Step dots */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((_, i) => (
              <button key={i} onClick={() => setStep(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width:  i === step ? "20px" : "8px",
                  height: "8px",
                  backgroundColor: i === step ? current.color : (isDark ? "#1e3a8a" : "#e2e8f0"),
                }} />
            ))}
          </div>
 
          {/* Buttons */}
          <div className="flex gap-3">
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)}
                className={`flex-1 py-3 rounded-2xl font-black text-xs border transition-all ${
                  isDark ? "border-yellow-900/40 text-blue-400 hover:bg-yellow-500/10" : "border-blue-200 text-slate-500 hover:bg-blue-50"
                }`}>
                ← Back
              </button>
            )}
            <button
              onClick={isLast ? onClose : () => setStep(s => s + 1)}
              className="flex-1 py-3 rounded-2xl font-black text-sm text-slate-900 transition-all shadow-lg"
              style={{ background: `linear-gradient(135deg, ${current.color}, ${current.color}cc)` }}>
              {isLast ? "Get Started!" : "Next →"}
            </button>
          </div>
 
          {/* Skip */}
          {!isLast && (
            <button onClick={onClose}
              className={`mt-3 text-[10px] font-black uppercase ${sub} hover:opacity-60 transition-opacity`}>
              Skip Tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
// ============================================================
// PRO GATE MODAL
// ============================================================
function ProGateModal({ message, onUpgrade, onClose, isDark, tabCount }) {
  const bg   = isDark ? "bg-[#060e1c]" : "bg-white";
  const text = isDark ? "text-slate-100" : "text-slate-900";
  const sub  = isDark ? "text-blue-400"  : "text-slate-500";
  const card = isDark ? "bg-[#08122a] border-yellow-900/20" : "bg-blue-50 border-blue-200";
  const isCooldown = message.includes("hours") || message.includes("min");

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className={`${bg} ${text} w-full max-w-sm rounded-[2rem] border border-yellow-500/30 shadow-2xl overflow-hidden`}>

        <div className="bg-gradient-to-br from-blue-900/40 via-yellow-900/10 to-transparent p-7 text-center border-b border-yellow-500/10">
          <div className="w-16 h-16 rounded-full bg-yellow-500/10 border-2 border-yellow-500/30 flex items-center justify-center mx-auto mb-4 text-3xl">
            {isCooldown ? "⏳" : "🔒"}
          </div>
          <h2 className="text-xl font-black uppercase tracking-tight bg-gradient-to-r from-blue-400 to-yellow-300 bg-clip-text text-transparent">
            {isCooldown ? "Cooldown Active" : "Free Plan Limit"}
          </h2>
          <p className={`text-[11px] ${sub} mt-2 leading-relaxed`}>{message}</p>
        </div>

        <div className="p-6">
          <div className={`p-4 rounded-2xl border mb-5 ${card}`}>
            <div className="flex justify-between items-center mb-3">
              <span className={`text-[10px] font-black uppercase tracking-widest ${sub}`}>তোমার Plan</span>
              <span className="text-[10px] font-black px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 uppercase">Free</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className={`text-[11px] ${sub}`}>Tables used</span>
              <span className="text-[11px] font-black text-yellow-400">{tabCount} / {FREE_TABLE_LIMIT}</span>
            </div>
            <div className={`w-full h-2 rounded-full ${isDark ? "bg-slate-800" : "bg-slate-200"} overflow-hidden`}>
              <div
                className="h-full rounded-full bg-gradient-to-r from-yellow-500 to-amber-400 transition-all"
                style={{ width: `${Math.min((tabCount / FREE_TABLE_LIMIT) * 100, 100)}%` }}
              />
            </div>
          </div>

          <div className={`p-4 rounded-2xl border mb-5 ${isDark ? "border-yellow-500/20 bg-yellow-500/5" : "border-yellow-200 bg-yellow-50"}`}>
            <p className="text-[10px] font-black text-yellow-400 uppercase tracking-widest mb-3">⭐ Pro তে যা পাবে</p>
            {[
              "Unlimited tables — কোনো limit নেই",
              "24h cooldown নেই",
              "সব AI features unlimited",
              "Ad-free experience",
            ].map((b, i) => (
              <p key={i} className={`text-[10px] ${sub} flex items-center gap-2 mt-1.5`}>
                <span className="text-yellow-400 font-black">✓</span> {b}
              </p>
            ))}
          </div>

          <button onClick={onUpgrade}
            className="w-full py-3.5 rounded-2xl font-black text-sm bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-400 hover:to-amber-300 text-slate-900 transition-all shadow-lg shadow-yellow-900/20 mb-3">
            ⭐ Upgrade to Pro — 640 BDT/month
          </button>
          <button onClick={onClose}
            className={`w-full py-3 rounded-2xl font-black text-xs border transition-all ${isDark ? "border-yellow-900/40 hover:bg-yellow-500/10 text-blue-400" : "border-blue-200 hover:bg-blue-50 text-slate-500"}`}>
            পরে করবো
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// UPGRADE MODAL — Deep Blue + Gold Theme
// ============================================================
function UpgradeModal({ onClose, isDark, currentUser, onSupportClick }) {
  const [plan, setPlan] = useState("monthly");
  const [step, setStep] = useState("plans");
  const [txId, setTxId] = useState("");
  const [amount, setAmount] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const PLANS = {
    monthly: { label: "Monthly", bdtPrice: 491.1, usdPrice: 4, period: "month", months: 1, checkoutLink: CHECKOUT_MONTHLY_LINK },
    yearly:  { label: "Yearly",  bdtPrice: 6138.62, usdPrice: 50, period: "year", months: 12, save: "Save 30%", checkoutLink: CHECKOUT_YEARLY_LINK },
  };
  const selectedPlan = PLANS[plan];

  // Deep Blue + Gold colors
  const bg      = isDark ? "bg-[#060e1c]" : "bg-white";
  const cardBg  = isDark ? "bg-[#08122a]/80 border-yellow-900/20" : "bg-blue-50 border-blue-200";
  const text    = isDark ? "text-slate-100" : "text-slate-900";
  const sub     = isDark ? "text-blue-400" : "text-slate-500";
  const inputCls = isDark
    ? "bg-[#050d1f] border-[#d4af3730] text-slate-200 placeholder:text-blue-900 focus:border-yellow-500"
    : "bg-blue-50 border-blue-300 text-slate-800 placeholder:text-slate-400 focus:border-yellow-400";

  const copyNumber = () => {
    navigator.clipboard.writeText(BKASH_NUMBER);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const validateBkash = () => {
    const newErrors = {};
    if (!txId.trim() || txId.trim().length < 5)
      newErrors.txId = "Valid Transaction ID দাও (কমপক্ষে ৫ অক্ষর)";
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount < selectedPlan.bdtPrice)
      newErrors.amount = `কমপক্ষে ${selectedPlan.bdtPrice} BDT পাঠাতে হবে`;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitPayment = async () => {
    if (!validateBkash()) return;
    setSubmitting(true);
    try {
      if (db && currentUser) {
        const requestId = `bkash_${currentUser.uid}_${Date.now()}`;

        // Save payment request
        await setDoc(doc(db, "payment_requests", requestId), {
          uid:           currentUser.uid,
          email:         currentUser.email,
          name:          currentUser.displayName || "",
          transactionId: txId.trim().toUpperCase(),
          amount:        parseFloat(amount),
          plan,
          bdtPrice:      selectedPlan.bdtPrice,
          status:        "pending",
          method:        "bkash",
          createdAt:     serverTimestamp(),
        });

        // Also update user profile payment status
        await setDoc(doc(db, "users", currentUser.uid), {
          paymentStatus:       "pending",
          paymentRef:          txId.trim().toUpperCase(),
          paymentAmount:       parseFloat(amount),
          paymentPlan:         plan,
          paymentSubmittedAt:  serverTimestamp(),
        }, { merge: true });

        // Save admin notification
        await setDoc(doc(db, "admin_notifications", requestId), {
          type:          "payment_request",
          uid:           currentUser.uid,
          email:         currentUser.email,
          transactionId: txId.trim().toUpperCase(),
          amount:        parseFloat(amount),
          plan,
          read:          false,
          createdAt:     serverTimestamp(),
        });
      }
      setStep("submitted");
    } catch (err) {
      setErrors({ general: "Submit করতে সমস্যা হয়েছে। আবার চেষ্টা করো।" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success Screen ──
  if (step === "submitted") {
    return (
      <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
        <div className={`${bg} ${text} w-full max-w-sm rounded-[2rem] border border-yellow-500/40 shadow-2xl p-10 text-center`}>
          <div className="w-20 h-20 rounded-full bg-yellow-500/10 border-2 border-yellow-500/40 flex items-center justify-center mx-auto mb-6 text-4xl">✅</div>
          <h2 className="text-2xl font-black uppercase tracking-tight mb-2 bg-gradient-to-r from-yellow-400 to-amber-300 bg-clip-text text-transparent">
            Request Submitted!
          </h2>
          <p className={`text-sm ${sub} mb-3`}>
            তোমার payment request admin এর কাছে পাঠানো হয়েছে। Approve হলে Premium activate হবে।
          </p>
          <div className={`p-3 rounded-xl border mb-2 ${isDark ? "border-yellow-500/20 bg-yellow-500/5" : "border-yellow-200 bg-yellow-50"}`}>
            <p className={`text-[10px] font-mono ${sub}`}>TXN ID: <span className="font-black text-yellow-400">{txId.toUpperCase()}</span></p>
            <p className={`text-[10px] ${sub} mt-1`}>Plan: <span className="font-black uppercase text-yellow-400">{plan}</span> · Amount: <span className="font-black text-yellow-400">{amount} BDT</span></p>
          </div>
          <div className={`p-3 rounded-xl border ${isDark ? "border-blue-500/20 bg-blue-500/5" : "border-blue-200 bg-blue-50"} mb-5`}>
            <p className="text-[10px] font-black text-blue-400 uppercase tracking-wider">⏳ Admin Approval Pending</p>
            <p className={`text-[9px] ${sub} mt-1`}>সাধারণত কয়েক ঘণ্টার মধ্যে approve হয়।</p>
          </div>
          <button onClick={onSupportClick} className={`mb-3 flex w-full items-center justify-center rounded-2xl border py-3 text-[11px] font-black uppercase tracking-widest transition-all ${isDark ? "border-blue-500/20 text-blue-400 hover:bg-blue-500/10" : "border-blue-200 text-blue-600 hover:bg-blue-50"}`}>Payment Support</button>
          <button onClick={onClose}
            className="w-full py-3 rounded-2xl font-black text-sm bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-400 hover:to-amber-300 text-slate-900 transition-all">
            বন্ধ করো
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
      <div className={`${bg} ${text} w-full max-w-md rounded-[2rem] border border-yellow-500/30 shadow-2xl overflow-hidden`}
        style={{ maxHeight: "90vh", overflowY: "auto" }}>

        {/* Header */}
        <div className="bg-gradient-to-br from-blue-900/40 via-yellow-900/10 to-transparent p-8 border-b border-yellow-500/10">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] font-black text-yellow-400 uppercase tracking-[0.3em] mb-2">⭐ Upgrade to Premium</p>
              <h2 className="text-2xl font-black tracking-tight bg-gradient-to-r from-blue-400 to-yellow-300 bg-clip-text text-transparent">
                NeoSheet Pro
              </h2>
              <p className={`text-[10px] ${sub} mt-1`}>bKash দিয়ে payment করো, admin activate করবে</p>
            </div>
            <button onClick={onClose} className="text-xl hover:opacity-60 transition-opacity mt-1">✕</button>
          </div>
        </div>

        <div className="p-6">

          {/* STEP 1: Plan Selection */}
          {step === "plans" && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {Object.entries(PLANS).map(([key, p]) => (
                  <button key={key} onClick={() => setPlan(key)}
                    className={`relative p-4 rounded-2xl border text-left transition-all ${
                      plan === key
                        ? "border-yellow-500 bg-yellow-500/10"
                        : cardBg
                    }`}>
                    {p.save && (
                      <span className="absolute -top-2 -right-2 bg-yellow-500 text-slate-900 text-[8px] font-black px-2 py-0.5 rounded-full uppercase">{p.save}</span>
                    )}
                    <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${plan === key ? "text-yellow-400" : sub}`}>{p.label}</p>
                    <p className="text-xl font-black">{p.bdtPrice} <span className={`text-sm font-normal ${sub}`}>BDT</span></p>
                    <p className={`text-[10px] ${sub}`}>≈ ${p.usdPrice} / {p.period}</p>
                  </button>
                ))}
              </div>

              {/* Benefits */}
              <div className={`p-4 rounded-2xl border mb-5 ${isDark ? "border-blue-500/20 bg-blue-500/5" : "border-blue-200 bg-blue-50"}`}>
                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-2">⭐ Premium সুবিধা</p>
                {[
                  "Unlimited tables (free তে মাত্র ৪টা)",
                  "24 ঘণ্টা cooldown নেই",
                  "সব AI features unlimited",
                  "Priority support",
                  "Ad-free experience"
                ].map((b, i) => (
                  <p key={i} className={`text-[10px] ${sub} flex items-center gap-2 mt-1.5`}>
                    <span className="text-yellow-400">✓</span> {b}
                  </p>
                ))}
              </div>

              <button onClick={() => setStep("bkash")}
                className="w-full bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-400 hover:to-amber-300 text-slate-900 py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-yellow-900/20">
                <span>💸</span> bKash দিয়ে Pay করো →
              </button>
              {/* OR divider */}
              <div className="flex items-center gap-3 my-3">
                <div className={`flex-1 h-px ${isDark ? "bg-yellow-900/30" : "bg-blue-100"}`} />
                <span className={`text-[9px] font-black uppercase tracking-widest ${sub}`}>or</span>
                <div className={`flex-1 h-px ${isDark ? "bg-yellow-900/30" : "bg-blue-100"}`} />
              </div>
 
              {/* Card Payment Button */}
              <a
                href={selectedPlan.checkoutLink || CHECKOUT_MONTHLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg border border-blue-500/30 hover:border-blue-400/60"
                style={{ background: "linear-gradient(135deg, #1e3a8a, #1d4ed8)", color: "white" }}>
                💳 Pay with Card (International)
              </a>
              <p className={`text-[9px] ${sub} text-center mt-1`}>
                Visa, Mastercard, PayPal - automatic activation after payment confirmation
              </p>
              <button onClick={onSupportClick} className={`mt-3 flex w-full items-center justify-center rounded-xl border py-2 text-[10px] font-black uppercase tracking-widest transition-all ${isDark ? "border-blue-500/20 text-blue-400 hover:bg-blue-500/10" : "border-blue-200 text-blue-600 hover:bg-blue-50"}`}>Payment Support</button>
            </>
          )}

          {/* STEP 2: bKash Payment Form */}
          {step === "bkash" && (
            <>
              {/* Instructions */}
              <div className={`p-4 rounded-2xl border mb-5 ${isDark ? "border-yellow-500/20 bg-yellow-500/5" : "border-yellow-200 bg-yellow-50"}`}>
                <p className="text-[10px] font-black text-yellow-400 uppercase tracking-widest mb-3">📱 bKash দিয়ে কীভাবে Pay করবে</p>
                <ol className={`text-[10px] ${sub} space-y-2`}>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-400 font-black flex-shrink-0">১.</span>
                    bKash app খোলো → <span className="text-yellow-300 font-black">Send Money</span> চাপো
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-400 font-black flex-shrink-0">২.</span>
                    নিচের নম্বরে <span className="text-yellow-300 font-black">{selectedPlan.bdtPrice} BDT</span> পাঠাও
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-400 font-black flex-shrink-0">৩.</span>
                    Confirmation SMS থেকে <span className="font-black">Transaction ID</span> copy করো
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-400 font-black flex-shrink-0">৪.</span>
                    নিচের form এ TXN ID ও amount দিয়ে submit করো
                  </li>
                </ol>

                {/* bKash number box */}
                <div className={`mt-4 p-3 rounded-xl ${isDark ? "bg-[#050d1f]" : "bg-white"} border ${isDark ? "border-yellow-500/20" : "border-yellow-300"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[9px] font-black uppercase tracking-widest ${sub}`}>bKash Number</span>
                    <button onClick={copyNumber}
                      className={`text-[9px] font-black px-2 py-1 rounded-lg transition-all ${copied ? "bg-yellow-500 text-slate-900" : "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"}`}>
                      {copied ? "✓ Copied!" : "Copy"}
                    </button>
                  </div>
                  <p className="text-xl font-black text-yellow-400 tracking-widest text-center">{BKASH_NUMBER}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-[9px] ${sub}`}>পাঠানোর পরিমাণ</span>
                    <span className="text-base font-black text-yellow-400">{selectedPlan.bdtPrice} BDT</span>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4 mb-5">
                <div>
                  <label className={`text-[10px] font-black uppercase tracking-widest ${sub} mb-1.5 block`}>
                    Transaction ID (TrxID) *
                  </label>
                  <input
                    type="text"
                    value={txId}
                    onChange={(e) => setTxId(e.target.value.toUpperCase())}
                    placeholder="যেমন: AB12345678"
                    className={`w-full px-4 py-3 rounded-2xl border outline-none text-sm font-mono tracking-widest transition-all ${inputCls} ${errors.txId ? "border-red-500" : ""}`}
                  />
                  {errors.txId && <p className="text-red-400 text-[10px] mt-1">{errors.txId}</p>}
                </div>
                <div>
                  <label className={`text-[10px] font-black uppercase tracking-widest ${sub} mb-1.5 block`}>
                    Amount Sent (BDT) *
                  </label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`${selectedPlan.bdtPrice}`}
                    min={selectedPlan.bdtPrice}
                    className={`w-full px-4 py-3 rounded-2xl border outline-none text-sm transition-all ${inputCls} ${errors.amount ? "border-red-500" : ""}`}
                  />
                  {errors.amount && <p className="text-red-400 text-[10px] mt-1">{errors.amount}</p>}
                </div>
                {errors.general && (
                  <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p className="text-red-400 text-[10px]">{errors.general}</p>
                  </div>
                )}
              </div>

              {/* Warning */}
              <div className={`p-3 rounded-xl border mb-5 ${isDark ? "border-blue-500/20 bg-blue-500/5" : "border-blue-200 bg-blue-50"}`}>
                <p className="text-[9px] font-black text-blue-400 uppercase tracking-wider">⚠️ Manual Approval Required</p>
                <p className={`text-[9px] ${sub} mt-0.5`}>Submit করার পর admin verify করবে। Instant activate হবে না।</p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep("plans")}
                  className={`px-5 py-3 rounded-2xl font-black text-xs border transition-all ${isDark ? "border-yellow-900/40 hover:bg-yellow-500/10 text-blue-400" : "border-blue-200 hover:bg-blue-50"}`}>
                  Back
                </button>
                <button
                  onClick={handleSubmitPayment}
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-400 hover:to-amber-300 disabled:opacity-50 text-slate-900 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-md shadow-yellow-900/20"
                >
                  {submitting
                    ? <><div className="w-3 h-3 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin"></div>Submitting...</>
                    : "✓ Submit for Approval"
                  }
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
//==========================================================
  //AITableModal
//==========================================================
function AITableModal({ onClose, onGenerate, isDark, isPremium, aiTableUsage }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const remaining = Math.max(AI_FREE_TABLE_LIMIT - aiTableUsage, 0);

  const bg = isDark ? "bg-[#0f2038]" : "bg-white";
  const text = isDark ? "text-[#f1f6ff]" : "text-[#172033]";
  const sub = isDark ? "text-[#9fb3d1]" : "text-[#5f6f89]";
  const input = isDark
    ? "bg-[#0a1729] border-[#315174] text-[#f1f6ff] placeholder:text-[#7186a6]"
    : "bg-white border-[#c8d7ec] text-[#172033] placeholder:text-[#7b8aa3]";

  return (
    <div className="fixed inset-0 z-[320] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className={`${bg} ${text} w-full max-w-md rounded-[2rem] border overflow-hidden`}
        style={{
          borderColor: isDark ? "#3f5f89" : "#c8d7ec",
          boxShadow: isDark
            ? "0 28px 80px rgba(0,0,0,0.45)"
            : "0 28px 80px rgba(43,76,126,0.22)",
        }}
      >
        <div className="p-7 border-b" style={{ borderColor: isDark ? "#29456b" : "#d9e4f3" }}>
          <div className="flex justify-between items-start gap-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#3978d8] mb-2">
                AI Table Builder
              </p>
              <h2 className="text-xl font-black uppercase tracking-tight">
                Build a table from a command
              </h2>
              <p className={`text-[11px] ${sub} mt-2 leading-relaxed`}>
                Tell AI what table you need. It will create columns and rows automatically.
              </p>
            </div>
            <button onClick={onClose} className={`text-xl ${sub} hover:opacity-60`}>
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {!isPremium && (
            <div className="mb-4 rounded-2xl border px-4 py-3"
              style={{
                borderColor: remaining > 0 ? "#3978d833" : "#ef444433",
                background: remaining > 0 ? "#3978d80d" : "#ef44440d",
              }}
            >
              <p className={`text-[10px] font-black uppercase ${remaining > 0 ? "text-[#3978d8]" : "text-red-400"}`}>
                Free AI tables: {aiTableUsage}/{AI_FREE_TABLE_LIMIT}
              </p>
              <p className={`text-[9px] ${sub} mt-1`}>
                {remaining > 0 ? `${remaining} AI table remaining.` : "Upgrade for unlimited AI tables."}
              </p>
            </div>
          )}

          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Example: Create a product inventory table with 15 rows, SKU, stock, supplier, reorder level and price."
            className={`w-full h-36 resize-none rounded-2xl border outline-none px-4 py-3 text-sm leading-relaxed ${input} focus:border-[#3978d8] focus:ring-2 focus:ring-[#3978d8]/15`}
          />

          <div className="flex gap-3 mt-5">
            <button
              onClick={onClose}
              className={`flex-1 py-3 rounded-xl font-black text-[11px] uppercase border ${
                isDark
                  ? "border-[#315174] text-[#c6d7ef] hover:bg-[#173251]"
                  : "border-[#c8d7ec] text-[#36516f] hover:bg-[#eaf2ff]"
              }`}
            >
              Cancel
            </button>

            <button
              onClick={async () => {
                if (!prompt.trim() || loading) return;
                setLoading(true);
                await onGenerate(prompt);
                setLoading(false);
              }}
              disabled={!prompt.trim() || loading}
              className="flex-1 py-3 rounded-xl font-black text-[11px] uppercase bg-gradient-to-r from-[#2563eb] to-[#0ea5e9] hover:from-[#1d4ed8] hover:to-[#0284c7] disabled:opacity-50 text-white shadow-[0_12px_28px_rgba(37,99,235,0.28)] flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Generating...
                </>
              ) : "Generate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
// ============================================================
// AI ANALYSIS MODAL
// ============================================================
function AIAnalysisModal({ columns, rows, isDark, onClose }) {
  const bg   = isDark ? "bg-[#0f172a]" : "bg-white";
  const text = isDark ? "text-slate-100" : "text-slate-900";
  const sub  = isDark ? "text-slate-400" : "text-slate-500";
  const card = isDark ? "bg-slate-800/60 border-white/10" : "bg-slate-50 border-slate-200";

  const numericCols = columns.filter(col =>
    col !== "ID" && rows.some(r => !isNaN(parseFloat(cellVal(r[col]))) && cellVal(r[col]) !== "")
  );

  const getStats = (col) => {
    const vals = rows.map(r => parseFloat(cellVal(r[col]))).filter(v => !isNaN(v));
    if (!vals.length) return null;
    const sum = vals.reduce((a, b) => a + b, 0);
    const avg = sum / vals.length;
    const sorted = [...vals].sort((a, b) => a - b);
    const median = sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];
    return { sum, avg, min: Math.min(...vals), max: Math.max(...vals), median, count: vals.length };
  };

  const textStats = columns.filter(c => c !== "ID" && !numericCols.includes(c)).map(col => {
    const vals = rows.map(r => cellVal(r[col])).filter(v => v !== "");
    const freq = {};
    vals.forEach(v => { freq[v] = (freq[v] || 0) + 1; });
    return { col, count: vals.length, unique: Object.keys(freq).length, top: Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 3) };
  });

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`${bg} ${text} w-full max-w-2xl rounded-[2rem] border ${isDark ? "border-white/10" : "border-slate-200"} shadow-2xl p-8 max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-black uppercase tracking-tight">🤖 AI Analysis</h2>
            <p className={`text-[10px] ${sub} uppercase tracking-widest mt-1`}>{rows.length} rows · {columns.length} columns</p>
          </div>
          <button onClick={onClose} className="text-2xl hover:opacity-60 transition-opacity">✕</button>
        </div>
        {numericCols.length > 0 && (
          <div className="mb-6">
            <h3 className={`text-[10px] font-black uppercase tracking-widest ${sub} mb-3`}>📊 Numeric Columns</h3>
            <div className="space-y-3">
              {numericCols.map(col => {
                const s = getStats(col);
                if (!s) return null;
                return (
                  <div key={col} className={`p-4 rounded-2xl border ${card}`}>
                    <p className="font-black text-sm text-sky-400 mb-3 uppercase">{col}</p>
                    <div className="grid grid-cols-5 gap-2">
                      {[
                        { label: "Sum",    value: s.sum.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                        { label: "Avg",    value: s.avg.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                        { label: "Min",    value: s.min },
                        { label: "Max",    value: s.max },
                        { label: "Median", value: s.median.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                      ].map(stat => (
                        <div key={stat.label} className={`p-2 rounded-xl text-center ${isDark ? "bg-slate-900/60" : "bg-white"}`}>
                          <p className="text-xs font-black text-emerald-400">{stat.value}</p>
                          <p className={`text-[8px] font-black uppercase ${sub} mt-0.5`}>{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {textStats.length > 0 && (
          <div>
            <h3 className={`text-[10px] font-black uppercase tracking-widest ${sub} mb-3`}>📝 Text Columns</h3>
            <div className="space-y-3">
              {textStats.map(({ col, count, unique, top }) => (
                <div key={col} className={`p-4 rounded-2xl border ${card}`}>
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-black text-sm text-indigo-400 uppercase">{col}</p>
                    <span className={`text-[9px] font-black ${sub}`}>{count} filled · {unique} unique</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {top.map(([val, cnt]) => (
                      <span key={val} className={`text-[9px] font-black px-2 py-1 rounded-full ${isDark ? "bg-slate-700 text-slate-300" : "bg-slate-200 text-slate-700"}`}>{val} ({cnt})</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {numericCols.length === 0 && textStats.length === 0 && <p className={`text-center py-8 ${sub}`}>No data to analyze yet.</p>}
      </div>
    </div>
  );
}

// ============================================================
// COLUMN TYPE MODAL
// ============================================================
function ColumnTypeModal({ col, currentType, currentOptions, onSave, onClose, isDark }) {
  const [type, setType]           = useState(currentType || "text");
  const [optionsStr, setOptionsStr] = useState(currentOptions ? currentOptions.join(", ") : "");
  const bg     = isDark ? "bg-slate-900" : "bg-white";
  const border = isDark ? "border-white/10" : "border-slate-200";
  const text   = isDark ? "text-slate-100" : "text-slate-900";
  const input  = isDark ? "bg-slate-800 border-white/10 text-slate-200" : "bg-slate-100 border-slate-300 text-slate-900";
  const types  = [
    { value: "text",     label: "📝 Text",     desc: "Free-form text input" },
    { value: "number",   label: "🔢 Number",   desc: "Numeric values only" },
    { value: "email",    label: "📧 Email",    desc: "Validates email format" },
    { value: "date",     label: "📅 Date",     desc: "Date picker input" },
    { value: "checkbox", label: "☑️ Checkbox", desc: "Boolean true/false" },
    { value: "dropdown", label: "📋 Dropdown", desc: "Select from options" },
  ];
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`${bg} ${text} w-full max-w-md rounded-[2rem] border ${border} shadow-2xl p-8`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-black text-lg uppercase">Column Type: {col}</h3>
          <button onClick={onClose} className="text-xl hover:opacity-60">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-6">
          {types.map(t => (
            <button key={t.value} onClick={() => setType(t.value)}
              className={`p-3 rounded-2xl border text-left transition-all ${
                type === t.value
                  ? "border-indigo-500 bg-indigo-500/10"
                  : `border-transparent ${isDark ? "bg-slate-800 hover:bg-slate-700" : "bg-slate-100 hover:bg-slate-200"}`
              }`}>
              <div className="text-sm font-black">{t.label}</div>
              <div className={`text-[10px] mt-1 ${isDark ? "text-slate-500" : "text-slate-400"}`}>{t.desc}</div>
            </button>
          ))}
        </div>
        {type === "dropdown" && (
          <div className="mb-6">
            <label className={`text-[10px] font-black uppercase tracking-widest ${isDark ? "text-slate-400" : "text-slate-500"} mb-2 block`}>Options (comma-separated)</label>
            <input type="text" value={optionsStr} onChange={e => setOptionsStr(e.target.value)} placeholder="Pending, In Progress, Done"
              className={`w-full px-4 py-3 rounded-2xl border outline-none text-sm ${input} focus:border-indigo-500`} />
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={onClose} className={`flex-1 py-3 rounded-2xl font-black text-[11px] uppercase border ${isDark ? "border-white/10 hover:bg-slate-800" : "border-slate-200 hover:bg-slate-100"} transition-all`}>Cancel</button>
          <button onClick={() => onSave(type, optionsStr.split(",").map(s => s.trim()).filter(Boolean))} className="flex-1 py-3 rounded-2xl font-black text-[11px] uppercase bg-indigo-600 hover:bg-indigo-500 text-white transition-all">Save Type</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CELL FORMAT MODAL
// ============================================================
function CellFormatModal({ col, rowIdx, scope, currentCell, currentColFormat, onSave, onClose, isDark, isPremium }) {
  const current       = scope === "cell" ? (currentCell || {}) : (currentColFormat || {});
  const [fontSize,   setFontSize]   = useState(current.fontSize   || 14);
  const [color,      setColor]      = useState(current.color      || (isDark ? "#e2e8f0" : "#0f172a"));
  const [bgColor,    setBgColor]    = useState(current.bgColor    || "");
  const [fontWeight, setFontWeight] = useState(current.fontWeight || "normal");

  const bg     = isDark ? "bg-slate-900" : "bg-white";
  const border = isDark ? "border-white/10" : "border-slate-200";
  const text   = isDark ? "text-slate-100" : "text-slate-900";
  const sub    = isDark ? "text-slate-400" : "text-slate-500";
  const premiumColors = ["#ff6b6b","#ffd93d","#6bcb77","#4d96ff","#c77dff","#ff9a3c","#00f5d4"];
  const title  = scope === "cell" ? `Cell [${col}, row ${rowIdx + 1}]` : scope === "tab" ? "Tab Color" : `Column: ${col}`;
  const bold   = fontWeight === "bold";

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`${bg} ${text} w-full max-w-sm rounded-[2rem] border ${border} shadow-2xl p-8`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-black text-lg uppercase">Format: {title}</h3>
          <button onClick={onClose} className="text-xl hover:opacity-60">✕</button>
        </div>
        <div className="space-y-5">
          {scope !== "tab" && (
            <>
              <div>
                <label className={`text-[10px] font-black uppercase tracking-widest ${sub} mb-2 block`}>Font Size: {fontSize}px</label>
                <input type="range" min="10" max="24" value={fontSize} onChange={e => setFontSize(Number(e.target.value))} className="w-full accent-indigo-500" />
              </div>
              <div className="flex items-center gap-3">
                <label className={`text-[10px] font-black uppercase tracking-widest ${sub}`}>Bold</label>
                <button onClick={() => setFontWeight(b => b === "bold" ? "normal" : "bold")}
                  className={`w-10 h-6 rounded-full transition-all ${bold ? "bg-indigo-500" : isDark ? "bg-slate-700" : "bg-slate-200"}`}>
                  <div className={`w-4 h-4 rounded-full bg-white mx-0.5 shadow transition-transform ${bold ? "translate-x-4" : ""}`}></div>
                </button>
              </div>
              <div>
                <label className={`text-[10px] font-black uppercase tracking-widest ${sub} mb-2 block`}>Font Color</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-10 h-10 rounded-xl border-0 cursor-pointer bg-transparent" />
                  <span className="text-xs font-mono">{color}</span>
                  {isPremium && premiumColors.map(c => (
                    <button key={c} onClick={() => setColor(c)} style={{ background: c }} className="w-5 h-5 rounded-full border-2 border-white/20 hover:scale-125 transition-transform" />
                  ))}
                  {!isPremium && <span className="text-[9px] text-indigo-400 font-black uppercase">⭐ Premium colors</span>}
                </div>
              </div>
            </>
          )}
          <div>
            <label className={`text-[10px] font-black uppercase tracking-widest ${sub} mb-2 block`}>
              {scope === "tab" ? "Tab Background Color" : "Background Color"}
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <input type="color" value={bgColor || "#1e293b"} onChange={e => setBgColor(e.target.value)} className="w-10 h-10 rounded-xl border-0 cursor-pointer bg-transparent" />
              {premiumColors.map(c => (
                <button key={c} onClick={() => setBgColor(c)} style={{ background: c }} className="w-5 h-5 rounded-full border-2 border-white/20 hover:scale-125 transition-transform" />
              ))}
              <button onClick={() => setBgColor("")} className="text-[10px] text-red-400 hover:text-red-300 font-black uppercase">Clear</button>
            </div>
          </div>
          {scope !== "tab" && (
            <div className={`p-4 rounded-2xl border ${border} text-center`}
              style={{ fontSize: `${fontSize}px`, color, backgroundColor: bgColor || "transparent", fontWeight }}>
              Preview Text Sample
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className={`flex-1 py-3 rounded-2xl font-black text-[11px] uppercase border ${isDark ? "border-white/10 hover:bg-slate-800" : "border-slate-200 hover:bg-slate-100"} transition-all`}>Cancel</button>
          <button onClick={() => onSave({ fontSize, color, bgColor, fontWeight })} className="flex-1 py-3 rounded-2xl font-black text-[11px] uppercase bg-indigo-600 hover:bg-indigo-500 text-white transition-all">Apply</button>
        </div>
      </div>
    </div>
  );
}

// Safe arithmetic evaluator — only digits, + - * / ( ) . and spaces allowed.
// Replaces new Function()/eval() so a formula can never execute arbitrary JS.
function safeArithmeticEval(expr) {
  if (!/^[0-9+\-*/().\s]+$/.test(expr)) return null;
  let i = 0;
  const peek = () => expr[i];
  const parseNumber = () => {
    let start = i;
    while (i < expr.length && /[0-9.]/.test(expr[i])) i++;
    if (start === i) return NaN;
    return parseFloat(expr.slice(start, i));
  };
  const parseFactor = () => {
    while (peek() === " ") i++;
    if (peek() === "(") {
      i++;
      const val = parseExpr();
      while (peek() === " ") i++;
      if (peek() === ")") i++;
      return val;
    }
    if (peek() === "-") { i++; return -parseFactor(); }
    if (peek() === "+") { i++; return parseFactor(); }
    return parseNumber();
  };
  const parseTerm = () => {
    let val = parseFactor();
    while (true) {
      while (peek() === " ") i++;
      if (peek() === "*") { i++; val *= parseFactor(); }
      else if (peek() === "/") { i++; val /= parseFactor(); }
      else break;
    }
    return val;
  };
  const parseExpr = () => {
    let val = parseTerm();
    while (true) {
      while (peek() === " ") i++;
      if (peek() === "+") { i++; val += parseTerm(); }
      else if (peek() === "-") { i++; val -= parseTerm(); }
      else break;
    }
    return val;
  };
  try {
    const val = parseExpr();
    while (peek() === " ") i++;
    if (i !== expr.length) return null;
    return val;
  } catch (e) {
    return null;
  }
}

function evaluateFormula(formula, rows, columns) {
  if (!formula || !formula.startsWith("=")) return formula;
  
  try {
    const expr = formula.slice(1).toUpperCase().trim();
    
    // Helper: get cell value by reference like A1, B2
    const getCellVal = (ref) => {
      const match = ref.match(/^([A-Z]+)(\d+)$/);
      if (!match) return 0;
      const colIndex = match[1].split("").reduce((acc, c) => acc * 26 + c.charCodeAt(0) - 64, 0) - 1;
      const rowIndex = parseInt(match[2]) - 1;
      if (rowIndex < 0 || rowIndex >= rows.length) return 0;
      const col = columns[colIndex];
      if (!col) return 0;
      const val = parseFloat(cellVal(rows[rowIndex]?.[col]));
      return isNaN(val) ? 0 : val;
    };
 
    // Helper: get range values like A1:A10
    const getRangeVals = (range) => {
      const parts = range.split(":");
      if (parts.length !== 2) return [];
      const startMatch = parts[0].match(/^([A-Z]+)(\d+)$/);
      const endMatch   = parts[1].match(/^([A-Z]+)(\d+)$/);
      if (!startMatch || !endMatch) return [];
      const startCol = startMatch[1].split("").reduce((acc, c) => acc * 26 + c.charCodeAt(0) - 64, 0) - 1;
      const endCol   = endMatch[1].split("").reduce((acc, c) => acc * 26 + c.charCodeAt(0) - 64, 0) - 1;
      const startRow = parseInt(startMatch[2]) - 1;
      const endRow   = parseInt(endMatch[2]) - 1;
      const vals = [];
      for (let r = startRow; r <= endRow; r++) {
        for (let c = startCol; c <= endCol; c++) {
          const col = columns[c];
          if (!col) continue;
          const v = parseFloat(cellVal(rows[r]?.[col]));
          if (!isNaN(v)) vals.push(v);
        }
      }
      return vals;
    };
 
    // ── SUM ──
    if (expr.startsWith("SUM(")) {
      const inner = expr.slice(4, -1);
      const vals = getRangeVals(inner);
      return vals.reduce((a, b) => a + b, 0).toString();
    }
 
    // ── AVERAGE / AVG ──
    if (expr.startsWith("AVERAGE(") || expr.startsWith("AVG(")) {
      const inner = expr.startsWith("AVG(") ? expr.slice(4, -1) : expr.slice(8, -1);
      const vals = getRangeVals(inner);
      if (!vals.length) return "0";
      return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
    }
 
    // ── MIN ──
    if (expr.startsWith("MIN(")) {
      const inner = expr.slice(4, -1);
      const vals = getRangeVals(inner);
      return vals.length ? Math.min(...vals).toString() : "0";
    }
 
    // ── MAX ──
    if (expr.startsWith("MAX(")) {
      const inner = expr.slice(4, -1);
      const vals = getRangeVals(inner);
      return vals.length ? Math.max(...vals).toString() : "0";
    }
 
    // ── COUNT ──
    if (expr.startsWith("COUNT(")) {
      const inner = expr.slice(6, -1);
      return getRangeVals(inner).length.toString();
    }
 
    // ── IF ──
    if (expr.startsWith("IF(")) {
      const inner = expr.slice(3, -1);
      const parts = inner.split(",");
      if (parts.length < 3) return "ERROR";
      const condition = parts[0].trim();
      const ifTrue    = parts[1].trim().replace(/"/g, "");
      const ifFalse   = parts[2].trim().replace(/"/g, "");
      // Simple conditions: A1>10, A1=5, A1<100
      const condMatch = condition.match(/^([A-Z]+\d+)\s*([><=!]+)\s*(.+)$/);
      if (condMatch) {
        const cellValue = getCellVal(condMatch[1]);
        const operator  = condMatch[2];
        const compareVal = parseFloat(condMatch[3]);
        let result = false;
        if (operator === ">")  result = cellValue >  compareVal;
        if (operator === "<")  result = cellValue <  compareVal;
        if (operator === ">=") result = cellValue >= compareVal;
        if (operator === "<=") result = cellValue <= compareVal;
        if (operator === "=")  result = cellValue === compareVal;
        if (operator === "!=") result = cellValue !== compareVal;
        return result ? ifTrue : ifFalse;
      }
      return "ERROR";
    }
 
    // ── CONCAT ──
    if (expr.startsWith("CONCAT(")) {
      const inner = expr.slice(7, -1);
      const parts = inner.split(",").map(p => p.trim().replace(/"/g, ""));
      return parts.map(p => {
        if (/^[A-Z]+\d+$/.test(p)) {
          const colIdx = p.match(/^([A-Z]+)/)[1].split("").reduce((acc, c) => acc * 26 + c.charCodeAt(0) - 64, 0) - 1;
          const rowIdx = parseInt(p.match(/(\d+)$/)[1]) - 1;
          const col = columns[colIdx];
          return col ? cellVal(rows[rowIdx]?.[col]) : "";
        }
        return p;
      }).join("");
    }
 
    // ── ROUND ──
    if (expr.startsWith("ROUND(")) {
      const inner = expr.slice(6, -1);
      const parts = inner.split(",");
      const refArg = parts[0].trim();
      const val  = /^[A-Z]+\d+$/.test(refArg) ? getCellVal(refArg) : parseFloat(refArg);
      const dec  = parseInt(parts[1]?.trim() || "0");
      return parseFloat(val.toFixed(dec)).toString();
    }
 
    // ── Simple arithmetic: =A1+B1, =A1*2 (safe, no new Function/eval) ──
    const arithmeticExpr = expr.replace(/[A-Z]+\d+/g, (ref) => getCellVal(ref));
    const result = safeArithmeticEval(arithmeticExpr);
    return result === null || isNaN(result) ? "ERROR" : result.toString();
 
  } catch (e) {
    return "ERROR";
  }
}
// ============================================================
// UTILITY
// ============================================================
const hasNonAscii = (text) => {
  return Array.from(String(text ?? "")).some(ch => ch.charCodeAt(0) > 127);
};

const hexToRgb = (hex) => {
  if (!hex || !hex.startsWith("#")) return [15, 23, 42];
  const h = hex.replace("#", "");
  return [parseInt(h.substring(0, 2), 16) || 0, parseInt(h.substring(2, 4), 16) || 0, parseInt(h.substring(4, 6), 16) || 0];
};

function BorderModal({ selectedCell, rows, activeTabId, setTabs, onClose, isDark }) {
  const cell = rows[selectedCell?.rIdx]?.[selectedCell?.col];
  const cellObj = cell && typeof cell === "object" ? cell : {};
 
  const [bTop,    setBTop]    = useState(cellObj.borderTop    || "");
  const [bBottom, setBBottom] = useState(cellObj.borderBottom || "");
  const [bLeft,   setBLeft]   = useState(cellObj.borderLeft   || "");
  const [bRight,  setBRight]  = useState(cellObj.borderRight  || "");
  const [bColor,  setBColor]  = useState("#6366f1");
  const [bWidth,  setBWidth]  = useState("1");
  const [bStyle,  setBStyle]  = useState("solid");
 
  const makeBorder = () => `${bWidth}px ${bStyle} ${bColor}`;
 
  const handleApply = () => {
    const { rIdx, col } = selectedCell;
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      const updatedRows = [...t.rows];
      const existing = updatedRows[rIdx]?.[col] || {};
      const existingObj = typeof existing === "object" ? existing : { value: String(existing) };
      updatedRows[rIdx] = {
        ...updatedRows[rIdx],
        [col]: { ...existingObj, borderTop: bTop, borderBottom: bBottom, borderLeft: bLeft, borderRight: bRight },
      };
      return { ...t, rows: updatedRows };
    }));
    onClose();
  };
 
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`w-full max-w-sm rounded-[2rem] border shadow-2xl p-8 ${isDark ? "bg-[#0a1628] border-[#1e3a5f] text-slate-100" : "bg-white border-slate-200 text-slate-900"}`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-black text-lg uppercase">Cell Border</h3>
          <button onClick={onClose} className="text-xl hover:opacity-60">✕</button>
        </div>
 
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div>
            <label className={`text-[9px] font-black uppercase tracking-widest block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Color</label>
            <input type="color" value={bColor} onChange={e => setBColor(e.target.value)} className="w-full h-9 rounded-xl border-0 cursor-pointer" />
          </div>
          <div>
            <label className={`text-[9px] font-black uppercase tracking-widest block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Width</label>
            <select value={bWidth} onChange={e => setBWidth(e.target.value)}
              className={`w-full h-9 rounded-xl px-2 text-xs font-black border outline-none ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-200" : "bg-slate-50 border-slate-200"}`}>
              <option value="1">1px</option>
              <option value="2">2px</option>
              <option value="3">3px</option>
            </select>
          </div>
          <div>
            <label className={`text-[9px] font-black uppercase tracking-widest block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Style</label>
            <select value={bStyle} onChange={e => setBStyle(e.target.value)}
              className={`w-full h-9 rounded-xl px-2 text-xs font-black border outline-none ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-200" : "bg-slate-50 border-slate-200"}`}>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
              <option value="double">Double</option>
            </select>
          </div>
        </div>
 
        {/* Preview */}
        <div className="flex items-center justify-center mb-5">
          <div className="w-24 h-24 flex items-center justify-center text-[10px] font-black text-slate-400"
            style={{
              borderTop:    bTop    || "1px dashed #334155",
              borderBottom: bBottom || "1px dashed #334155",
              borderLeft:   bLeft   || "1px dashed #334155",
              borderRight:  bRight  || "1px dashed #334155",
            }}>
            Preview
          </div>
        </div>
 
        {/* Side buttons */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[
            { label: "Top",    val: bTop,    set: setBTop },
            { label: "Bottom", val: bBottom, set: setBBottom },
            { label: "Left",   val: bLeft,   set: setBLeft },
            { label: "Right",  val: bRight,  set: setBRight },
          ].map(({ label, val, set }) => (
            <button key={label} onClick={() => set(val ? "" : makeBorder())}
              className={`py-2 rounded-xl font-black text-[10px] uppercase transition-all border ${
                val
                  ? isDark ? "bg-blue-600/20 border-blue-500/40 text-blue-400" : "bg-blue-50 border-blue-300 text-blue-600"
                  : isDark ? "border-[#1e3a5f] text-slate-500 hover:text-slate-300" : "border-slate-200 text-slate-400 hover:text-slate-600"
              }`}>
              {label} {val ? "On" : "Off"}
            </button>
          ))}
        </div>
 
        {/* Presets */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => { const b = makeBorder(); setBTop(b); setBBottom(b); setBLeft(b); setBRight(b); }}
            className={`flex-1 py-2 rounded-xl font-black text-[10px] uppercase transition-all ${isDark ? "bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white" : "bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white"}`}>
            All Sides
          </button>
          <button onClick={() => { setBTop(""); setBBottom(""); setBLeft(""); setBRight(""); }}
            className={`flex-1 py-2 rounded-xl font-black text-[10px] uppercase transition-all ${isDark ? "bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white" : "bg-red-50 text-red-500 hover:bg-red-500 hover:text-white"}`}>
            Clear
          </button>
        </div>
 
        <div className="flex gap-3">
          <button onClick={onClose}
            className={`flex-1 py-3 rounded-2xl font-black text-xs uppercase border transition-all ${isDark ? "border-[#1e3a5f] hover:bg-white/5 text-slate-400" : "border-slate-200 hover:bg-slate-50 text-slate-500"}`}>
            Cancel
          </button>
          <button onClick={handleApply}
            className="flex-1 py-3 rounded-2xl font-black text-xs uppercase bg-blue-600 hover:bg-blue-500 text-white transition-all">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
 
function ConditionalModal({ columns, conditionalRules, onApply, onClose, isDark }) {
  const [rules, setRules] = useState(conditionalRules);
 
  const addRule = () => setRules(prev => [...prev, { col: "all", type: "gt", value: "", color: "#ef4444", bgColor: "" }]);
  const removeRule = (i) => setRules(prev => prev.filter((_, idx) => idx !== i));
  const updateRule = (i, key, val) => setRules(prev => prev.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
 
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`w-full max-w-lg rounded-[2rem] border shadow-2xl overflow-hidden ${isDark ? "bg-[#0a1628] border-[#1e3a5f] text-slate-100" : "bg-white border-slate-200 text-slate-900"}`}
        style={{ maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
 
        <div className={`px-7 pt-7 pb-5 border-b ${isDark ? "border-[#1e3a5f]" : "border-slate-200"} flex-shrink-0`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-black text-lg uppercase">Conditional Formatting</h3>
              <p className={`text-[10px] mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Rules apply automatically to matching cells</p>
            </div>
            <button onClick={onClose} className="text-xl hover:opacity-60">✕</button>
          </div>
        </div>
 
        <div className="overflow-y-auto flex-1 p-6 space-y-3">
          {rules.length === 0 && (
            <div className="text-center py-8">
              <p className={`text-sm font-black uppercase ${isDark ? "text-slate-500" : "text-slate-400"}`}>No rules yet</p>
              <p className={`text-[10px] mt-2 ${isDark ? "text-slate-600" : "text-slate-400"}`}>Click Add Rule to get started</p>
            </div>
          )}
          {rules.map((rule, i) => (
            <div key={i} className={`p-4 rounded-2xl border ${isDark ? "bg-[#070f1e] border-[#1e3a5f]" : "bg-slate-50 border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <select value={rule.col} onChange={e => updateRule(i, "col", e.target.value)}
                  className={`px-3 py-2 rounded-xl text-[11px] font-black border outline-none ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-200" : "bg-white border-slate-200"}`}>
                  <option value="all">All Columns</option>
                  {columns.filter(c => c !== "ID").map(c => <option key={c} value={c}>{c}</option>)}
                </select>
 
                <select value={rule.type} onChange={e => updateRule(i, "type", e.target.value)}
                  className={`px-3 py-2 rounded-xl text-[11px] font-black border outline-none ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-200" : "bg-white border-slate-200"}`}>
                  <option value="gt">Greater than</option>
                  <option value="lt">Less than</option>
                  <option value="eq">Equal to</option>
                  <option value="contains">Contains</option>
                  <option value="empty">Is Empty</option>
                </select>
 
                {rule.type !== "empty" && (
                  <input type="text" value={rule.value} onChange={e => updateRule(i, "value", e.target.value)}
                    placeholder="Value..."
                    className={`px-3 py-2 rounded-xl text-[11px] border outline-none w-24 ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-200 placeholder:text-slate-600" : "bg-white border-slate-200"}`} />
                )}
 
                <button onClick={() => removeRule(i)} className="text-red-400 hover:text-red-300 text-lg ml-auto">✕</button>
              </div>
 
              <div className="flex items-center gap-3 flex-wrap">
                <label className={`text-[9px] font-black uppercase tracking-widest ${isDark ? "text-slate-400" : "text-slate-500"}`}>Text</label>
                <input type="color" value={rule.color || "#ef4444"} onChange={e => updateRule(i, "color", e.target.value)} className="w-8 h-8 rounded-lg border-0 cursor-pointer" />
                <label className={`text-[9px] font-black uppercase tracking-widest ${isDark ? "text-slate-400" : "text-slate-500"}`}>Background</label>
                <input type="color" value={rule.bgColor || "#ffffff"} onChange={e => updateRule(i, "bgColor", e.target.value)} className="w-8 h-8 rounded-lg border-0 cursor-pointer" />
                {[
                  { label: "Red",    color: "#ef4444", bg: "#fef2f2" },
                  { label: "Green",  color: "#10b981", bg: "#f0fdf4" },
                  { label: "Blue",   color: "#3b82f6", bg: "#eff6ff" },
                  { label: "Yellow", color: "#f59e0b", bg: "#fffbeb" },
                ].map(preset => (
                  <button key={preset.label}
                    onClick={() => { updateRule(i, "color", preset.color); updateRule(i, "bgColor", preset.bg); }}
                    className="w-5 h-5 rounded-full border-2 border-white/20 hover:scale-125 transition-transform"
                    style={{ backgroundColor: preset.color }}
                    title={preset.label} />
                ))}
              </div>
            </div>
          ))}
        </div>
 
        <div className={`px-6 pb-6 pt-4 border-t ${isDark ? "border-[#1e3a5f]" : "border-slate-200"} flex-shrink-0`}>
          <button onClick={addRule}
            className={`w-full py-3 rounded-2xl font-black text-[11px] uppercase border transition-all mb-3 ${isDark ? "border-[#1e3a5f] text-blue-400 hover:bg-blue-500/10" : "border-blue-200 text-blue-600 hover:bg-blue-50"}`}>
            + Add Rule
          </button>
          <div className="flex gap-3">
            <button onClick={() => { onApply([]); onClose(); }}
              className={`flex-1 py-3 rounded-2xl font-black text-xs uppercase border transition-all ${isDark ? "border-[#1e3a5f] text-slate-400 hover:bg-white/5" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              Clear All
            </button>
            <button onClick={() => { onApply(rules); onClose(); }}
              className="flex-1 py-3 rounded-2xl font-black text-xs uppercase bg-blue-600 hover:bg-blue-500 text-white transition-all">
              Apply Rules
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
// ============================================================
// MAIN APP
// ============================================================
function App() {
  const [genText,            setGenText]            = useState("");
  const [inputText,          setInputText]          = useState("");
  const [listeningField,     setListeningField]     = useState(null); // "gen" | "calc" | null
  const [voiceLang,          setVoiceLang]          = useState("bn-BD"); // "bn-BD" | "en-US"
  const [highlightEmpty,     setHighlightEmpty]      = useState(false);
  const [duplicateCol,       setDuplicateCol]        = useState(null);
  const [validateCol,        setValidateCol]         = useState(null);
  const [searchTerm,         setSearchTerm]         = useState("");
  const [user,               setUser]               = useState(null);
  const [page,               setPage]               = useState("showcase");
  const [activeBlogSlug,     setActiveBlogSlug]      = useState(null);
  const [isDark,             setIsDark]             = useState(false);
  const [showAdmin,          setShowAdmin]          = useState(false);
  const [showAnalytics,      setShowAnalytics]      = useState(false);
  const [showTopPanel,       setShowTopPanel]       = useState(true);
  const [showUpgrade,        setShowUpgrade]        = useState(false);
  const [showSupportChat,    setShowSupportChat]    = useState(false);
  const [showAIAnalysis,     setShowAIAnalysis]     = useState(false);
  const [showTemplates,      setShowTemplates]      = useState(false);
  const [isPremium,          setIsPremium]          = useState(false);
  const [isOnTrial,          setIsOnTrial]          = useState(false);
  const [isAdmin,            setIsAdmin]            = useState(false);
  const [syncStatus,         setSyncStatus]         = useState("idle");
  const [lastSaved,          setLastSaved]          = useState(null);
  const [isLoading,          setIsLoading]          = useState(false);
  const [calculationResult,  setCalculationResult]  = useState(null);
  const [selectedRows,       setSelectedRows]       = useState(new Set());
  const [dragRowIdx,         setDragRowIdx]         = useState(null);
  const [dragOverRowIdx,     setDragOverRowIdx]     = useState(null);
  const [dragColIdx,         setDragColIdx]         = useState(null);
  const [dragOverColIdx,     setDragOverColIdx]     = useState(null);
  const [menuConfig,         setMenuConfig]         = useState({ show: false, x: 0, y: 0, col: null });
  const [editingColType,     setEditingColType]     = useState(null);
  const [editingFormat,      setEditingFormat]      = useState(null);
  const [history,            setHistory]            = useState([]);
  const [currentTableId,     setCurrentTableId]     = useState(null);
  const [currentFilePath, setCurrentFilePath] = useState(null);
  const [tabs,               setTabs]               = useState([makeNewTab("Sheet 1", ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"], 20)]);
  const [activeTabId,        setActiveTabId]        = useState(null);
  const [showAITable, setShowAITable] = useState(false);
  const [hideEmptyRows, setHideEmptyRows] = useState(false);
  const [aiTableUsage, setAiTableUsage] = useState(0);
  const [aiBuildLoading, setAiBuildLoading] = useState(false);
  const [lifetimeTablesCreated, setLifetimeTablesCreated] = useState(1);

  // ── NEW: track table creation timestamps for 24h cooldown ──
  // Shape: [{ id, createdAt: timestamp_ms }, ...]
  const [tableCreationLog,   setTableCreationLog]   = useState([]);
  const [showProGate,    setShowProGate]    = useState(false);
  const [showWelcome,    setShowWelcome]    = useState(false);
  const [proGateMessage, setProGateMessage] = useState("");

  const [colWidths,      setColWidths]      = useState({});
  const [rowHeights,     setRowHeights]     = useState({});
  const [copiedCell,     setCopiedCell]     = useState(null);
  const [freezeCol,      setFreezeCol]      = useState(true);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const showToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }, []);
  const [toasts, setToasts] = useState([]);
  const [commentCell,  setCommentCell]  = useState(null);
  const [commentText,  setCommentText]  = useState("");
  const [hoverComment, setHoverComment] = useState(null);
  const [autoSuggest,  setAutoSuggest]  = useState({ show: false, rIdx: -1, col: "", items: [] });
  const [showConditional, setShowConditional] = useState(false);
  const [conditionalRules, setConditionalRules] = useState([]);
  const [showBorderModal, setShowBorderModal] = useState(false);
  const [undoStack,      setUndoStack]      = useState([]);
  const [redoStack,      setRedoStack]      = useState([]);
  const [frozenCols,     setFrozenCols]     = useState(1);  // কতটা column freeze
  const [shareModalOpen,   setShareModalOpen]   = useState(false);
  const [isSharingTab,     setIsSharingTab]      = useState(false);
  const [shareLink,        setShareLink]        = useState("");
  const [shareId,          setShareId]          = useState(null);
  const [collaborators,    setCollaborators]    = useState({});

  // ====== Sidebar Chat & Sticky Notes (Real-time) ======
  const [chatMessages,     setChatMessages]     = useState([]);
  const [chatInput,        setChatInput]        = useState("");
  const [showChatSidebar,  setShowChatSidebar]  = useState(false);
  const [stickyNotes,      setStickyNotes]      = useState({});
  const [activeStickyNote, setActiveStickyNote] = useState(null); // { rIdx, col }
  const [stickyNoteText,   setStickyNoteText]   = useState("");
  const [typingUsers,      setTypingUsers]      = useState({});
  const typingTimeoutRef   = useRef(null);
  const [lastReadChatTime, setLastReadChatTime]  = useState(0);
  const chatBottomRef      = useRef(null);
  const [showNotesList,    setShowNotesList]     = useState(false);
  const [chatBoxPos,       setChatBoxPos]        = useState(null); // {x, y} — null মানে ডিফল্ট পজিশন
  const [reactionPickerFor, setReactionPickerFor] = useState(null); // msgId যার picker খোলা আছে
  const [chatMuted,         setChatMuted]         = useState(false);
  const [showSupport,       setShowSupport]        = useState(false);
  const [showHelpBot,       setShowHelpBot]        = useState(false);
  const [promptModal,       setPromptModal]         = useState(null); // {title, defaultValue, onConfirm}
  const [promptValue,       setPromptValue]         = useState("");

  // ====== prompt()-এর বদলে ব্যবহারযোগ্য কাস্টম প্রম্পট ======
  const showPrompt = useCallback((title, defaultValue, onConfirm) => {
    setPromptValue(defaultValue || "");
    setPromptModal({ title, onConfirm });
  }, []);
  const [helpMessages,      setHelpMessages]       = useState([]); // {from: "user"|"bot", text}
  const [helpInput,         setHelpInput]          = useState("");
  const helpBottomRef       = useRef(null);
  const [supportName,       setSupportName]        = useState("");
  const [supportEmail,      setSupportEmail]       = useState("");
  const [supportMsg,        setSupportMsg]         = useState("");
  const [supportSending,    setSupportSending]     = useState(false);
  const [supportSent,       setSupportSent]        = useState(false);
  const prevChatCountRef    = useRef(0);

  // ====== নতুন মেসেজ এলে "ding" শব্দ বাজাও (সাইডবার বন্ধ থাকলে) ======
  const playChatDing = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(); osc.stop(ctx.currentTime + 0.3);
    } catch (err) { /* audio not supported, ignore */ }
  }, []);
  const dragStateRef       = useRef(null);

  // ====== নতুন মেসেজ আসলে অটো স্ক্রল নিচে ======
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ block: "end" });
    }
  }, [chatMessages, showChatSidebar]);

  // ====== নতুন মেসেজে "ding" শব্দ (সাইডবার বন্ধ + mute না থাকলে + অন্যের মেসেজ হলে) ======
  useEffect(() => {
    const count = chatMessages.length;
    if (count > prevChatCountRef.current && prevChatCountRef.current !== 0) {
      const latestMsg = chatMessages[count - 1];
      if (latestMsg && latestMsg.uid !== user?.uid && !showChatSidebar && !chatMuted) {
        playChatDing();
      }
    }
    prevChatCountRef.current = count;
  }, [chatMessages, showChatSidebar, chatMuted, user, playChatDing]);
  const [isSharedView,     setIsSharedView]     = useState(false);
  const [sharedTabData,    setSharedTabData]    = useState(null);
  const [sharePermissions, setSharePermissions] = useState({});
  const [shareEmailInput,  setShareEmailInput]  = useState("");
  const [shareRoleInput,   setShareRoleInput]   = useState("editor");
  const [myShareRole,      setMyShareRole]      = useState("owner");
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [hiddenRows,     setHiddenRows]     = useState(new Set());
  const [hiddenCols,     setHiddenCols]     = useState(new Set());
  const [formulaBarVal,  setFormulaBarVal]  = useState("");
  const showFormulaBar = true;
  const [findText,       setFindText]       = useState("");
  const [replaceText,    setReplaceText]    = useState("");
  const [findResults,    setFindResults]    = useState([]);
  const [findIndex,      setFindIndex]      = useState(0);
  const resizingCol      = useRef(null);
  const resizingRow      = useRef(null);
  const startX           = useRef(0);
  const startY           = useRef(0);
  const startWidth       = useRef(0);
  const startHeight      = useRef(0);

  const menuRef        = useRef(null);
  const tableRef       = useRef(null);
  const [gridScrollTop, setGridScrollTop] = useState(0);
  const fileInputRef   = useRef(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragCounterRef  = useRef(0);
  const autoSaveTimer  = useRef(null);
  const firestoreUnsubRef = useRef(null);
  const sharedUnsubRef  = useRef(null);
  const recognitionRef = useRef(null);
  const isInitialLoad  = useRef(true);
  const userRef        = useRef(null);

  useEffect(() => {
    if (!activeTabId && tabs.length > 0) setActiveTabId(tabs[0].id);
  }, [activeTabId, tabs]);

  const activeTab = useMemo(
    () => tabs.find(t => t.id === activeTabId) || tabs[0],
    [tabs, activeTabId]
  );
  const columns      = useMemo(() => activeTab?.columns || [], [activeTab]);
  const rows         = useMemo(() => activeTab?.rows || [], [activeTab]);
  const tableTitle   = activeTab?.title        || "";
  const columnTypes  = useMemo(() => activeTab?.columnTypes || {}, [activeTab]);
  const columnFormats = useMemo(() => activeTab?.columnFormats || {}, [activeTab]);

  const updateActiveTab = useCallback((updater) => {
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      return typeof updater === "function" ? updater(t) : { ...t, ...updater };
    }));
  }, [activeTabId]);

  const setColumns    = useCallback((cols) => updateActiveTab({ columns: cols }),  [updateActiveTab]);
  const setTableTitle = useCallback((title) => updateActiveTab({ title }),          [updateActiveTab]);

  const handleCellChange = useCallback((rIdx, col, value) => {
    if (myShareRole === "viewer") return;
    setUndoStack(prev => {
      const snapshot = JSON.parse(JSON.stringify(rows));
      return [...prev, snapshot].slice(-30);
    });
    setRedoStack([]);
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      const updatedRows = [...t.rows];
      const existingCell = updatedRows[rIdx]?.[col] || {};
      updatedRows[rIdx] = {
        ...updatedRows[rIdx],
        [col]: { ...(typeof existingCell === "object" ? existingCell : {}), value: String(value ?? "") },
      };
      return { ...t, rows: updatedRows };
    }));

    // ====== টাইপ করার সাথে সাথে row height smoothly বাড়ানো (হালকা, শুধু গণনা — কোনো DOM measurement না) ======
    const text = String(value ?? "");
    const estLines = Math.max(1, text.split("\n").length, Math.ceil(text.length / 30));
    const neededHeight = Math.max(24, Math.min(estLines * 24, 200));
    setRowHeights(prev => (neededHeight > (prev[rIdx] || 24) ? { ...prev, [rIdx]: neededHeight } : prev));
  }, [activeTabId, myShareRole, rows]);

  const applyCellFormat = useCallback((rIdx, col, fmt) => {
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      const updatedRows = [...t.rows];
      const existingCell = updatedRows[rIdx]?.[col] || {};
      updatedRows[rIdx] = {
        ...updatedRows[rIdx],
        [col]: {
          value:      typeof existingCell === "object" ? (existingCell.value ?? "") : String(existingCell),
          color:      fmt.color      || "",
          bgColor:    fmt.bgColor    || "",
          fontSize:   fmt.fontSize   || "",
          fontWeight: fmt.fontWeight || "normal",
        },
      };
      return { ...t, rows: updatedRows };
    }));
  }, [activeTabId]);

  const applyColumnFormat = useCallback((col, fmt) => {
    updateActiveTab(t => ({ ...t, columnFormats: { ...t.columnFormats, [col]: fmt } }));
  }, [updateActiveTab]);

  const setColumnTypes = useCallback((updater) => {
    updateActiveTab(t => ({
      ...t,
      columnTypes: typeof updater === "function" ? updater(t.columnTypes || {}) : updater,
    }));
  }, [updateActiveTab]);

  const setRows = useCallback((rws) => updateActiveTab({ rows: rws }), [updateActiveTab]);
  // ============================================================
  // FREE PLAN LIFETIME LIMIT
  // ============================================================
  const canCreateTable = useCallback(() => {
  if (isPremium) return { allowed: true, reason: "" };

  if (lifetimeTablesCreated >= FREE_TABLE_LIMIT) {
    return {
      allowed: false,
      reason: `Free plan allows ${FREE_TABLE_LIMIT} lifetime tables per Gmail. You already used all free tables. Upgrade to Pro for unlimited tables.`,
    };
  }

  if (tabs.length >= FREE_TABLE_LIMIT) {
    return {
      allowed: false,
      reason: `Free plan allows max ${FREE_TABLE_LIMIT} open sheets. Delete one to continue, but lifetime table count will not reset.`,
    };
  }

  return { allowed: true, reason: "" };
}, [isPremium, lifetimeTablesCreated, tabs.length]);

  const getFreshLifetimeTableCount = useCallback(async () => {
  if (!user?.uid || !db) return lifetimeTablesCreated;

  try {
    const snap = await getDoc(doc(db, "users", user.uid));
    const count = snap.exists() ? snap.data().lifetimeTablesCreated : null;
    return typeof count === "number" ? count : lifetimeTablesCreated;
  } catch (err) {
    console.error("Lifetime table count check failed:", err);
    return lifetimeTablesCreated;
  }
}, [user?.uid, lifetimeTablesCreated]);

  // ============================================================
  // AUTO-SAVE
  // ============================================================
  const saveToFirestore = useCallback(async (tabsData, histData, creationLog) => {
    const currentUser = userRef.current;
    try {
      localStorage.setItem("ss_tabs_v14",         JSON.stringify(tabsData));
      localStorage.setItem("ss_history_v14",      JSON.stringify(histData));
      localStorage.setItem("ss_creation_log_v14", JSON.stringify(creationLog));
      localStorage.setItem("ss_ai_usage_v15", JSON.stringify(aiTableUsage));
    } catch (e) {}
    if (currentUser && db) {
      try {
        await setDoc(doc(db, "spreadsheets", currentUser.uid), {
          tabs:            tabsData,
          history:         histData,
          tableCreationLog: creationLog,
          tableCount:      tabsData.length,
          updatedAt:       serverTimestamp(),
          uid:             currentUser.uid,
          aiTableUsage,
        }, { merge: true });
        await setDoc(doc(db, "users", currentUser.uid), { tableCount: tabsData.length }, { merge: true });
        setLastSaved(new Date().toLocaleTimeString());
        setSyncStatus("saved");
      } catch (err) { console.error("Firestore save error:", err); setSyncStatus("local"); }
    } else { setSyncStatus("local"); }
  }, [aiTableUsage]);

  useEffect(() => {
    if (isInitialLoad.current) return;
    setSyncStatus("syncing");
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveToFirestore(tabs, history, tableCreationLog);
    }, AUTOSAVE_DELAY);
    
  }, [tabs, history, tableCreationLog, aiTableUsage, saveToFirestore]);

  // ============================================================
  // LOAD DATA
  // ============================================================
  const loadUserDataFromFirestore = useCallback((uid) => {
    if (firestoreUnsubRef.current) firestoreUnsubRef.current();
    if (!db) { loadFromLocalStorage(); return; }
    const userDocRef = doc(db, "spreadsheets", uid);
    const unsub = onSnapshot(userDocRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (typeof data.aiTableUsage === "number") setAiTableUsage(data.aiTableUsage);
        isInitialLoad.current = true;
        if (data.tabs && Array.isArray(data.tabs) && data.tabs.length > 0) {
          const migratedTabs = data.tabs.map(tab => ({
            ...tab,
            columnTypes:   tab.columnTypes   || {},
            columnFormats: tab.columnFormats || {},
            tabColor:      tab.tabColor      || "",
            rows: (tab.rows || []).map(row => migrateRow(row, tab.columns || [])),
          }));
          setTabs(migratedTabs);
          setActiveTabId(prevActiveId => {
            const stillExists = migratedTabs.some(tab => tab.id === prevActiveId);
            return stillExists ? prevActiveId : migratedTabs[0].id;
          });
        }
        if (data.history)          setHistory(data.history);
        if (data.tableCreationLog) setTableCreationLog(data.tableCreationLog);
        if (data.isPremium)        setIsPremium(true);
        setLastSaved(data.updatedAt ? new Date(data.updatedAt.seconds * 1000).toLocaleTimeString() : null);
        setSyncStatus("saved");
        setTimeout(() => { isInitialLoad.current = false; }, 600);
      } else {
        isInitialLoad.current = false;
        loadFromLocalStorage();
      }
    }, (err) => {
      console.error("Firestore sync error:", err);
      setSyncStatus("local");
      isInitialLoad.current = false;
      loadFromLocalStorage();
    });
    firestoreUnsubRef.current = unsub;
  }, []);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedTabs        = localStorage.getItem("ss_tabs_v14");
      const savedHistory     = localStorage.getItem("ss_history_v14");
      const savedCreationLog = localStorage.getItem("ss_creation_log_v14");
      if (savedTabs) {
        const parsed = JSON.parse(savedTabs);
        const migratedTabs = parsed.map(tab => ({
          ...tab,
          columnTypes:   tab.columnTypes   || {},
          columnFormats: tab.columnFormats || {},
          tabColor:      tab.tabColor      || "",
          rows: (tab.rows || []).map(row => migrateRow(row, tab.columns || [])),
        }));
        setTabs(migratedTabs);
        if (migratedTabs.length > 0) setActiveTabId(migratedTabs[0].id);
      }
      if (savedHistory)     setHistory(JSON.parse(savedHistory));
      if (savedCreationLog) setTableCreationLog(JSON.parse(savedCreationLog));
      const savedAiUsage = localStorage.getItem("ss_ai_usage_v15");
      if (savedAiUsage) setAiTableUsage(JSON.parse(savedAiUsage));
    } catch (e) { console.error("LocalStorage load error:", e); }
    finally { isInitialLoad.current = false; }
  }, []);

  const checkUserRole = useCallback(async (uid, email) => {
    if (email === ADMIN_EMAIL) { setIsAdmin(true); setIsPremium(true); return; }
    if (db) {
      try {
        const userDoc = await getDoc(doc(db, "users", uid));
        if (!userDoc.exists()) {
          // নতুন user — welcome modal দেখাও + ১৪ দিনের ফ্রি ট্রায়াল দাও
          setShowWelcome(true);
          setLifetimeTablesCreated(1);
          const trialExpiry = new Date();
          trialExpiry.setDate(trialExpiry.getDate() + TRIAL_DAYS);
          try {
            await setDoc(doc(db, "users", uid), {
              isPremium: true, plan: "trial",
              premiumPlan: "trial", premiumExpiry: trialExpiry,
              trialStartedAt: serverTimestamp(),
            }, { merge: true });
            setIsPremium(true);
            setIsOnTrial(true);
          } catch (err) { console.error("Trial grant error:", err); }
        }
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (typeof data.lifetimeTablesCreated === "number") {
            setLifetimeTablesCreated(data.lifetimeTablesCreated);
          } else {
            setLifetimeTablesCreated(1);
          }
          if (data.isPremium) {
            if (data.premiumExpiry) {
              if (new Date(data.premiumExpiry.seconds * 1000) > new Date()) {
                setIsPremium(true);
                if (data.plan === "trial") setIsOnTrial(true);
              }
            } else setIsPremium(true);
          }
        }
      } catch (err) { console.error("Role check error:", err); }
    }
  }, []);

  useEffect(() => {
    if (!auth) { loadFromLocalStorage(); return; }
    const unsub = onAuthStateChanged(auth, async (u) => {
      userRef.current = u;
      if (u) {
        setUser(u); setPage("app");
        await checkUserRole(u.uid, u.email);
        try {
          await setDoc(doc(db, "users", u.uid), 
        {
          name: u.displayName,
          email: u.email,
          photoURL: u.photoURL,
          lastLogin: serverTimestamp(),
          uid: u.uid,
          lifetimeTableLimit: FREE_TABLE_LIMIT,
        }, { merge: true });
        } catch (err) {}
        loadUserDataFromFirestore(u.uid);
      } else {
        loadFromLocalStorage();
      }
    });
    return () => unsub();
  }, [checkUserRole, loadUserDataFromFirestore]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuConfig(p => ({ ...p, show: false }));
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (firestoreUnsubRef.current) firestoreUnsubRef.current();
      if (sharedUnsubRef.current)    sharedUnsubRef.current();
      if (recognitionRef.current)    recognitionRef.current.stop();
      if (autoSaveTimer.current)     clearTimeout(autoSaveTimer.current);
    };
  }, []);

  // ====== নতুন ইউজারকে Welcome Email পাঠাও (শুধু প্রথমবার সাইনআপে) ======
  const sendWelcomeEmail = useCallback((name, email) => {
    if (!email) return;
    emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
      to_name: name || "there",
      to_email: email,
    }, EMAILJS_PUBLIC_KEY)
      .then(() => console.log("Welcome email sent."))
      .catch((err) => console.error("Welcome email error:", err));
  }, []);

  // ====== Help Bot-এ প্রশ্ন পাঠাও ======
  const askHelpBot = useCallback(() => {
    const q = helpInput.trim();
    if (!q) return;
    const isBn = /[\u0980-\u09FF]/.test(q);
    const answer = matchHelpQuestion(q) ||
      (isBn
        ? "দুঃখিত, এটা এখনো বুঝতে পারছি না। Support বাটন দিয়ে সরাসরি আমাদের জিজ্ঞেস করতে পারো।"
        : "Sorry, I don't know that one yet. You can ask our team directly via the Support button.");
    setHelpMessages(prev => [...prev, { from: "user", text: q }, { from: "bot", text: answer }]);
    setHelpInput("");
  }, [helpInput]);

  // ====== Help Bot-এ অটো স্ক্রল ======
  useEffect(() => {
    if (helpBottomRef.current) helpBottomRef.current.scrollIntoView({ block: "end" });
  }, [helpMessages, showHelpBot]);

  // ====== Support ফর্ম থেকে মেসেজ পাঠাও ======
  const sendSupportMessage = useCallback(async () => {
    if (!supportMsg.trim() || !supportEmail.trim()) return;
    setSupportSending(true);
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_SUPPORT_TEMPLATE_ID, {
        from_name: supportName.trim() || user?.displayName || "Anonymous",
        reply_email: supportEmail.trim(),
        message: supportMsg.trim(),
      }, EMAILJS_PUBLIC_KEY);
      setSupportSent(true);
      setSupportMsg("");
      setTimeout(() => { setShowSupport(false); setSupportSent(false); }, 2000);
    } catch (err) {
      console.error("Support message error:", err);
      showToast("Failed to send message. Please try again.", "error");
    } finally {
      setSupportSending(false);
    }
  }, [supportName, supportEmail, supportMsg, user]);

  const handleGoogleLogin = async () => {
    if (!auth) { showToast("Firebase not initialized.", "error"); return; }
    try {
      const result = await signInWithPopup(auth, provider);
      const u = result.user;
      const isNewUser = result?._tokenResponse?.isNewUser || false;
      userRef.current = u; setUser(u); setPage("app");
      await checkUserRole(u.uid, u.email);
      try {
        await setDoc(doc(db, "users", u.uid), {
          name: u.displayName, email: u.email, photoURL: u.photoURL,
          lastLogin: serverTimestamp(), uid: u.uid,
        }, { merge: true });
      } catch (err) {}
      if (isNewUser) sendWelcomeEmail(u.displayName, u.email);
      loadUserDataFromFirestore(u.uid);
    } catch (err) { console.error("Login error:", err); }
  };

  const handleLogout = () => {
    if (firestoreUnsubRef.current) firestoreUnsubRef.current();
    userRef.current = null;
    if (auth) signOut(auth).then(() => {
      setUser(null); setPage("landing");
      setIsAdmin(false); setIsPremium(false);
    });
  };

  // ============================================================
  // CALCULATION ENGINE
  // ============================================================
  useEffect(() => {
    const performCalculation = () => {
      if (!inputText.trim()) { setCalculationResult(null); return; }
      try {
        let finalValue = 0;
        const inputLower = inputText.toLowerCase();
        let targetRows = rows;
        if (inputLower.includes(" for ") || inputLower.includes(" of ")) {
          const parts = inputLower.split(/for|of/);
          const targets = parts[1].split(",").map(t => t.trim().toLowerCase());
          targetRows = rows.filter(row => {
            const nameMatch = targets.some(t => cellVal(row["Name"]).toLowerCase().includes(t));
            const idMatch   = targets.some(t => cellVal(row["ID"]) === t);
            return nameMatch || idMatch;
          });
        }

        // ===== AVERAGE / MAX / MIN / TOTAL (single column aggregate) =====
        const AGG_WORDS = {
          average: ["average", "gore", "গড়", "avg"],
          max:     ["max", "boro", "সর্বোচ্চ", "highest"],
          min:     ["min", "choto", "সর্বনিম্ন", "lowest"],
          sum:     ["total", "টোটাল", "সর্বমোট", "যোগফল"],
        };
        const aggMatchCol = columns.find(c => inputLower.includes(c.toLowerCase()));
        const aggType = Object.keys(AGG_WORDS).find(key => AGG_WORDS[key].some(w => inputLower.includes(w)));
        if (aggMatchCol && aggType) {
          const nums = targetRows.map(row => parseFloat(String(cellVal(row[aggMatchCol])).replace(/[^0-9.]/g, "")) || 0);
          let aggVal = 0;
          if (aggType === "average") aggVal = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
          else if (aggType === "max") aggVal = nums.length ? Math.max(...nums) : 0;
          else if (aggType === "min") aggVal = nums.length ? Math.min(...nums) : 0;
          else if (aggType === "sum") aggVal = nums.reduce((a, b) => a + b, 0);
          setCalculationResult({
            label:   aggType[0].toUpperCase() + aggType.slice(1),
            formula: `${aggType.toUpperCase()}(${aggMatchCol})`,
            value:   aggVal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
            count:   targetRows.length,
            columnsUsed: [],
            aggColumn: aggType === "sum" ? aggMatchCol : null,
            aggValueRaw: aggVal,
          });
          return;
        }

        const operators = [
          { symbol: "+", words: ["jog", "যোগ", "sum", "add", "plus"], fn: (a, b) => a + b, label: "Sum" },
          { symbol: "-", words: ["biyog", "বিয়োগ", "subtract", "minus"], fn: (a, b) => a - b, label: "Sub" },
          { symbol: "*", words: ["gun", "গুণ", "multiply", "times"], fn: (a, b) => a * b, label: "Mul" },
          { symbol: "/", words: ["bhag", "ভাগ", "divide"], fn: (a, b) => b !== 0 ? a / b : 0, label: "Div" },
          { symbol: "%", words: ["percent", "শতাংশ"], fn: (a, b) => (a * b) / 100, label: "Percentage" },
        ];
        let activeOp =
          operators.find(op => inputText.includes(op.symbol)) ||
          operators.find(op => op.words.some(w => inputLower.includes(w))) ||
          operators[0];
        // Bangla/word command hole column name-gula shorashori text theke khoje ber kori
        const twoColsInText = columns.filter(c => inputLower.includes(c.toLowerCase()));
        const calculationPart = twoColsInText.length >= 2
          ? twoColsInText.join(` ${activeOp.symbol} `).toLowerCase()
          : inputLower.split(/for|of/)[0];
        const parts = calculationPart.split(activeOp.symbol).map(p => p.trim());
        const columnsUsed = [];
        targetRows.forEach(row => {
          const vals = parts.map(p => {
            const colMatch = columns.find(c => c.toLowerCase() === p);
            if (colMatch) {
              if (!columnsUsed.includes(colMatch)) columnsUsed.push(colMatch);
              return parseFloat(String(cellVal(row[colMatch])).replace(/[^0-9.]/g, "")) || 0;
            }
            return parseFloat(p) || 0;
          });
          if (vals.length >= 2) finalValue += vals.reduce((acc, curr) => activeOp.fn(acc, curr));
          else if (vals.length === 1) finalValue += vals[0];
        });
        setCalculationResult({
          label:   activeOp.label,
          formula: calculationPart.toUpperCase(),
          value:   finalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
          count:   targetRows.length,
          symbol:  activeOp.symbol,
          fn:      activeOp.fn,
          columnsUsed,
        });
      } catch (err) { console.error("Calc error", err); }
    };
    const t = setTimeout(performCalculation, 300);
    return () => clearTimeout(t);
  }, [inputText, rows, columns]);

  // ============================================================
  // TAB CREATION — with creation log + cooldown enforcement
  // ============================================================
  const addNewTab = useCallback(async (templateData = null) => {
  const check = canCreateTable();

  if (!check.allowed) {
    setProGateMessage(check.reason);
    setShowProGate(true);
    return;
  }

  // Fresh Firestore check: user already used 4 lifetime free tables কিনা
  if (!isPremium) {
    const freshCount = await getFreshLifetimeTableCount();

    if (freshCount >= FREE_TABLE_LIMIT) {
      setLifetimeTablesCreated(freshCount);
      setProGateMessage(
        `Free plan allows ${FREE_TABLE_LIMIT} lifetime tables per Gmail. Upgrade to Pro for unlimited tables.`
      );
      setShowProGate(true);
      return;
    }
  }

  const newTab = templateData
    ? makeNewTab(templateData.name, templateData.columns, Math.max(templateData.sampleRows?.length || 0, 5))
    : makeNewTab(`Sheet ${tabs.length + 1}`, ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"], 20);

  if (templateData?.sampleRows?.length) {
    newTab.rows = templateData.sampleRows.map((rowArr) => {
      const obj = {};
      templateData.columns.forEach((col, ci) => {
        obj[col] = makeCellObj(rowArr[ci] !== undefined ? String(rowArr[ci]) : "");
      });
      return obj;
    });
  }

  const creationEntry = { id: newTab.id, createdAt: Date.now() };

  setTabs(prev => [...prev, newTab]);
  setTableCreationLog(prev => [...prev, creationEntry]);
  if (!isPremium) {
    setLifetimeTablesCreated(prev => prev + 1);

    if (user?.uid && db) {
      setDoc(doc(db, "users", user.uid), {
        lifetimeTablesCreated: increment(1),
        lifetimeTableLimit: FREE_TABLE_LIMIT,
        lastTableCreatedAt: serverTimestamp(),
      }, { merge: true }).catch(err => {
        console.error("Lifetime table count update failed:", err);
      });
    }
  }

  queueMicrotask(() => {
    setActiveTabId(newTab.id);
    setCurrentTableId(null);
    setSelectedRows(new Set());
    setSearchTerm("");
  });
}, [
  canCreateTable,
  getFreshLifetimeTableCount,
  isPremium,
  tabs.length,
  user?.uid,
]);
  const applyTemplateToCurrentTab = useCallback((template) => {
    const newRows = template.sampleRows.map((rowArr) => {
      const obj = {};
      template.columns.forEach((col, ci) => {
        obj[col] = makeCellObj(rowArr[ci] !== undefined ? String(rowArr[ci]) : "");
      });
      return obj;
    });
    updateActiveTab({
      columns: template.columns,
      rows: newRows,
      title: template.name,
      columnTypes: {},
      columnFormats: {},
    });
    setShowTemplates(false);
    setCurrentTableId(null);
  }, [updateActiveTab]);

  const closeTab = (e, id) => {
    e.stopPropagation();
    if (tabs.length === 1) return;
    setTabs(prev => {
      const newTabs = prev.filter(t => t.id !== id);
      if (activeTabId === id) setActiveTabId(newTabs[0].id);
      return newTabs;
    });
  };

  const switchTab = useCallback((id) => {
    setActiveTabId(id);
    setCurrentTableId(null);
    setSelectedRows(new Set());
    setSearchTerm("");
    setRowHeights({});
  }, []);

  // ── Column Resize ──
  const handleColResizeStart = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    resizingCol.current = col;
    startX.current = e.clientX;
    startWidth.current = colWidths[col] || 75;
 
    const onMove = (ev) => {
      const diff = ev.clientX - startX.current;
      const newWidth = Math.max(40, startWidth.current + diff);
      setColWidths(prev => ({ ...prev, [resizingCol.current]: newWidth }));
    };
    const onUp = () => {
      resizingCol.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [colWidths]);
 
  // ── Row Resize ──
  const handleRowResizeStart = useCallback((e, rIdx) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRow.current = rIdx;
    startY.current = e.clientY;
    startHeight.current = rowHeights[rIdx] || 24;
 
    const onMove = (ev) => {
      const diff = ev.clientY - startY.current;
      const newHeight = Math.max(32, startHeight.current + diff);
      setRowHeights(prev => ({ ...prev, [resizingRow.current]: newHeight }));
    };
    const onUp = () => {
      resizingRow.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [rowHeights]);
 
  const isCellInSelection = useCallback((rIdx, cIdx) => {
    if (!selectionStart || !selectionEnd) return false;
    const minR = Math.min(selectionStart.rIdx, selectionEnd.rIdx);
    const maxR = Math.max(selectionStart.rIdx, selectionEnd.rIdx);
    const minC = Math.min(selectionStart.cIdx, selectionEnd.cIdx);
    const maxC = Math.max(selectionStart.cIdx, selectionEnd.cIdx);
    return rIdx >= minR && rIdx <= maxR && cIdx >= minC && cIdx <= maxC;
  }, [selectionStart, selectionEnd]);

  // Undo snapshot নাও
  const takeSnapshot = useCallback(() => {
    setUndoStack(prev => {
      const snapshot = JSON.parse(JSON.stringify(rows));
      const newStack = [...prev, snapshot];
      return newStack.slice(-30); // max 30 undo steps
    });
    setRedoStack([]);
  }, [rows]);
 
  // Undo
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const snapshot = undoStack[undoStack.length - 1];
    setRedoStack(prev => [...prev, JSON.parse(JSON.stringify(rows))]);
    setUndoStack(prev => prev.slice(0, -1));
    setTabs(prev => prev.map(t => t.id !== activeTabId ? t : { ...t, rows: snapshot }));
  }, [undoStack, rows, activeTabId]);
 
  // Redo
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const snapshot = redoStack[redoStack.length - 1];
    setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(rows))]);
    setRedoStack(prev => prev.slice(0, -1));
    setRows(snapshot);
  }, [redoStack, rows, setRows]);
 
  // Row Height Auto
  const autoFitRowHeight = useCallback((rIdx) => {
    const row = rows[rIdx];
    if (!row) return;
    let maxLines = 1;
    columns.forEach(col => {
      const val = String(row[col]?.value || row[col] || "");
      const lines = val.split("\n").length;
      const charLines = Math.ceil(val.length / 30);
      maxLines = Math.max(maxLines, lines, charLines);
    });
    const newHeight = Math.max(24, Math.min(maxLines * 24, 200));
    setRowHeights(prev => ({ ...prev, [rIdx]: newHeight }));
  }, [rows, columns]);
 
  const autoFitAllRows = useCallback(() => {
    const newHeights = {};
    rows.forEach((row, rIdx) => {
      let maxLines = 1;
      columns.forEach(col => {
        const val = String(row[col]?.value || row[col] || "");
        const lines = val.split("\n").length;
        const charLines = Math.ceil(val.length / 30);
        maxLines = Math.max(maxLines, lines, charLines);
      });
      newHeights[rIdx] = Math.max(24, Math.min(maxLines * 24, 200));
    });
    setRowHeights(newHeights);
  }, [rows, columns]);
 
  // Find & Replace
  const handleFind = useCallback(() => {
    if (!findText) { setFindResults([]); return; }
    const results = [];
    rows.forEach((row, rIdx) => {
      columns.forEach(col => {
        const val = String(row[col]?.value || row[col] || "");
        if (val.toLowerCase().includes(findText.toLowerCase())) {
          results.push({ rIdx, col });
        }
      });
    });
    setFindResults(results);
    setFindIndex(0);
    if (results.length > 0) setSelectedCell(results[0]);
  }, [findText, rows, columns]);

  // ====== চ্যাট বক্স ড্র্যাগ করা ======
  const handleChatDragStart = useCallback((e) => {
    const box = e.currentTarget.closest("[data-chat-box]");
    if (!box) return;
    const rect = box.getBoundingClientRect();
    dragStateRef.current = {
      startX: e.clientX, startY: e.clientY,
      origLeft: rect.left, origTop: rect.top,
    };
    const onMouseMove = (moveEvt) => {
      if (!dragStateRef.current) return;
      const dx = moveEvt.clientX - dragStateRef.current.startX;
      const dy = moveEvt.clientY - dragStateRef.current.startY;
      setChatBoxPos({
        left: dragStateRef.current.origLeft + dx,
        top: dragStateRef.current.origTop + dy,
      });
    };
    const onMouseUp = () => {
      dragStateRef.current = null;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, []);

  // ====== চ্যাট মেসেজ ডিলিট করো ======
  const deleteChatMessage = useCallback(async (msgId) => {
    if (!shareId || !rtdb) return;
    try { await remove(ref(rtdb, `chat/${shareId}/${msgId}`)); } catch (err) { console.error("Chat delete error:", err); }
  }, [shareId, rtdb]);

  // ====== মেসেজে Emoji Reaction টগল করো ======
  const toggleMessageReaction = useCallback(async (msgId, emoji) => {
    if (!shareId || !rtdb || !user) return;
    const reactionRef = ref(rtdb, `chat/${shareId}/${msgId}/reactions/${emoji}/${user.uid}`);
    try {
      const msg = chatMessages.find(m => m.id === msgId);
      const alreadyReacted = msg?.reactions?.[emoji]?.[user.uid];
      if (alreadyReacted) {
        await remove(reactionRef);
      } else {
        await set(reactionRef, true);
      }
    } catch (err) { console.error("Reaction error:", err); }
  }, [shareId, rtdb, user, chatMessages]);

  // ====== Sticky Note-এ ক্লিক করে সেই সেলে জাম্প করা ======
  const jumpToStickyNote = useCallback((rIdx, col) => {
    setSelectedCell({ rIdx, col });
    setShowNotesList(false);
    setTimeout(() => {
      const el = document.querySelector(`[data-cell="r${rIdx}_${col}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }, 100);
  }, []);
 
  const handleReplaceOne = useCallback(() => {
    if (findResults.length === 0) return;
    const { rIdx, col } = findResults[findIndex];
    takeSnapshot();
    const val = String(rows[rIdx]?.[col]?.value || rows[rIdx]?.[col] || "");
    const newVal = val.replace(new RegExp(findText, "gi"), replaceText);
    handleCellChange(rIdx, col, newVal);
    const next = (findIndex + 1) % findResults.length;
    setFindIndex(next);
  }, [findResults, findIndex, findText, replaceText, rows, takeSnapshot, handleCellChange]);
 
  const handleReplaceAll = useCallback(() => {
    if (!findText) return;
    takeSnapshot();
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      const updatedRows = t.rows.map(row => {
        const newRow = { ...row };
        columns.forEach(col => {
          const val = String(row[col]?.value || row[col] || "");
          if (val.toLowerCase().includes(findText.toLowerCase())) {
            const newVal = val.replace(new RegExp(findText, "gi"), replaceText);
            const existing = row[col];
            const existingObj = typeof existing === "object" ? existing : { value: val };
            newRow[col] = { ...existingObj, value: newVal };
          }
        });
        return newRow;
      });
      return { ...t, rows: updatedRows };
    }));
    handleFind();
  }, [findText, replaceText, columns, activeTabId, takeSnapshot, handleFind]);

  const handleSaveComment = useCallback((rIdx, col, text) => {
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      const updatedRows = [...t.rows];
      const existing = updatedRows[rIdx]?.[col] || {};
      updatedRows[rIdx] = {
        ...updatedRows[rIdx],
        [col]: {
          ...(typeof existing === "object" ? existing : { value: existing }),
          comment: text || undefined,
        },
      };
      return { ...t, rows: updatedRows };
    }));
    setCommentCell(null);
    setCommentText("");
  }, [activeTabId]);
 
  // Multi-cell format apply করো
  const applyMultiCellFormat = useCallback((fmt) => {
    if (!selectionStart || !selectionEnd) {
      // single cell
      if (selectedCell) {
        const { rIdx, col } = selectedCell;
        setTabs(prev => prev.map(t => {
          if (t.id !== activeTabId) return t;
          const updatedRows = [...t.rows];
          const existing = updatedRows[rIdx]?.[col] || {};
          const existingObj = typeof existing === "object" ? existing : { value: String(existing) };
          updatedRows[rIdx] = {
            ...updatedRows[rIdx],
            [col]: { ...existingObj, ...fmt },
          };
          return { ...t, rows: updatedRows };
        }));
      }
      return;
    }
    const minR = Math.min(selectionStart.rIdx, selectionEnd.rIdx);
    const maxR = Math.max(selectionStart.rIdx, selectionEnd.rIdx);
    const minC = Math.min(selectionStart.cIdx, selectionEnd.cIdx);
    const maxC = Math.max(selectionStart.cIdx, selectionEnd.cIdx);
 
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      const updatedRows = [...t.rows];
      for (let r = minR; r <= maxR; r++) {
        for (let c = minC; c <= maxC; c++) {
          const col = columns[c];
          if (!col || col === "ID") continue;
          const existing = updatedRows[r]?.[col] || {};
          const existingObj = typeof existing === "object" ? existing : { value: String(existing) };
          updatedRows[r] = {
            ...updatedRows[r],
            [col]: { ...existingObj, ...fmt },
          };
        }
      }
      return { ...t, rows: updatedRows };
    }));
  }, [selectionStart, selectionEnd, selectedCell, activeTabId, columns]);
  const getConditionalStyle = useCallback((col, val) => {
    for (const rule of conditionalRules) {
      if (rule.col !== col && rule.col !== "all") continue;
      const num = parseFloat(val);
      let match = false;
      if (rule.type === "gt"       && !isNaN(num) && num >  parseFloat(rule.value)) match = true;
      if (rule.type === "lt"       && !isNaN(num) && num <  parseFloat(rule.value)) match = true;
      if (rule.type === "eq"       && val === rule.value)                            match = true;
      if (rule.type === "contains" && val.toLowerCase().includes(rule.value.toLowerCase())) match = true;
      if (rule.type === "empty"    && val === "")                                   match = true;
      if (match) return { color: rule.color || "", backgroundColor: rule.bgColor || "" };
    }
    return {};
  }, [conditionalRules]);

  // ============================================================
  // LIVE CURSORS — publish my selected cell to presence, and
  // read others' selected cells to render on the grid
  // ============================================================
  useEffect(() => {
    if (!shareId || !rtdb || !user) return;
    const timer = setTimeout(() => {
      const cellRef = ref(rtdb, `presence/${shareId}/${user.uid}/activeCell`);
      set(cellRef, selectedCell ? { rIdx: selectedCell.rIdx, col: selectedCell.col } : null).catch(() => {});
    }, 150);
    return () => clearTimeout(timer);
  }, [selectedCell, shareId, rtdb, user]);

  const getCellCollaborators = useCallback((rIdx, col) => {
    if (!collaborators || !user) return [];
    return Object.entries(collaborators)
      .filter(([uid, c]) => uid !== user.uid && c?.activeCell && c.activeCell.rIdx === rIdx && c.activeCell.col === col)
      .map(([uid, c]) => ({ uid, ...c }));
  }, [collaborators, user]);

  // ====== Chat Messages শোনো (real-time, শুধু সর্বশেষ ১০০টা) ======
  useEffect(() => {
    if (!shareId || !rtdb) return;
    const chatRef = query(ref(rtdb, `chat/${shareId}`), limitToLast(100));
    const unsub = onValue(chatRef, (snap) => {
      const data = snap.val() || {};
      const list = Object.entries(data)
        .map(([id, msg]) => ({ id, ...msg }))
        .sort((a, b) => (a.time || 0) - (b.time || 0));
      setChatMessages(list);
    });
    return () => unsub();
  }, [shareId, rtdb]);

  // ====== Sticky Notes শোনো (real-time) ======
  useEffect(() => {
    if (!shareId || !rtdb) return;
    const notesRef = ref(rtdb, `stickyNotes/${shareId}`);
    const unsub = onValue(notesRef, (snap) => {
      setStickyNotes(snap.val() || {});
    });
    return () => unsub();
  }, [shareId, rtdb]);

  // ====== Typing status শোনো (real-time) ======
  useEffect(() => {
    if (!shareId || !rtdb) return;
    const typingRef = ref(rtdb, `typing/${shareId}`);
    const unsub = onValue(typingRef, (snap) => {
      const data = snap.val() || {};
      const others = {};
      Object.entries(data).forEach(([uid, val]) => {
        if (uid !== user?.uid && val) others[uid] = val;
      });
      setTypingUsers(others);
    });
    return () => unsub();
  }, [shareId, rtdb, user]);

  // ====== Typing status সেট করো (debounce সহ) ======
  const handleTypingChange = useCallback((text) => {
    setChatInput(text);
    if (!shareId || !rtdb || !user) return;
    const myTypingRef = ref(rtdb, `typing/${shareId}/${user.uid}`);
    set(myTypingRef, user.displayName || "User");
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      set(myTypingRef, null);
    }, 2000);
  }, [shareId, rtdb, user]);

  // ====== চ্যাট মেসেজ পাঠাও ======
  const sendChatMessage = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || !shareId || !rtdb || !user) return;
    setChatInput("");
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    set(ref(rtdb, `typing/${shareId}/${user.uid}`), null);
    try {
      const msgRef = push(ref(rtdb, `chat/${shareId}`));
      await set(msgRef, {
        text,
        name: user.displayName || "User",
        uid: user.uid,
        color: collaborators?.[user.uid]?.color || "#6366F1",
        time: Date.now(),
      });
    } catch (err) { console.error("Chat send error:", err); }
  }, [chatInput, shareId, rtdb, user, collaborators]);

  // ====== Sticky Note সেভ করো (নির্দিষ্ট সেলে) ======
  const saveStickyNote = useCallback(async (rIdx, col, text) => {
    if (!shareId || !rtdb || !user) return;
    const cellKey = `r${rIdx}_${col}`;
    try {
      if (!text.trim()) {
        await remove(ref(rtdb, `stickyNotes/${shareId}/${cellKey}`));
      } else {
        await set(ref(rtdb, `stickyNotes/${shareId}/${cellKey}`), {
          text: text.trim(),
          author: user.displayName || "User",
          color: collaborators?.[user.uid]?.color || "#F59E0B",
          rIdx, col,
          updatedAt: Date.now(),
        });
      }
    } catch (err) { console.error("Sticky note save error:", err); }
    setActiveStickyNote(null);
    setStickyNoteText("");
  }, [shareId, rtdb, user, collaborators]);

  // ====== Sticky Note ডিলিট করো ======
  const deleteStickyNote = useCallback(async (rIdx, col) => {
    if (!shareId || !rtdb) return;
    const cellKey = `r${rIdx}_${col}`;
    try { await remove(ref(rtdb, `stickyNotes/${shareId}/${cellKey}`)); } catch (err) { console.error("Sticky note delete error:", err); }
  }, [shareId, rtdb]);

  // ============================================================
  // REAL-TIME COLLABORATION
  // ============================================================

  // Share current tab — creates a shared session
  const handleAddPermission = useCallback(async (email, role) => {
    if (!shareId || !db || !email) return;
    const newPerms = { ...sharePermissions, [email.toLowerCase()]: role };
    setSharePermissions(newPerms);
    try {
      await setDoc(doc(db, "shared_tables", shareId), {
        permissions: newPerms,
        lastUpdated: serverTimestamp(),
      }, { merge: true });
    } catch (err) { console.error("Permission error:", err); }
  }, [shareId, db, sharePermissions]);

  const handleRemovePermission = useCallback(async (email) => {
    if (!shareId || !db) return;
    const newPerms = { ...sharePermissions };
    delete newPerms[email];
    setSharePermissions(newPerms);
    try {
      await setDoc(doc(db, "shared_tables", shareId), {
        permissions: newPerms,
        lastUpdated: serverTimestamp(),
      }, { merge: true });
    } catch (err) { console.error("Remove permission error:", err); }
  }, [shareId, db, sharePermissions]);

  const handleShareTab = useCallback(async () => {
    if (!user) { showToast("Please login first!", "error"); return; }
    if (!db) { showToast("Database not connected!", "error"); return; }
    if (isSharingTab) return;
    setIsSharingTab(true);

    try {
      const newShareId = `share_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const shareData = {
        id:        newShareId,
        tabData:   activeTab,
        ownerId:   user.uid,
        ownerName: user.displayName || "User",
        ownerEmail: user.email,
        isPremium,
        maxCollaborators: isPremium ? 999 : FREE_COLLAB_LIMIT,
        permissions: {},
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      };

      // Link ID আগে থেকেই জানা যায় (Firestore এর জন্য অপেক্ষা করা লাগে না) —
      // তাই popup সাথে সাথে দেখাই, আসল সেভ ব্যাকগ্রাউন্ডে চলুক
      const link = `${window.location.origin}?share=${newShareId}`;
      setShareId(newShareId);
      setShareLink(link);
      setMyShareRole("owner");
      setShareModalOpen(true);
      startSyncingSharedTab(newShareId);

      // Firestore + RTDB write একসাথে (parallel) — সিরিয়ালি না করে সময় বাঁচালাম
      const presenceWrite = rtdb
        ? (async () => {
            const presenceRef = ref(rtdb, `presence/${newShareId}/${user.uid}`);
            await set(presenceRef, {
              name:   user.displayName || "User",
              email:  user.email,
              photo:  user.photoURL || "",
              color:  `#${Math.floor(Math.random()*16777215).toString(16).padStart(6,"0")}`,
              joinedAt: Date.now(),
              activeCell: null,
            });
            onDisconnect(presenceRef).remove();
          })()
        : Promise.resolve();

      await Promise.all([
        setDoc(doc(db, "shared_tables", newShareId), shareData),
        presenceWrite,
      ]);
    } catch (err) {
      console.error("Share error:", err);
      showToast("Failed to create share link.", "error");
    } finally {
      setIsSharingTab(false);
    }
  }, [user, isPremium, activeTab, db]);

  // Sync active tab data to Firestore for collaborators
  const startSyncingSharedTab = useCallback((sharedId) => {
    if (!sharedId || !db) return;
    setShareId(sharedId);
  }, [db]);

  // Auto-sync when tab changes and shareId exists — works for owner AND editor
  useEffect(() => {
    if (!shareId || !db || !activeTab) return;
    if (myShareRole === "viewer") return;
    const timer = setTimeout(async () => {
      try {
        await setDoc(doc(db, "shared_tables", shareId), {
          tabData: activeTab,
          lastUpdated: serverTimestamp(),
          lastEditedBy: user?.uid || "unknown",
        }, { merge: true });
      } catch (err) { console.error("sync err:", err); }
    }, 600);
    return () => clearTimeout(timer);
  }, [activeTab, shareId, db, myShareRole, user]);

  // Join a shared table from URL
  const handleJoinSharedTable = useCallback(async (sharedId) => {
    if (!db) return;
    if (sharedUnsubRef.current) { sharedUnsubRef.current(); sharedUnsubRef.current = null; }
    try {
      const sharedDoc = await getDoc(doc(db, "shared_tables", sharedId));
      if (!sharedDoc.exists()) { showToast("Shared table not found!", "error"); return; }

      const data = sharedDoc.data();

      // Permission check
      const myEmail = user?.email?.toLowerCase() || "";
      const perms = data.permissions || {};
      let myRole = "viewer";
      if (data.ownerId === user?.uid) myRole = "owner";
      else if (perms[myEmail]) myRole = perms[myEmail];
      setMyShareRole(myRole);
      setSharePermissions(perms);

      // Editor/owner shareId set করো — sync এর জন্য
      if (myRole === "editor" || myRole === "owner") {
        setShareId(sharedId);
      }

      setIsSharedView(true);

      // সবাই real-time দেখবে — owner ও
      const unsubShared = onSnapshot(doc(db, "shared_tables", sharedId), (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          // নিজে যদি last editor হই তাহলে skip — নিজের input overwrite হবে না
          if (d.lastEditedBy && d.lastEditedBy === user?.uid) return;
          if (d.tabData) {
            setTabs(prev => {
              const exists = prev.find(t => t.id === d.tabData.id);
              if (exists) {
                return prev.map(t => t.id === d.tabData.id ? { ...t, ...d.tabData } : t);
              }
              return [...prev, d.tabData];
            });
            setActiveTabId(d.tabData.id);
          }
          if (d.permissions) setSharePermissions(d.permissions);
        }
      });

      // Set own presence
      if (rtdb && user) {
        const presenceRef = ref(rtdb, `presence/${sharedId}/${user.uid}`);
        await set(presenceRef, {
          name:   user.displayName || "Viewer",
          email:  user.email || "",
          photo:  user.photoURL || "",
          color:  `#${Math.floor(Math.random()*16777215).toString(16).padStart(6,"0")}`,
          joinedAt: Date.now(),
          activeCell: null,
        });
        onDisconnect(presenceRef).remove();
      }

      // Listen to presence
      let unsubPresence = null;
      if (rtdb) {
        const presRef = ref(rtdb, `presence/${sharedId}`);
        unsubPresence = onValue(presRef, (snap) => {
          setCollaborators(snap.val() || {});
        });
      }

      sharedUnsubRef.current = () => {
        unsubShared();
        if (unsubPresence) unsubPresence();
      };
    } catch (err) {
      console.error("Join error:", err);
    }
  }, [db, rtdb, user]);

  // Check URL for share param — user load হওয়ার পর call করো
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sharedId = params.get("share");
    if (sharedId && user) handleJoinSharedTable(sharedId);
  }, [user]);
  // ── Copy Cell ──
  const applyToolbarFormat = useCallback((fmt) => {
    if (!selectedCell) return;
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      const updatedRows = [...t.rows];
      const cellsToUpdate = [];
      if (selectionStart && selectionEnd && !(selectionStart.rIdx === selectionEnd.rIdx && selectionStart.cIdx === selectionEnd.cIdx)) {
        const minR = Math.min(selectionStart.rIdx, selectionEnd.rIdx);
        const maxR = Math.max(selectionStart.rIdx, selectionEnd.rIdx);
        const minC = Math.min(selectionStart.cIdx, selectionEnd.cIdx);
        const maxC = Math.max(selectionStart.cIdx, selectionEnd.cIdx);
        for (let r = minR; r <= maxR; r++) {
          for (let c = minC; c <= maxC; c++) {
            if (columns[c]) cellsToUpdate.push({ rIdx: r, col: columns[c] });
          }
        }
      } else {
        cellsToUpdate.push({ rIdx: selectedCell.rIdx, col: selectedCell.col });
      }
      cellsToUpdate.forEach(({ rIdx, col }) => {
        if (!col || updatedRows[rIdx] === undefined) return;
        const existing = updatedRows[rIdx]?.[col] || {};
        const existingObj = typeof existing === "object" ? existing : { value: String(existing) };
        updatedRows[rIdx] = {
          ...updatedRows[rIdx],
          [col]: {
            ...existingObj,
            ...(fmt.color !== undefined && { color: fmt.color }),
            ...(fmt.bgColor !== undefined && { bgColor: fmt.bgColor }),
            ...(fmt.fontSize !== undefined && { fontSize: fmt.fontSize }),
            ...(fmt.fontWeight !== undefined && { fontWeight: fmt.fontWeight }),
            ...(fmt.fontStyle !== undefined && { fontStyle: fmt.fontStyle }),
            ...(fmt.textAlign !== undefined && { textAlign: fmt.textAlign }),
            ...(fmt.textDecoration !== undefined && { textDecoration: fmt.textDecoration }),
            ...(fmt.numberFormat !== undefined && { numberFormat: fmt.numberFormat }),
            ...(fmt.whiteSpace !== undefined && { whiteSpace: fmt.whiteSpace }),
          },
        };
      });
      return { ...t, rows: updatedRows };
    }));
  }, [selectedCell, selectionStart, selectionEnd, activeTabId, columns]);

const getSelectedCellFormat = useCallback(() => {
  if (!selectedCell) return {};
  const { rIdx, col } = selectedCell;
  const cell = rows[rIdx]?.[col];
  if (!cell || typeof cell !== "object") return {};
  return {
    color:          cell.color          || "",
    bgColor:        cell.bgColor        || "",
    fontSize:       cell.fontSize       || 14,
    fontWeight:     cell.fontWeight     || "normal",
    fontStyle:      cell.fontStyle      || "normal",
    textAlign:      cell.textAlign      || "left",
    textDecoration: cell.textDecoration || "none",
    numberFormat:   cell.numberFormat   || "none",
    whiteSpace:     cell.whiteSpace     || "nowrap",
  };
}, [selectedCell, rows]);
  const handleCopyCell = useCallback((rIdx, col) => {
    const val = cellVal(rows[rIdx]?.[col]);
    setCopiedCell({ value: val, fromRow: rIdx, fromCol: col });
    navigator.clipboard.writeText(val).catch(() => {});
  }, [rows]);
 
  // ── Paste Cell ──
  const handlePasteCell = useCallback((rIdx, col) => {
    if (!copiedCell) return;
    handleCellChange(rIdx, col, copiedCell.value);
  }, [copiedCell, handleCellChange]);
 
  // ── Keyboard Copy/Paste Global ──
  useEffect(() => {
    const handleMouseUp = () => setIsSelecting(false);
    document.addEventListener("mouseup", handleMouseUp);
 
    const handleKeyboard = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "h") {
        e.preventDefault();
        setShowFindReplace(true);
      }
    };
    document.addEventListener("keydown", handleKeyboard);
    return () => {
      document.removeEventListener("keydown", handleKeyboard);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleUndo, handleRedo]);

  // COLUMN OPERATIONS
  // ============================================================
  const addColumn = () => {
    showPrompt("Enter New Column Name", "", (newColName) => {
      if (newColName && !columns.includes(newColName)) {
        setColumns([...columns, newColName]);
        setRows(rows.map(row => ({ ...row, [newColName]: makeCellObj("") })));
      }
    });
  };

  const handleColumnHeaderClick = (e, col) => {
    if (col === "ID") return;
    e.preventDefault(); e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuConfig({ show: true, x: rect.left > window.innerWidth - 220 ? rect.left - 200 : rect.left, y: rect.bottom + 4, col });
  };

  const renameColumn = (e) => {
    e.stopPropagation();
    const oldName = menuConfig.col;
    setMenuConfig(p => ({ ...p, show: false }));
    showPrompt("Enter new name", oldName, (newName) => {
      if (newName && newName !== oldName) {
        setColumns(columns.map(c => c === oldName ? newName : c));
        setRows(rows.map(r => { const nr = { ...r, [newName]: r[oldName] || makeCellObj("") }; delete nr[oldName]; return nr; }));
        setColumnTypes(prev => { const ct = { ...prev }; if (ct[oldName]) { ct[newName] = ct[oldName]; delete ct[oldName]; } return ct; });
        updateActiveTab(t => {
          const cf = { ...(t.columnFormats || {}) };
          if (cf[oldName]) { cf[newName] = cf[oldName]; delete cf[oldName]; }
          return { ...t, columnFormats: cf };
        });
      }
    });
  };

  const moveColumn = (e, direction) => {
    e.stopPropagation();
    const idx = columns.indexOf(menuConfig.col);
    const newCols = [...columns];
    const targetIdx = direction === "left" ? idx - 1 : idx + 1;
    if (targetIdx > 0 && targetIdx < columns.length) {
      [newCols[idx], newCols[targetIdx]] = [newCols[targetIdx], newCols[idx]];
      setColumns(newCols);
    }
    setMenuConfig(p => ({ ...p, show: false }));
  };

  const deleteColumn = (e, colName) => {
    e.stopPropagation();
    if (colName === "ID") return;
    if (window.confirm(`Delete column "${colName}"?`)) {
      setColumns(columns.filter(c => c !== colName));
      setRows(rows.map(row => { const nr = { ...row }; delete nr[colName]; return nr; }));
    }
    setMenuConfig(p => ({ ...p, show: false }));
  };

  const clearColumn = (colName) => {
    if (window.confirm(`Clear all values in "${colName}"?`))
      setRows(rows.map(r => ({ ...r, [colName]: { ...(r[colName] || {}), value: "" } })));
    setMenuConfig(p => ({ ...p, show: false }));
  };

  // ============================================================
  // ROW OPERATIONS
  // ============================================================
  const deleteRow = (idx) => {
    setRows(prev =>
      prev.filter((_, i) => i !== idx)
          .map((row, i) => ({ ...row, ID: { ...(row.ID || {}), value: String(i + 1) } }))
    );
  };

  const deleteSelectedRows = () => {
    if (!selectedRows.size) return;
    if (window.confirm(`Delete ${selectedRows.size} selected rows?`)) {
      setRows(rows.filter((_, i) => !selectedRows.has(i)).map((row, i) => ({ ...row, ID: { ...(row.ID || {}), value: String(i + 1) } })));
      setSelectedRows(new Set());
    }
  };

  const addRow = () => {
    const newRow = makeBlankRow(columns, rows.length + 1);
    setRows([...rows, newRow]);
  };

  const cloneRow = (row) => JSON.parse(JSON.stringify(row || {}));

const cloneCell = (cell) => {
  if (cell && typeof cell === "object") {
    return JSON.parse(JSON.stringify(cell));
  }
  return makeCellObj(cell ?? "");
};

const normalizeRowIds = (list) => {
  return list.map((row, i) => ({
    ...row,
    ID: {
      ...(row.ID && typeof row.ID === "object" ? row.ID : makeCellObj(row.ID)),
      value: String(i + 1),
    },
  }));
};

const autoNumberRows = () => {
  setRows(normalizeRowIds(rows));
};

const cleanData = () => {
  const cleaned = rows.map(row => {
    const next = {};

    columns.forEach(col => {
      const cell = row[col];
      const obj = cell && typeof cell === "object" ? cell : makeCellObj(cell);

      next[col] = {
        ...obj,
        value: col === "ID"
          ? String(obj.value ?? "")
          : String(obj.value ?? "").replace(/\s+/g, " ").trim(),
      };
    });

    return next;
  });

  setRows(normalizeRowIds(cleaned));
};

const removeEmptyRows = () => {
  const nonEmptyRows = rows.filter(row =>
    columns.some(col => col !== "ID" && cellVal(row[col]).trim() !== "")
  );

  setRows(normalizeRowIds(
    nonEmptyRows.length ? nonEmptyRows : [makeBlankRow(columns, 1)]
  ));

  setSelectedRows(new Set());
};

const duplicateSelectedRows = () => {
  if (!selectedRows.size) return;

  const selectedIndexes = [...selectedRows].sort((a, b) => a - b);
  const duplicates = selectedIndexes
    .map(idx => rows[idx])
    .filter(Boolean)
    .map(row => cloneRow(row));

  setRows(normalizeRowIds([...rows, ...duplicates]));
  setSelectedRows(new Set());
};

const fillDownSelectedRows = () => {
  if (!selectedRows.size) return;

  const selectedIndexes = [...selectedRows].sort((a, b) => a - b);
  const nextRows = rows.map(row => cloneRow(row));

  selectedIndexes.forEach(idx => {
    if (idx <= 0 || !nextRows[idx]) return;

    columns.forEach(col => {
      if (col === "ID") return;

      const currentValue = cellVal(nextRows[idx][col]).trim();

      if (currentValue === "") {
        nextRows[idx][col] = cloneCell(nextRows[idx - 1][col]);
      }
    });
  });

  setRows(normalizeRowIds(nextRows));
};
  // ============================================================
  // DRAG & DROP
  // ============================================================
  const handleRowDragStart = (idx) => setDragRowIdx(idx);
  const handleRowDragOver  = (e, idx) => { e.preventDefault(); setDragOverRowIdx(idx); };
  const handleRowDrop = (idx) => {
    if (dragRowIdx === null || dragRowIdx === idx) { setDragRowIdx(null); setDragOverRowIdx(null); return; }
    const newRows = [...rows];
    const [moved] = newRows.splice(dragRowIdx, 1);
    newRows.splice(idx, 0, moved);
    setRows(newRows.map((r, i) => ({ ...r, ID: { ...(r.ID || {}), value: String(i + 1) } })));
    setDragRowIdx(null); setDragOverRowIdx(null);
  };

  const handleColDragStart = (idx) => setDragColIdx(idx);
  const handleColDragOver  = (e, idx) => { e.preventDefault(); setDragOverColIdx(idx); };
  const handleColDrop = (idx) => {
    if (dragColIdx === null || dragColIdx === idx || dragColIdx === 0 || idx === 0) {
      setDragColIdx(null); setDragOverColIdx(null); return;
    }
    const newCols = [...columns];
    const [moved] = newCols.splice(dragColIdx, 1);
    newCols.splice(idx, 0, moved);
    setColumns(newCols);
    setDragColIdx(null); setDragOverColIdx(null);
  };

  // ============================================================
  // FILE IMPORT
  // ============================================================
  const processImportFile = async (file) => {
    if (!file) return;
    setIsLoading(true);
    try {
      const data = await file.arrayBuffer();
      const wb   = XLSX.read(data);
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1 });
      if (json.length < 1) return;
      const newCols = json[0].map(String);
      const newRows = json.slice(1).map((r) => {
        const obj = {};
        newCols.forEach((col, ci) => { obj[col] = makeCellObj(r[ci] !== undefined ? String(r[ci]) : ""); });
        return obj;
      });
      updateActiveTab({ columns: newCols, rows: newRows, title: file.name.replace(/\.[^.]+$/, ""), columnTypes: {}, columnFormats: {} });
    } catch (err) { showToast("Failed to import file: " + err.message, "error"); }
    finally { setIsLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    await processImportFile(file);
    e.target.value = "";
  };

  const ACCEPTED_IMPORT_EXT = /\.(csv|xlsx|xls)$/i;
  const handleDragEnter = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!e.dataTransfer.types.includes("Files")) return;
    dragCounterRef.current++;
    setIsDraggingFile(true);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDraggingFile(false);
  };
  const handleFileDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDraggingFile(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!ACCEPTED_IMPORT_EXT.test(file.name)) { showToast("Only .csv, .xlsx, .xls files are supported", "error"); return; }
    await processImportFile(file);
  };

  // ============================================================
  // VOICE INPUT (browser Web Speech API — no backend needed)
  // ============================================================
  const startVoiceInput = useCallback((field, setter, currentVal) => {
    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      showToast("এই ব্রাউজারে ভয়েস ইনপুট সাপোর্ট করে না। Chrome ব্যবহার করে দেখো।", "error");
      return;
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (listeningField === field) { setListeningField(null); return; }

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = voiceLang;
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    const baseText = currentVal ? currentVal + " " : "";

    recognition.onresult = (e) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript;
      setter(baseText + transcript);
    };
    recognition.onerror = (e) => {
      console.error("Voice input error:", e.error);
      setListeningField(null);
    };
    recognition.onend = () => {
      setListeningField(null);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setListeningField(field);
    recognition.start();
  }, [listeningField, voiceLang]);

  // ============================================================
  // AI TABLE GENERATOR
  // ============================================================
  const handleGenerateTable = () => {
    if (!genText) return;
    if (!isPremium && history.length >= FREE_TABLE_LIMIT) { setShowUpgrade(true); return; }
    setIsLoading(true);
    setTimeout(() => {
      const input = genText.toLowerCase();
      const aiResult = runAICommand(genText, { columns });
      let newCols, finalTitle, rowCount = 10;

      // Row count detect করো
      const rowMatch = input.match(/(\d+)/);
      if (rowMatch) rowCount = Math.min(parseInt(rowMatch[1]), 5000);
      if (aiResult.type === "create_table") rowCount = aiResult.rows;

      // ===== CUSTOM AI COMMAND (explicit column name dile) =====
      if (aiResult.type === "create_table" && aiResult.columnNames.length > 0) {
        newCols = aiResult.columnNames;
        finalTitle = "Custom Table";

      // ===== SALARY / PAYROLL =====
      } else if (input.includes("salary") || input.includes("payroll") || input.includes("বেতন") || input.includes("মজুরি")) {
        newCols = ["ID", "Name", "Position", "Department", "Basic Salary", "Bonus", "Deduction", "Net Salary", "Status"];
        finalTitle = "Salary Sheet";

      // ===== STUDENT / SCHOOL =====
      } else if (input.includes("student") || input.includes("school") || input.includes("ছাত্র") || input.includes("শিক্ষার্থী") || input.includes("স্কুল")) {
        newCols = ["ID", "Name", "Roll", "Class", "Section", "Math", "English", "Science", "Total", "Grade"];
        finalTitle = "Student List";

      // ===== ATTENDANCE =====
      } else if (input.includes("attendance") || input.includes("হাজিরা") || input.includes("উপস্থিতি")) {
        newCols = ["ID", "Name", "Date", "In Time", "Out Time", "Hours", "Status", "Remarks"];
        finalTitle = "Attendance Sheet";

      // ===== INVENTORY / STOCK =====
      } else if (input.includes("inventory") || input.includes("stock") || input.includes("স্টক") || input.includes("পণ্য") || input.includes("product")) {
        newCols = ["ID", "Product Name", "SKU", "Category", "Stock Qty", "Unit Price", "Total Value", "Supplier", "Status"];
        finalTitle = "Inventory Tracker";

      // ===== EXPENSE / BUDGET =====
      } else if (input.includes("expense") || input.includes("budget") || input.includes("খরচ") || input.includes("বাজেট")) {
        newCols = ["ID", "Date", "Category", "Description", "Amount", "Payment Method", "Status"];
        finalTitle = "Expense Tracker";

      // ===== INVOICE / BILL =====
      } else if (input.includes("invoice") || input.includes("bill") || input.includes("ইনভয়েস") || input.includes("বিল")) {
        newCols = ["ID", "Invoice No", "Client Name", "Date", "Item", "Qty", "Unit Price", "Total", "Status"];
        finalTitle = "Invoice Sheet";

      // ===== CRM / CUSTOMER =====
      } else if (input.includes("crm") || input.includes("customer") || input.includes("client") || input.includes("কাস্টমার") || input.includes("গ্রাহক")) {
        newCols = ["ID", "Name", "Company", "Email", "Phone", "City", "Stage", "Deal Value", "Last Contact"];
        finalTitle = "CRM / Customer List";

      // ===== EMPLOYEE / HR =====
      } else if (input.includes("employee") || input.includes("staff") || input.includes("hr") || input.includes("কর্মী") || input.includes("কর্মচারী")) {
        newCols = ["ID", "Name", "Department", "Position", "Join Date", "Phone", "Email", "Salary", "Status"];
        finalTitle = "Employee List";

      // ===== ORDER / SALES =====
      } else if (input.includes("order") || input.includes("sales") || input.includes("অর্ডার") || input.includes("বিক্রয়")) {
        newCols = ["ID", "Order No", "Customer", "Product", "Qty", "Unit Price", "Total", "Date", "Status"];
        finalTitle = "Order / Sales Sheet";

      // ===== TASK / PROJECT =====
      } else if (input.includes("task") || input.includes("project") || input.includes("কাজ") || input.includes("প্রজেক্ট")) {
        newCols = ["ID", "Task Name", "Assignee", "Priority", "Start Date", "Due Date", "Progress", "Status"];
        finalTitle = "Task Tracker";

      // ===== CONTACT / PHONE BOOK =====
      } else if (input.includes("contact") || input.includes("phone book") || input.includes("যোগাযোগ") || input.includes("নম্বর")) {
        newCols = ["ID", "Name", "Phone", "Email", "Address", "City", "Relation", "Notes"];
        finalTitle = "Contact List";

      // ===== MEDICINE / PHARMACY =====
      } else if (input.includes("medicine") || input.includes("pharmacy") || input.includes("ওষুধ") || input.includes("ফার্মেসি")) {
        newCols = ["ID", "Medicine Name", "Generic Name", "Category", "Stock", "Unit Price", "Expiry Date", "Supplier"];
        finalTitle = "Medicine Stock";

      // ===== RENT / PROPERTY =====
      } else if (input.includes("rent") || input.includes("property") || input.includes("ভাড়া") || input.includes("বাড়ি")) {
        newCols = ["ID", "Tenant Name", "Room/Unit", "Rent Amount", "Due Date", "Paid Date", "Status", "Remarks"];
        finalTitle = "Rent Collection";

      // ===== MARKS / RESULT =====
      } else if (input.includes("result") || input.includes("marks") || input.includes("exam") || input.includes("ফলাফল") || input.includes("পরীক্ষা")) {
        newCols = ["ID", "Name", "Roll", "Bengali", "English", "Math", "Science", "Social", "Total", "GPA"];
        finalTitle = "Exam Result Sheet";

      // ===== DONATION / FUND =====
      } else if (input.includes("donation") || input.includes("fund") || input.includes("দান") || input.includes("চাঁদা")) {
        newCols = ["ID", "Donor Name", "Phone", "Amount", "Date", "Purpose", "Payment Method", "Status"];
        finalTitle = "Donation / Fund Collection";

      // ===== MEMBER LIST =====
      } else if (input.includes("member") || input.includes("সদস্য")) {
        newCols = ["ID", "Name", "Phone", "Address", "Join Date", "Member Type", "Fee", "Status"];
        finalTitle = "Member List";

      // ===== GROCERY / MUDI SHOP =====
      } else if (input.includes("grocery") || input.includes("mudi") || input.includes("মুদি") || input.includes("দোকান")) {
        newCols = ["ID", "Item Name", "Category", "Unit", "Stock Qty", "Buy Price", "Sell Price", "Profit", "Status"];
        finalTitle = "Grocery Shop Hisab";

      // ===== TUITION FEE =====
      } else if (input.includes("tuition") || input.includes("টিউশন") || input.includes("প্রাইভেট")) {
        newCols = ["ID", "Student Name", "Class", "Subject", "Monthly Fee", "Paid", "Due", "Month", "Status"];
        finalTitle = "Tuition Fee Tracker";

      // ===== LOCAL COMMITTEE / CHADA =====
      } else if (input.includes("committee") || input.includes("chada") || input.includes("চাঁদা") || input.includes("কমিটি")) {
        newCols = ["ID", "Name", "Address", "Amount", "Date", "Purpose", "Collected By", "Status"];
        finalTitle = "Committee Fund Collection";

      // ===== VEHICLE / RICKSHAW RENT =====
      } else if (input.includes("rickshaw") || input.includes("vehicle rent") || input.includes("ভাড়া গাড়ি") || input.includes("রিকশা")) {
        newCols = ["ID", "Driver Name", "Vehicle No", "Route", "Date", "Daily Rent", "Fuel Cost", "Net Income", "Status"];
        finalTitle = "Vehicle Rent Hisab";

      // ===== QURBANI / LIVESTOCK =====
      } else if (input.includes("qurbani") || input.includes("livestock") || input.includes("কোরবানি") || input.includes("পশু")) {
        newCols = ["ID", "Buyer Name", "Animal Type", "Weight", "Price", "Advance Paid", "Due", "Delivery Date", "Status"];
        finalTitle = "Qurbani / Livestock Hisab";

      // ===== LAND / PROPERTY =====
      } else if (input.includes("land") || input.includes("plot") || input.includes("জমি") || input.includes("প্লট")) {
        newCols = ["ID", "Owner Name", "Plot No", "Location", "Size (Decimal)", "Price", "Advance", "Due", "Status"];
        finalTitle = "Land / Property Record";

      // ===== DEFAULT =====
      } else {
        newCols = ["ID", "Name", "Description", "Category", "Value", "Date", "Status"];
        finalTitle = genText.toUpperCase();
      }

      // ===== SAME TABLE-E MERGE/APPEND (naam ar purono data change hobe na) =====
      const mergedCols = [...columns];
      newCols.forEach((c) => { if (!mergedCols.includes(c)) mergedCols.push(c); });

      const startId = rows.length + 1;
      const addedRows = Array.from({ length: rowCount }, (_, i) => makeBlankRow(mergedCols, startId + i));
      const backfilledOldRows = rows.map((row) => {
        const patched = { ...row };
        mergedCols.forEach((c) => { if (!(c in patched)) patched[c] = makeCellObj(""); });
        return patched;
      });
      const mergedRows = [...backfilledOldRows, ...addedRows];

      let mergedColumnFormats = { ...columnFormats };
      if (aiResult.type === "create_table" && aiResult.color) {
        newCols.forEach((c) => { mergedColumnFormats[c] = { ...(mergedColumnFormats[c] || {}), bgColor: aiResult.color }; });
      }

      updateActiveTab({ columns: mergedCols, rows: mergedRows, columnFormats: mergedColumnFormats });
      setIsLoading(false);
      setGenText("");
      setCurrentTableId(null);
    }, 800);
  };

  // ============================================================
  const buildRowsFromAI = (columns, aiRows) => {
  const safeRows = Array.isArray(aiRows) ? aiRows : [];

  return safeRows.map((row, rowIndex) => {
    const obj = {};

    columns.forEach((col, colIndex) => {
      let value = "";

      if (Array.isArray(row)) {
        value = row[colIndex] ?? "";
      } else if (row && typeof row === "object") {
        value = row[col] ?? "";
      }

      obj[col] = makeCellObj(col === "ID" ? rowIndex + 1 : value);
    });

    return obj;
  });
};

const handleAITableGenerate = async (prompt) => {
  if (!prompt.trim()) return;

  if (!isPremium && aiTableUsage >= AI_FREE_TABLE_LIMIT) {
    setProGateMessage(
      `Free plan allows ${AI_FREE_TABLE_LIMIT} AI-generated tables. Upgrade to Pro for unlimited AI table generation.`
    );
    setShowAITable(false);
    setShowProGate(true);
    return;
  }

  const check = canCreateTable();

  if (!check.allowed) {
    setProGateMessage(check.reason);
    setShowAITable(false);
    setShowProGate(true);
    return;
  }

  setAiBuildLoading(true);

  try {
    const idToken = user ? await user.getIdToken() : null;
    const res = await fetch(AI_TABLE_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        prompt,
        uid: user?.uid || null,
        email: user?.email || null,
        isPremium,
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data.error || data.code || "AI table request failed");
    }

    const aiColumns = Array.isArray(data.columns) && data.columns.length
      ? data.columns.map(String)
      : ["ID", "Name", "Email", "Status"];

    const columnsWithId = aiColumns.includes("ID")
      ? aiColumns
      : ["ID", ...aiColumns];

    const aiRows = buildRowsFromAI(columnsWithId, data.rows || []);

    const finalRows = aiRows.length
      ? aiRows
      : Array.from({ length: 8 }, (_, i) => makeBlankRow(columnsWithId, i + 1));

    const newTab = {
      id: Date.now() + Math.random(),
      title: data.title || "AI Generated Table",
      columns: columnsWithId,
      rows: finalRows,
      columnTypes: data.columnTypes || {},
      columnFormats: {},
      tabColor: "#3978d8",
    };

    const creationEntry = { id: newTab.id, createdAt: Date.now() };

    setTabs(prev => [...prev, newTab]);
    setTableCreationLog(prev => [...prev, creationEntry]);
  if (!isPremium) {
    setLifetimeTablesCreated(prev => prev + 1);

    if (user?.uid && db) {
      setDoc(doc(db, "users", user.uid), {
        lifetimeTablesCreated: increment(1),
        lifetimeTableLimit: FREE_TABLE_LIMIT,
        lastTableCreatedAt: serverTimestamp(),
      }, { merge: true }).catch(err => {
        console.error("Lifetime table count update failed:", err);
      });
    }
  }
    setAiTableUsage(prev => prev + 1);
    setActiveTabId(newTab.id);
    setCurrentTableId(null);
    setSelectedRows(new Set());
    setSearchTerm("");
    setShowAITable(false);
  } catch (err) {
    console.error("AI table error:", err);
    showToast("AI Table Error: " + err.message, "error");
  } finally {
    setAiBuildLoading(false);
  }
};

// ============================================================

  /**
   * Whether the current active sheet already has a saved history entry.
   * We match by currentTableId.
   */
  const isCurrentTableSaved = currentTableId !== null && history.some(h => h.id === currentTableId);

  const saveToHistory = () => {
  const isNewSave = !isCurrentTableSaved;

  if (!isPremium && isNewSave && history.length >= FREE_TABLE_LIMIT) {
    setShowUpgrade(true);
    return;
  }

  const snapshot = {
    id: isCurrentTableSaved ? currentTableId : Date.now(),
    title: tableTitle || "Untitled",
    date: new Date().toLocaleString(),
    columns: [...columns],
    rows: JSON.parse(JSON.stringify(rows.map(row => migrateRow(row, columns)))),
    columnTypes: { ...columnTypes },
    columnFormats: { ...columnFormats },
    tabColor: activeTab?.tabColor || "",
  };

  if (isCurrentTableSaved) {
    setHistory(prev => prev.map(item =>
      item.id === currentTableId ? snapshot : item
    ));
    showToast("Table Updated! ✅", "success");
  } else {
    setHistory(prev => [snapshot, ...prev]);
    setCurrentTableId(snapshot.id);
    showToast("Table Saved! ✅", "success");
  }
};
const handleHistoryClick = (item) => {
  if (currentTableId === item.id) {
    setCurrentTableId(null);
    return;
  }

  const migratedRows = (item.rows || []).map(row =>
    migrateRow(row, item.columns || [])
  );

  updateActiveTab({
    columns: item.columns || [],
    rows: migratedRows,
    title: item.title || "Untitled",
    columnTypes: item.columnTypes || {},
    columnFormats: item.columnFormats || {},
    tabColor: item.tabColor || "",
  });

  setCurrentTableId(item.id);
};

const deleteHistoryItem = (e, historyId) => {
  e.stopPropagation();

  if (window.confirm("Delete this table from history?")) {
    setHistory(prev => prev.filter(item => item.id !== historyId));

    if (currentTableId === historyId) {
      setCurrentTableId(null);
    }
  }
};

const clearAllHistory = () => {
  if (window.confirm("Clear all history?")) {
    setHistory([]);
    setCurrentTableId(null);
  }
};
  // ============================================================
  // KEYBOARD NAVIGATION
  // ============================================================
  const handleKeyDown = (e, rIdx, cIdx) => {
    const input          = e.target;
    const cursorPosition = input.selectionStart;
    const textLength     = input.value.length;
    let targetRow = rIdx, targetCol = cIdx;
    if (e.key === "ArrowRight")                       { if (cursorPosition < textLength) return; targetCol++; }
    else if (e.key === "ArrowLeft")                   { if (cursorPosition > 0) return; targetCol--; }
    else if (e.key === "ArrowDown" || e.key === "Enter") {
      e.preventDefault();
      targetRow++;
      // শেষ row এ Enter চাপলে নতুন row তৈরি করো
      if (e.key === "Enter" && rIdx === rows.length - 1) {
        addRow();
      }
    }
    else if (e.key === "ArrowUp")                     { targetRow--; }
    else if (e.key === "Tab") { e.preventDefault(); targetCol++; if (targetCol >= columns.length) { targetCol = 0; targetRow++; } }
    else return;
    if (targetRow >= 0 && targetRow < rows.length && targetCol >= 0 && targetCol < columns.length) {
      const inputs = tableRef.current?.querySelectorAll('input[type="text"], input[type="date"], textarea');
      const next   = targetRow * columns.length + targetCol;
      if (inputs?.[next]) { inputs[next].focus(); if (e.key !== "ArrowLeft") inputs[next].select(); }
    }
  };

  // ============================================================
  // ANALYTICS
  // ============================================================
  const getAnalyticsData = () => {
    const numericCols = columns.filter(col =>
      col !== "ID" && rows.some(r => !isNaN(parseFloat(cellVal(r[col]))) && cellVal(r[col]) !== "")
    );
    if (!numericCols.length) return null;
    const col    = numericCols[0];
    const vals   = rows.map(r => parseFloat(cellVal(r[col])) || 0);
    const labels = rows.map(r => cellVal(r["Name"] || r["ID"] || ""));
    return { col, vals, labels };
  };

  // ============================================================
  // CELL RENDERING
  // ============================================================
  const getColMinWidth = (col) => {
    const headerLen = col.length;
    const maxCellLen = rows.reduce((max, row) => {
      const v = cellVal(row[col]);
      return Math.max(max, v.length);
    }, 0);
    const maxLen = Math.max(headerLen, maxCellLen);
    const width = maxLen * 6 + 16;
    return `${Math.min(Math.max(width, 40), 75)}px`;
  };

  const getConditionalTextColor = (col, val) => {
    const num = parseFloat(val);
    if (isNaN(num) || val === "" || col === "ID") return null;
    if (/salary|income|wage|pay|earning/i.test(col) && num > 50000) return "#10b981";
    if (num < 33) return "#ef4444";
    return null;
  };

  const buildCellStyle = (col, cell) => {
    const colFmt    = columnFormats[col];
    const cellObj   = cell && typeof cell === "object" ? cell : null;
    const val       = cellObj ? cellObj.value : String(cell ?? "");
    const conditionalColor = getConditionalTextColor(col, val);
    const condFmt   = getConditionalStyle(col, val);
    const s = {};
    const effectiveColor      = (cellObj?.color)      || (colFmt?.color)      || conditionalColor || condFmt.color;
    const effectiveBg         = (cellObj?.bgColor)    || (colFmt?.bgColor)    || condFmt.backgroundColor;
    const effectiveFontSize   = (cellObj?.fontSize)   || (colFmt?.fontSize);
    const effectiveFontWeight = (cellObj?.fontWeight) || (colFmt?.fontWeight);
    const effectiveFontStyle  = (cellObj?.fontStyle);
    const effectiveTextAlign  = (cellObj?.textAlign);
    const effectiveTextDec    = (cellObj?.textDecoration);
    // Border
    const borderTop    = cellObj?.borderTop;
    const borderBottom = cellObj?.borderBottom;
    const borderLeft   = cellObj?.borderLeft;
    const borderRight  = cellObj?.borderRight;
 
    if (effectiveColor)      s.color           = effectiveColor;
    if (effectiveBg)         s.backgroundColor = effectiveBg;
    if (effectiveFontSize)   s.fontSize        = `${effectiveFontSize}px`;
    if (effectiveFontWeight) s.fontWeight      = effectiveFontWeight;
    if (effectiveFontStyle)  s.fontStyle       = effectiveFontStyle;
    if (effectiveTextAlign)  s.textAlign       = effectiveTextAlign;
    if (effectiveTextDec)    s.textDecoration  = effectiveTextDec;
    if (borderTop)           s.borderTop       = borderTop;
    if (borderBottom)        s.borderBottom    = borderBottom;
    if (borderLeft)          s.borderLeft      = borderLeft;
    if (borderRight)         s.borderRight     = borderRight;
 
    const colType = columnTypes[col];
    if (colType === "email" && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) s.color = "#ef4444";
    return s;
  };

  const duplicateValueCounts = useMemo(() => {
    if (!duplicateCol) return null;
    const counts = {};
    rows.forEach((r) => {
      const v = String(cellVal(r[duplicateCol])).trim();
      if (v) counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  }, [duplicateCol, rows]);

  const renderCell = (row, rIdx, col, cIdx) => {
    const colType  = columnTypes[col];
    const cell     = row[col];
    const rawVal   = cellVal(cell);
    const val      = rawVal.startsWith("=") ? evaluateFormula(rawVal, rows, columns) : rawVal;
    const styleObj = buildCellStyle(col, cell);
    const cellObj2 = cell && typeof cell === "object" ? cell : null;
    const numFmt   = cellObj2?.numberFormat || "none";
    const formatVal = (v) => {
      const n = parseFloat(v);
      if (isNaN(n) || numFmt === "none") return v;
      if (numFmt === "currency") return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      if (numFmt === "percent") return (n).toFixed(2) + "%";
      if (numFmt === "decimal2") return n.toFixed(2);
      if (numFmt === "integer") return Math.round(n).toString();
      return v;
    };
    const baseCls = `bg-transparent outline-none text-[11px] transition-all focus:bg-sky-500/[0.08] px-2 h-full w-full ${isDark ? "text-[#f1f6ff]" : "text-[#172033]"}`;
    const noSpellCheckProps = { spellCheck: false, autoCorrect: "off", autoCapitalize: "off" };
    const isEmptyFlagged = highlightEmpty && col !== "ID" && !String(rawVal).trim();
    const isDuplicateFlagged = duplicateCol === col && String(rawVal).trim() &&
      duplicateValueCounts && (duplicateValueCounts[String(rawVal).trim()] || 0) > 1;
    const isInvalidFlagged = validateCol && validateCol.column === col && String(rawVal).trim() &&
      (validateCol.kind === "email"
        ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(rawVal).trim())
        : !/^[\d+\-\s()]{7,15}$/.test(String(rawVal).trim()));
    const inputStyle = {
      ...styleObj,
      width: "100%", minWidth: 0, boxSizing: "border-box",
      ...(isEmptyFlagged ? { backgroundColor: "rgba(239,68,68,0.15)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.4)" } : {}),
      ...(isDuplicateFlagged ? { backgroundColor: "rgba(168,85,247,0.15)", boxShadow: "inset 0 0 0 1px rgba(168,85,247,0.4)" } : {}),
      ...(isInvalidFlagged ? { backgroundColor: "rgba(239,68,68,0.2)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.5)" } : {}),
    };

    if (col === "ID") return (
      <input type="text" value={val} readOnly
        className="bg-transparent px-2 py-1 outline-none text-xs text-[#3978d8] font-black text-center w-full"
        style={{ minWidth: "50px" }} />
    );

    if (colType === "checkbox") return (
      <div className="flex items-center justify-center py-1">
        <input type="checkbox" checked={val === "true" || val === true}
          onChange={e => handleCellChange(rIdx, col, String(e.target.checked))}
          className="w-4 h-4 accent-indigo-500 cursor-pointer" />
      </div>
    );

    if (colType === "date") return (
      <input type="date" value={val}
        onKeyDown={e => handleKeyDown(e, rIdx, cIdx)}
        onChange={e => handleCellChange(rIdx, col, e.target.value)}
        onBlur={e => {
          const newDate = e.target.value;
          if (!newDate) return;
          const baseDate = new Date(newDate + "T00:00:00");
          updateActiveTab(t => {
            const updatedRows = t.rows.map((row, i) => {
              if (i < rIdx) return row;
              const offset = i - rIdx;
              const d = new Date(baseDate);
              d.setMonth(d.getMonth() + offset);
              const iso = d.toISOString().slice(0, 10);
              return { ...row, [col]: makeCellObj(iso) };
            });
            return { ...t, rows: updatedRows };
          });
        }}
        className={`${baseCls} cursor-pointer w-full`}
        style={{ ...inputStyle, colorScheme: isDark ? "dark" : "light" }} />
    );

    if (colType === "dropdown") {
      const opts = columnTypes[col + "__options"] || [];
      return (
        <select value={val} onChange={e => handleCellChange(rIdx, col, e.target.value)}
          className="bg-transparent px-2 py-1 outline-none text-xs cursor-pointer appearance-none w-full"
          style={styleObj}>
          <option value="">— Select —</option>
          {opts.map(o => <option key={o} value={o} style={{ background: isDark ? "#1e293b" : "#fff" }}>{o}</option>)}
        </select>
      );
    }

    // Autocomplete suggestions — same column এর আগের values
    const suggestions = autoSuggest.show && autoSuggest.rIdx === rIdx && autoSuggest.col === col
      ? autoSuggest.items : [];

    return (
      <div className="relative w-full">
        <textarea value={formatVal(val)}
          rows={1}
          spellCheck={false} autoCorrect="off" autoCapitalize="off"
          onFocus={e => {
            setSelectedCell({ rIdx, col });
            setFormulaBarVal(rawVal);
            if (rawVal.startsWith("=")) e.target.value = rawVal;
            e.target.style.height = "auto";
            e.target.style.height = e.target.scrollHeight + "px";
          }}
          onChange={e => {
            const newVal = e.target.value;
            handleCellChange(rIdx, col, newVal);
            setFormulaBarVal(newVal);
            e.target.style.height = "auto";
            e.target.style.height = e.target.scrollHeight + "px";
            // Suggest from same column
            if (newVal.length > 0) {
              const seen = new Set();
              const items = rows
                .filter((r, i) => i !== rIdx)
                .map(r => cellVal(r[col]))
                .filter(v => v && v.toLowerCase().startsWith(newVal.toLowerCase()) && !seen.has(v) && seen.add(v))
                .slice(0, 5);
              setAutoSuggest({ show: items.length > 0, rIdx, col, items });
            } else {
              setAutoSuggest({ show: false, rIdx: -1, col: "", items: [] });
            }
          }}
          onBlur={() => setTimeout(() => setAutoSuggest({ show: false, rIdx: -1, col: "", items: [] }), 150)}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); }
            handleKeyDown(e, rIdx, cIdx);
          }}
          className={`${baseCls} resize-none`}
          style={{ ...inputStyle, whiteSpace: "pre-wrap", wordBreak: "break-word", overflow: "hidden", display: "block", lineHeight: "1.4" }} />
        {suggestions.length > 0 && (
          <div className={`absolute top-full left-0 z-50 rounded-xl border shadow-xl overflow-hidden min-w-[140px] ${isDark ? "bg-[#0a1628] border-[#1e3a5f]" : "bg-white border-slate-200"}`}>
            {suggestions.map((s, i) => (
              <button key={i}
                onMouseDown={e => {
                  e.preventDefault();
                  handleCellChange(rIdx, col, s);
                  setAutoSuggest({ show: false, rIdx: -1, col: "", items: [] });
                }}
                className={`w-full text-left px-3 py-1.5 text-[11px] font-medium transition-all ${isDark ? "text-slate-300 hover:bg-indigo-600/30" : "text-slate-700 hover:bg-indigo-50"}`}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getRowStyle = (row) => {
    const statusCols = ["Status", "status"];
    for (const col of statusCols) {
      if (row[col] && ["checked", "done", "complete", "completed"].includes(cellVal(row[col]).toLowerCase()))
        return isDark ? "bg-emerald-500/10" : "bg-emerald-50";
    }
    return "";
  };

  // ============================================================
  // PDF EXPORT
  // ============================================================
  const downloadPDF = async () => {
    try {
      const docPDF = new jsPDF({ orientation: columns.length > 6 ? "landscape" : "portrait" });
      docPDF.setFillColor(255, 255, 255);
      docPDF.rect(0, 0, docPDF.internal.pageSize.width, docPDF.internal.pageSize.height, "F");
      const hasUnicode = rows.some(row => columns.some(col => hasNonAscii(cellVal(row[col]))));
      const getCellPdfStyle = (row, col) => {
        const cell    = row[col];
        const colFmt  = columnFormats[col];
        const cellObj = cell && typeof cell === "object" ? cell : null;
        return {
          bgColor:    (cellObj?.bgColor)    || (colFmt?.bgColor)    || "",
          color:      (cellObj?.color)      || (colFmt?.color)      || "#0f172a",
          fontSize:   (cellObj?.fontSize)   || (colFmt?.fontSize)   || 11,
          fontWeight: (cellObj?.fontWeight) || (colFmt?.fontWeight) || "normal",
        };
      };
      docPDF.setFontSize(18); docPDF.setTextColor(99, 102, 241);
      docPDF.text(tableTitle || "SheetMind", 14, 18);
      docPDF.setFontSize(9);  docPDF.setTextColor(150, 150, 150);
      docPDF.text(`Exported: ${new Date().toLocaleString()} · ${rows.length} rows · ${columns.length} cols`, 14, 26);
      if (hasUnicode) {
        const PXPMM = 8;
        const measureCtx = document.createElement("canvas").getContext("2d");

        const wrapText = (text, maxWidthPx, fontPx, isBold) => {
          measureCtx.font = `${isBold ? "bold " : ""}${fontPx}px 'Noto Sans Bengali', Arial, sans-serif`;
          const words = String(text).split(" ");
          const lines = [];
          let line = "";
          words.forEach((word) => {
            const test = line ? line + " " + word : word;
            if (measureCtx.measureText(test).width > maxWidthPx && line) {
              lines.push(line);
              line = word;
            } else {
              line = test;
            }
          });
          if (line) lines.push(line);
          return lines.length ? lines : [""];
        };

        const renderLinesToCanvas = (lines, fontPx, fontColor, bgColor, isBold, canvasWpx, canvasHpx) => {
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(canvasWpx));
          canvas.height = Math.max(1, Math.round(canvasHpx));
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = bgColor || "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = fontColor || "#0f172a";
          ctx.font = `${isBold ? "bold " : ""}${fontPx}px 'Noto Sans Bengali', Arial, sans-serif`;
          ctx.textBaseline = "top";
          const lineH = fontPx * 1.35;
          const totalTextH = lines.length * lineH;
          const startYpx = Math.max(2, (canvas.height - totalTextH) / 2);
          lines.forEach((ln, i) => ctx.fillText(ln, 4, startYpx + i * lineH));
          return canvas.toDataURL("image/png");
        };

        const pageW = docPDF.internal.pageSize.width;
        const usableW = pageW - 28;
        const rawWidths = columns.map((col) => {
          const maxLen = Math.max(col.length, ...rows.map((r) => cellVal(r[col]).length));
          return Math.min(Math.max(maxLen * 1.8 + 8, 18), 55);
        });
        const totalRaw = rawWidths.reduce((a, b) => a + b, 0);
        const scaleW = totalRaw > usableW ? usableW / totalRaw : 1;
        const cellWidths = rawWidths.map((w) => w * scaleW);

        let startY = 34;
        let colX = 14;
        const headerHmm = 9;
        columns.forEach((col, ci) => {
          docPDF.setFillColor(30, 41, 59); docPDF.rect(colX, startY, cellWidths[ci], headerHmm, "F");
          const cwPx = cellWidths[ci] * PXPMM, chPx = headerHmm * PXPMM;
          if (hasNonAscii(col)) {
            const fontPx = Math.min(26, chPx * 0.5);
            const lines = wrapText(col, cwPx - 10, fontPx, true);
            const img = renderLinesToCanvas(lines.slice(0, 1), fontPx, "#94a3b8", "#1e293b", true, cwPx, chPx);
            docPDF.addImage(img, "PNG", colX, startY, cellWidths[ci], headerHmm);
          } else {
            docPDF.setTextColor(148, 163, 184); docPDF.setFontSize(8);
            docPDF.text(col, colX + 2, startY + 6);
          }
          colX += cellWidths[ci];
        });
        startY += headerHmm;

        rows.forEach((row) => {
          const cellData = columns.map((col, ci) => {
            const text = cellVal(row[col]);
            const fmt  = getCellPdfStyle(row, col);
            const cwPx = cellWidths[ci] * PXPMM;
            if (!hasNonAscii(text)) return { lines: [text], fmt, fontPx: null };
            const fontPx = Math.min(24, Math.max(16, Math.min(fmt.fontSize, 10) * 2.6));
            const lines = wrapText(text, cwPx - 10, fontPx, fmt.fontWeight === "bold");
            return { lines, fmt, fontPx };
          });
          const maxLines = Math.max(1, ...cellData.map((c) => c.lines.length));
          const rowHmm = Math.max(9, maxLines * 5.5 + 3);

          if (startY + rowHmm > docPDF.internal.pageSize.height - 16) {
            docPDF.addPage(); docPDF.setFillColor(255, 255, 255);
            docPDF.rect(0, 0, docPDF.internal.pageSize.width, docPDF.internal.pageSize.height, "F");
            startY = 14;
          }

          colX = 14;
          columns.forEach((col, ci) => {
            const text = cellVal(row[col]);
            const { lines, fmt, fontPx } = cellData[ci];
            const cellBg = fmt.bgColor || (rows.indexOf(row) % 2 === 0 ? "#f8fafc" : "#ffffff");
            const [r, g, b] = hexToRgb(cellBg);
            docPDF.setFillColor(r, g, b); docPDF.rect(colX, startY, cellWidths[ci], rowHmm, "F");
            docPDF.setDrawColor(226, 232, 240); docPDF.rect(colX, startY, cellWidths[ci], rowHmm, "S");
            if (hasNonAscii(text)) {
              const cwPx = cellWidths[ci] * PXPMM, chPx = rowHmm * PXPMM;
              const img = renderLinesToCanvas(lines, fontPx, fmt.color, cellBg, fmt.fontWeight === "bold", cwPx, chPx);
              docPDF.addImage(img, "PNG", colX, startY, cellWidths[ci], rowHmm);
            } else {
              const [cr, cg, cb] = hexToRgb(fmt.color || "#0f172a");
              docPDF.setTextColor(cr, cg, cb); docPDF.setFontSize(Math.min(fmt.fontSize, 10));
              docPDF.setFont("helvetica", fmt.fontWeight === "bold" ? "bold" : "normal");
              docPDF.text(String(text).slice(0, 40), colX + 2, startY + 6);
            }
            colX += cellWidths[ci];
          });
          startY += rowHmm;
        });
      } else {
        const head = [columns];
        const body = rows.map(row => columns.map(col => cellVal(row[col])));
        const didParseCell = (data) => {
          if (data.section === "body") {
            const { row, column } = data;
            const col      = columns[column.index];
            const tableRow = rows[row.index];
            if (!tableRow) return;
            const fmt = getCellPdfStyle(tableRow, col);
            if (fmt.bgColor)              { const [r, g, b] = hexToRgb(fmt.bgColor); data.cell.styles.fillColor = [r, g, b]; }
            if (fmt.color)                { const [r, g, b] = hexToRgb(fmt.color);   data.cell.styles.textColor = [r, g, b]; }
            if (fmt.fontSize)             data.cell.styles.fontSize  = Math.min(fmt.fontSize, 10);
            if (fmt.fontWeight === "bold") data.cell.styles.fontStyle = "bold";
          }
        };
        const colWidthsPDF = columns.map(col => {
          const maxLen = Math.max(col.length, ...rows.map(r => cellVal(r[col]).length));
          return Math.min(Math.max(maxLen * 2.2 + 6, 12), 60);
        });
        autoTable(docPDF, {
          head, body, startY: 32,
          styles:            { fontSize: 8, cellPadding: 2, font: "helvetica", lineColor: [226, 232, 240], lineWidth: 0.3, minCellHeight: 6 },
          headStyles:        { fillColor: [30, 41, 59], textColor: [148, 163, 184], fontStyle: "bold", fontSize: 8 },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          bodyStyles:        { fillColor: [255, 255, 255], textColor: [15, 23, 42] },
          columnStyles:      Object.fromEntries(columns.map((_, i) => [i, { cellWidth: colWidthsPDF[i] }])),
          theme: "grid", didParseCell,
        });
      }
      docPDF.save(`${(tableTitle || "SHEETMIND").replace(/\s+/g, "_")}.pdf`);
    } catch (err) { console.error("PDF export error:", err); showToast("PDF export failed: " + err.message, "error"); }
  };

  // ============================================================
  // EXCEL EXPORT
  // ============================================================
  const downloadExcel = () => {
    try {
      const wsData = [columns, ...rows.map(row => columns.map(col => cellVal(row[col])))];
      const ws     = XLSX.utils.aoa_to_sheet(wsData);
      const colWidths = columns.map(col => {
        const maxLen = Math.max(col.length, ...rows.map(r => cellVal(r[col]).length));
        return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
      });
      ws["!cols"] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, tableTitle || "Sheet1");
      XLSX.writeFile(wb, `${(tableTitle || "SHEETMIND").replace(/\s+/g, "_")}.xlsx`);
    } catch (err) { showToast("Excel export failed: " + err.message, "error"); }
  };

  // ============================================================
  // UI STATE
  // ============================================================
  const analyticsData  = getAnalyticsData();
  const filteredRows = useMemo(() => {
  const indexedRows = rows.map((row, originalIndex) => ({ row, originalIndex }));

  let result = indexedRows;

  if (searchTerm) {
    result = result.filter(({ row }) =>
      Object.values(row).some(v =>
        cellVal(v).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }

  if (hideEmptyRows) {
    result = result.filter(({ row }) => {
      const vals = Object.entries(row).filter(([k]) => k !== "ID");
      return vals.some(([, v]) => cellVal(v).trim() !== "");
    });
  }

  return result;
}, [rows, searchTerm, hideEmptyRows]);

  // ============================================================
  // GRID VIRTUALIZATION — only render rows near the viewport once
  // the table grows past VIRTUALIZE_THRESHOLD rows
  // ============================================================
  const GRID_ROW_H = 26;
  const GRID_VIRTUALIZE_THRESHOLD = 150;
  const gridVirtualActive = filteredRows.length > GRID_VIRTUALIZE_THRESHOLD;
  const gridStartIdx = gridVirtualActive
    ? Math.max(0, Math.floor(gridScrollTop / GRID_ROW_H) - 15)
    : 0;
  const gridEndIdx = gridVirtualActive
    ? Math.min(filteredRows.length, Math.ceil((gridScrollTop + (tableRef.current?.clientHeight || 600)) / GRID_ROW_H) + 15)
    : filteredRows.length;
  const gridVisibleRows = gridVirtualActive ? filteredRows.slice(gridStartIdx, gridEndIdx) : filteredRows;
  const gridTopSpacerH = gridStartIdx * GRID_ROW_H;
  const gridBottomSpacerH = (filteredRows.length - gridEndIdx) * GRID_ROW_H;
  const gridVisibleColCount = 1 + columns.filter(c => !hiddenCols.has(c)).length;

  if (page === "showcase") return <ShowcasePage onContinue={() => setPage("landing")} isDark={isDark} />;
  if (page === "landing") return <LandingPage onLogin={handleGoogleLogin} isDark={isDark} onSupportClick={() => { setSupportMsg("Payment Support: "); setShowSupport(true); }} onNavigate={setPage} />;
  if (page === "blog") return <BlogPage onBackToHome={() => setPage("landing")} isDark={isDark} />;
  if (page === "contact") return <ContactPage onBackToHome={() => setPage("landing")} isDark={isDark} />;
  if (page === "demo") return <DemoPage onBackToHome={() => setPage("landing")} onSignup={handleGoogleLogin} isDark={isDark} />;

  // ============================================================
  // DESIGN TOKENS — DEEP BLUE + GOLD PREMIUM THEME
  // ============================================================
  const bgPage = "bg-[#0A1F15]";


const bgPanel = "bg-[#0F2A1E] border-[#39AC73]/40 shadow-[0_18px_55px_rgba(0,0,0,0.35)]";
const bgSubtle = "bg-[#1a2942]";
const textMain = "text-[#F8FAFC]";
const textSub = "text-[#94a3b8]";
const borderColor = "border-[#29456B]";
const inputCls = "bg-[#0a1628] border-[#334155] text-[#F8FAFC] placeholder:text-[#7186a6] focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20";
const tableHeadCls = "bg-[#F0F0F0]";
const dividerCls = "divide-[#334155]";

const btnPrimary =
  "bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-black shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-all duration-200 rounded-xl border border-white/10";

const btnGhost =
  "bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-black shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-all duration-200 rounded-xl border border-white/10";

const btnDanger =
  "bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-black shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-all duration-200 rounded-xl border border-white/10";

const btnSuccess =
  "bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-black shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-all duration-200 rounded-xl border border-white/10";
  const syncBadge = () => {
    if (syncStatus === "syncing") return <span className="flex items-center gap-1 text-[8px] text-sky-400 font-black"><div className="w-1.5 h-1.5 border border-sky-400/50 border-t-sky-400 rounded-full animate-spin"></div>Saving...</span>;
    if (syncStatus === "saved" && lastSaved) return <span className="text-[8px] text-emerald-400 font-black">✓ {lastSaved}</span>;
    if (syncStatus === "local") return <span className="text-[8px] text-amber-400 font-black">💾 Local</span>;
    return null;
  };

  // Cooldown status for UI hint
  const freeTablesRemaining = Math.max(FREE_TABLE_LIMIT - lifetimeTablesCreated, 0);
  const showFreeLimitHint = !isPremium;
  const workspaceStats = [
    { label: "Active Sheet", value: activeTab?.title || "Untitled", accent: "text-sky-400" },
    { label: "Rows", value: rows.length, accent: "text-emerald-400" },
    { label: "Columns", value: columns.length, accent: "text-indigo-400" },
    { label: isPremium ? "Plan" : "Free Left", value: isPremium ? (isOnTrial ? "Free Trial" : "Premium") : `${freeTablesRemaining}/${FREE_TABLE_LIMIT}`, accent: isPremium ? "text-yellow-400" : "text-amber-400" },
  ];

  const ToastContainer = () => (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 99999, display: "flex", flexDirection: "column", gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: "10px 18px",
          borderRadius: 10,
          fontWeight: 700,
          fontSize: 13,
          color: "#fff",
          background: t.type === "error" ? "#ef4444" : t.type === "warning" ? "#f59e0b" : "#10b981",
          boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
          minWidth: 200,
        }}>
          {t.type === "error" ? "❌ " : t.type === "warning" ? "⚠️ " : "✅ "}{t.message}
        </div>
      ))}

      {promptModal && (
        <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/50"
          onClick={() => setPromptModal(null)}>
          <div onClick={e => e.stopPropagation()}
            className="w-80 rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
            <div className="px-4 py-3" style={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)" }}>
              <h3 className="text-xs font-black uppercase tracking-wider text-white">{promptModal.title}</h3>
            </div>
            <div className="p-4 flex flex-col gap-3">
              <input
                autoFocus
                value={promptValue}
                onChange={e => setPromptValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") { promptModal.onConfirm(promptValue.trim()); setPromptModal(null); }
                  if (e.key === "Escape") setPromptModal(null);
                }}
                className="w-full rounded-xl px-3 py-2 text-sm outline-none bg-white/5 text-white placeholder-white/30"
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setPromptModal(null)}
                  className="px-4 py-2 rounded-xl text-[10px] font-black uppercase bg-white/10 hover:bg-white/20 text-white">
                  Cancel
                </button>
                <button
                  onClick={() => { promptModal.onConfirm(promptValue.trim()); setPromptModal(null); }}
                  className="px-4 py-2 rounded-xl text-[10px] font-black uppercase bg-indigo-500 hover:bg-indigo-400 text-white">
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`min-h-screen ${bgPage} ${textMain} p-2 md:p-5 font-sans relative`}
      style={{ fontFamily: "'DM Mono','Fira Mono',monospace", transition: "background 0.3s, color 0.3s" }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleFileDrop}
    >
      {isDraggingFile && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center bg-indigo-950/70 backdrop-blur-sm pointer-events-none">
          <div className="border-4 border-dashed border-indigo-400 rounded-[2rem] px-16 py-12 text-center bg-[#0F172A]/80">
            <div className="text-5xl mb-4">📂</div>
            <p className="text-lg font-black uppercase text-indigo-300">Drop your CSV / XLSX file here</p>
          </div>
        </div>
      )}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Bengali&display=swap" rel="stylesheet" />

      <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept=".csv,.xlsx,.xls" />

      {/* ======== MODALS ======== */}
      {/* ====== SHARE MODAL ====== */}
      {shareModalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className={`w-full max-w-md rounded-[2rem] border shadow-2xl p-8 ${isDark ? "bg-[#0a1628] border-[#1e3a5f] text-slate-100" : "bg-white border-slate-200 text-slate-900"}`}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-black text-lg uppercase">Share Table</h3>
                <p className={`text-[10px] mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {isPremium ? "Unlimited collaborators" : `Free plan: max ${FREE_COLLAB_LIMIT} collaborators`}
                </p>
              </div>
              <button onClick={() => setShareModalOpen(false)} className="text-xl hover:opacity-60">✕</button>
            </div>

            {/* Collaborators online */}
            {Object.keys(collaborators).length > 0 && (
              <div className={`mb-5 p-4 rounded-2xl border ${isDark ? "bg-[#070f1e] border-[#1e3a5f]" : "bg-slate-50 border-slate-200"}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Online ({Object.keys(collaborators).length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {Object.values(collaborators).map((c, i) => (
                    <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-black"
                      style={{ backgroundColor: c.color + "20", border: `1px solid ${c.color}40`, color: c.color }}>
                      <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: c.color }} />
                      {c.name?.split(" ")[0] || "User"}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Share link */}
            <div className={`p-4 rounded-2xl border mb-5 ${isDark ? "bg-[#070f1e] border-[#1e3a5f]" : "bg-slate-50 border-slate-200"}`}>
              <p className={`text-[9px] font-black uppercase tracking-widest mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Share Link</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={shareLink}
                  className={`flex-1 px-3 py-2 rounded-xl text-[11px] border outline-none font-mono ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-300" : "bg-white border-slate-200"}`}
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(shareLink); showToast("Link copied!", "success"); }}
                  className="px-4 py-2 rounded-xl font-black text-[10px] uppercase bg-blue-600 text-white hover:bg-blue-500 transition-all">
                  Copy
                </button>
              </div>
            </div>

            {/* Add Email Permission */}
            <div className={`p-4 rounded-2xl border mb-4 ${isDark ? "bg-[#070f1e] border-[#1e3a5f]" : "bg-slate-50 border-slate-200"}`}>
              <p className={`text-[9px] font-black uppercase tracking-widest mb-3 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Add People</p>
              <div className="flex gap-2 mb-3">
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={shareEmailInput}
                  onChange={e => setShareEmailInput(e.target.value)}
                  className={`flex-1 px-3 py-2 rounded-xl text-[11px] border outline-none ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-300 placeholder-slate-600" : "bg-white border-slate-200"}`}
                />
                <select
                  value={shareRoleInput}
                  onChange={e => setShareRoleInput(e.target.value)}
                  className={`px-3 py-2 rounded-xl text-[11px] border outline-none font-black ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-300" : "bg-white border-slate-200"}`}>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button
                  onClick={() => {
                    if (!shareEmailInput.trim()) return;
                    handleAddPermission(shareEmailInput.trim(), shareRoleInput);
                    setShareEmailInput("");
                  }}
                  className="px-4 py-2 rounded-xl font-black text-[10px] uppercase bg-blue-600 text-white hover:bg-blue-500 transition-all">
                  Add
                </button>
              </div>
              {Object.keys(sharePermissions).length > 0 && (
                <div className="space-y-2">
                  {Object.entries(sharePermissions).map(([email, role]) => (
                    <div key={email} className={`flex items-center justify-between px-3 py-2 rounded-xl border ${isDark ? "border-[#1e3a5f] bg-[#050d1f]" : "border-slate-200 bg-white"}`}>
                      <div>
                        <p className={`text-[11px] font-bold ${isDark ? "text-slate-300" : "text-slate-700"}`}>{email}</p>
                        <p className={`text-[9px] font-black uppercase ${role === "editor" ? "text-green-400" : "text-amber-400"}`}>{role}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={role}
                          onChange={e => handleAddPermission(email, e.target.value)}
                          className={`px-2 py-1 rounded-lg text-[10px] border outline-none font-black ${isDark ? "bg-[#0a1628] border-[#1e3a5f] text-slate-300" : "bg-slate-50 border-slate-200"}`}>
                          <option value="editor">Editor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <button
                          onClick={() => handleRemovePermission(email)}
                          className="text-red-400 hover:text-red-300 text-[11px] font-black px-2">
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isPremium && (
              <div className={`p-3 rounded-xl border mb-4 ${isDark ? "border-amber-500/20 bg-amber-500/5" : "border-amber-200 bg-amber-50"}`}>
                <p className="text-[10px] font-black text-amber-400 uppercase">⭐ Free Plan Limit</p>
                <p className={`text-[10px] mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Max {FREE_COLLAB_LIMIT} collaborators. Upgrade for unlimited.
                </p>
              </div>
            )}

            <button onClick={() => setShareModalOpen(false)}
              className={`w-full py-3 rounded-2xl font-black text-xs uppercase border transition-all ${isDark ? "border-[#1e3a5f] text-slate-400 hover:bg-white/5" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              Close
            </button>
          </div>
        </div>
      )}
      {showAdmin    && <AdminPanel onClose={() => setShowAdmin(false)} isDark={isDark} currentUser={user} />}
      {showUpgrade  && <UpgradeModal onClose={() => setShowUpgrade(false)} isDark={isDark} currentUser={user} onSupportClick={() => {
        setShowUpgrade(false);
        setSupportEmail(user?.email || "");
        setSupportName(user?.displayName || "");
        setSupportMsg("Payment Support: ");
        setShowSupport(true);
      }} />}

      {commentCell && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className={`w-full max-w-sm rounded-2xl border shadow-2xl p-5 ${isDark ? "bg-[#070f1e] border-[#1e3a5f]" : "bg-white border-slate-200"}`}>
            <p className="text-xs font-black uppercase tracking-widest text-yellow-400 mb-3">💬 Cell Comment</p>
            <textarea
              autoFocus
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Write your comment here..."
              rows={4}
              className={`w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-300 placeholder-slate-600" : "bg-slate-50 border-slate-200 text-slate-700"}`}
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => handleSaveComment(commentCell.rIdx, commentCell.col, commentText)}
                className="flex-1 py-2 rounded-xl font-black text-xs uppercase bg-yellow-500 hover:bg-yellow-400 text-black transition-all">
                Save
              </button>
              <button
                onClick={() => { setCommentCell(null); setCommentText(""); }}
                className={`flex-1 py-2 rounded-xl font-black text-xs uppercase border transition-all ${isDark ? "border-[#1e3a5f] text-slate-400 hover:bg-white/5" : "border-slate-200 text-slate-500"}`}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {hoverComment && (
        <div className="fixed z-[9999] pointer-events-none"
          style={{ top: hoverComment.y + 10, left: hoverComment.x + 10 }}>
          <div className="bg-yellow-400 text-black text-xs font-bold rounded-xl px-3 py-2 max-w-[200px] shadow-xl">
            {hoverComment.text}
          </div>
        </div>
      )}

      {activeStickyNote && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
          onClick={() => { setActiveStickyNote(null); setStickyNoteText(""); }}>
          <div onClick={e => e.stopPropagation()}
            className={`w-80 rounded-2xl p-4 shadow-2xl ${isDark ? "bg-[#0a1018] border border-white/10" : "bg-white border border-slate-200"}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-xs font-black uppercase tracking-wider ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                📌 Sticky Note
              </h3>
              <button onClick={() => { setActiveStickyNote(null); setStickyNoteText(""); }}
                className="text-lg opacity-60 hover:opacity-100">✕</button>
            </div>
            <textarea
              autoFocus
              value={stickyNoteText}
              onChange={e => setStickyNoteText(e.target.value)}
              placeholder="Write a note..."
              rows={4}
              className={`w-full rounded-xl p-3 text-sm resize-none outline-none ${
                isDark ? "bg-white/5 text-white placeholder-white/30" : "bg-slate-100 text-slate-800 placeholder-slate-400"
              }`}
            />
            <div className="flex justify-between items-center mt-3">
              {stickyNotes[`r${activeStickyNote.rIdx}_${activeStickyNote.col}`] ? (
                <button
                  onClick={() => { deleteStickyNote(activeStickyNote.rIdx, activeStickyNote.col); setActiveStickyNote(null); setStickyNoteText(""); }}
                  className="text-[10px] font-black uppercase text-red-500 hover:text-red-400">
                  🗑 Delete
                </button>
              ) : <span />}
              <button
                onClick={() => saveStickyNote(activeStickyNote.rIdx, activeStickyNote.col, stickyNoteText)}
                className="px-4 py-2 rounded-xl text-[10px] font-black uppercase bg-amber-500 hover:bg-amber-400 text-black">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {showNotesList && shareId && (
        <div className="fixed z-[9998] flex flex-col shadow-2xl backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 bg-black"
          style={{ top: "190px", right: "340px", width: "260px", maxHeight: "380px" }}>
          <div className="flex items-center justify-between px-3 py-2.5 flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)" }}>
            <h3 className="text-[10px] font-black uppercase tracking-wider text-white">📌 All Notes</h3>
            <button onClick={() => setShowNotesList(false)} className="text-sm text-white/80 hover:text-white">✕</button>
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-2 flex flex-col gap-1.5">
            {Object.entries(stickyNotes).map(([cellKey, note]) => (
              <button
                key={cellKey}
                onClick={() => jumpToStickyNote(note.rIdx, note.col)}
                className="text-left rounded-xl px-3 py-2 bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
                <p className="text-[9px] font-black text-amber-400 mb-0.5">
                  {note.col} · Row {note.rIdx + 1}
                </p>
                <p className="text-[10px] text-white/70 line-clamp-2">{note.text}</p>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* ====== Support ফ্লোটিং বাটন ====== */}
      <ToastContainer />

      {user && !showSupport && (
        <button
          onClick={() => {
            setSupportEmail(user?.email || "");
            setSupportName(user?.displayName || "");
            setShowSupport(true);
          }}
          title="Support"
          className="fixed bottom-5 right-5 z-[9997] w-12 h-12 rounded-full shadow-2xl flex items-center justify-center text-xl"
          style={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)" }}>
          💬
        </button>
      )}

      {showHelpBot && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
          onClick={() => setShowHelpBot(false)}>
          <div onClick={e => e.stopPropagation()}
            className="w-80 h-[420px] rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)" }}>
              <h3 className="text-xs font-black uppercase tracking-wider text-white">🤖 Help Assistant</h3>
              <button onClick={() => setShowHelpBot(false)} className="text-sm text-white/80 hover:text-white">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2">
              {helpMessages.length === 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-[10px] text-white/40 text-center mb-1">Try asking / জিজ্ঞেস করে দেখো:</p>
                  {HELP_SUGGESTIONS.map((s, i) => (
                    <button key={i}
                      onClick={() => { setHelpMessages(prev => [...prev, { from: "user", text: s.en }, { from: "bot", text: matchHelpQuestion(s.en) }]); }}
                      className="text-left text-[10px] px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 border border-white/10">
                      {s.en} <span className="text-white/30">/ {s.bn}</span>
                    </button>
                  ))}
                </div>
              )}
              {helpMessages.map((msg, i) => (
                <div key={i} className={`max-w-[85%] ${msg.from === "user" ? "self-end" : "self-start"}`}>
                  <div className={`rounded-xl px-3 py-2 text-xs whitespace-pre-wrap ${
                    msg.from === "user" ? "bg-sky-500 text-white" : "bg-white/10 text-white"
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              <div ref={helpBottomRef} />
            </div>

            <div className="p-3 border-t border-white/10 flex items-center gap-2 flex-shrink-0">
              <input
                value={helpInput}
                onChange={e => setHelpInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") askHelpBot(); }}
                placeholder="Ask in English or বাংলায়..."
                className="flex-1 rounded-xl px-3 py-2 text-xs outline-none bg-white/5 text-white placeholder-white/30"
              />
              <button
                onClick={askHelpBot}
                disabled={!helpInput.trim()}
                className="w-8 h-8 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white flex items-center justify-center text-xs">
                ➤
              </button>
            </div>
          </div>
        </div>
      )}

      {showSupport && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
          onClick={() => setShowSupport(false)}>
          <div onClick={e => e.stopPropagation()}
            className="w-80 rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
            <div className="flex items-center justify-between px-4 py-3"
              style={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)" }}>
              <h3 className="text-xs font-black uppercase tracking-wider text-white">Contact Support</h3>
              <button onClick={() => setShowSupport(false)} className="text-sm text-white/80 hover:text-white">✕</button>
            </div>

            <div className="p-4 flex flex-col gap-3">
              {supportSent ? (
                <p className="text-center text-sm text-green-400 py-6">✅ Message sent! We'll get back to you soon.</p>
              ) : (
                <>
                  <input
                    value={supportName}
                    onChange={e => setSupportName(e.target.value)}
                    placeholder="Your name"
                    className="w-full rounded-xl px-3 py-2 text-xs outline-none bg-white/5 text-white placeholder-white/30"
                  />
                  <input
                    value={supportEmail}
                    onChange={e => setSupportEmail(e.target.value)}
                    placeholder="Your email"
                    type="email"
                    className="w-full rounded-xl px-3 py-2 text-xs outline-none bg-white/5 text-white placeholder-white/30"
                  />
                  <textarea
                    value={supportMsg}
                    onChange={e => setSupportMsg(e.target.value)}
                    placeholder="How can we help?"
                    rows={4}
                    className="w-full rounded-xl px-3 py-2 text-xs outline-none resize-none bg-white/5 text-white placeholder-white/30"
                  />
                  <button
                    onClick={sendSupportMessage}
                    disabled={supportSending || !supportMsg.trim() || !supportEmail.trim()}
                    className="w-full py-2 rounded-xl text-[10px] font-black uppercase bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white">
                    {supportSending ? "Sending..." : "Send Message"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showChatSidebar && shareId && (
        <div data-chat-box className="fixed z-[9998] flex flex-col shadow-2xl backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 bg-black"
          style={chatBoxPos
            ? { left: chatBoxPos.left, top: chatBoxPos.top, width: "300px", height: "420px" }
            : { top: "190px", right: "24px", width: "300px", height: "420px" }
          }>
          <div onMouseDown={handleChatDragStart}
            className="flex items-center justify-between px-3 py-2.5 flex-shrink-0 cursor-move select-none"
            style={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)" }}>
            <h3 className="text-[10px] font-black uppercase tracking-wider text-white flex items-center gap-1">
              💬 Team Chat
            </h3>
            <div className="flex items-center gap-2">
              <button
                onMouseDown={e => e.stopPropagation()}
                onClick={() => setChatMuted(v => !v)}
                title={chatMuted ? "Unmute notifications" : "Mute notifications"}
                className="text-xs text-white/80 hover:text-white">
                {chatMuted ? "🔕" : "🔔"}
              </button>
              <button onClick={() => setShowChatSidebar(false)} className="text-sm text-white/80 hover:text-white">✕</button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2">
            {chatMessages.length === 0 && (
              <p className={`text-[11px] text-center mt-6 ${isDark ? "text-white/30" : "text-slate-400"}`}>
                এখনো কোনো মেসেজ নেই। প্রথম মেসেজ পাঠাও!
              </p>
            )}
            {chatMessages.map(msg => {
              const isMine = msg.uid === user?.uid;
              return (
                <div key={msg.id} className={`max-w-[85%] group/msg relative ${isMine ? "self-end" : "self-start"}`}>
                  {!isMine && (
                    <p className="text-[9px] font-black mb-0.5" style={{ color: msg.color || "#6366F1" }}>
                      {msg.name}
                    </p>
                  )}
                  <div className="flex items-center gap-1">
                    {isMine && (
                      <button
                        onClick={() => deleteChatMessage(msg.id)}
                        title="Delete message"
                        className="opacity-0 group-hover/msg:opacity-100 transition-opacity text-[10px] text-white/30 hover:text-red-400 flex-shrink-0">
                        🗑
                      </button>
                    )}
                    <div className={`rounded-xl px-3 py-2 text-xs ${
                      isMine
                        ? "bg-sky-500 text-white"
                        : isDark ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
                    }`}>
                      {msg.text}
                    </div>
                    <button
                      onClick={() => setReactionPickerFor(v => v === msg.id ? null : msg.id)}
                      title="React"
                      className="opacity-0 group-hover/msg:opacity-100 transition-opacity text-[10px] text-white/30 hover:text-amber-400 flex-shrink-0">
                      😀
                    </button>
                  </div>

                  {reactionPickerFor === msg.id && (
                    <div className={`flex gap-1 mt-1 p-1 rounded-lg w-fit ${isMine ? "self-end" : "self-start"} bg-white/10`}>
                      {["👍", "❤️", "😂", "😮", "🙏"].map(emo => (
                        <button key={emo}
                          onClick={() => { toggleMessageReaction(msg.id, emo); setReactionPickerFor(null); }}
                          className="text-sm hover:scale-125 transition-transform">
                          {emo}
                        </button>
                      ))}
                    </div>
                  )}

                  {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                    <div className={`flex gap-1 mt-1 flex-wrap ${isMine ? "justify-end" : "justify-start"}`}>
                      {Object.entries(msg.reactions).map(([emo, uidsObj]) => {
                        const count = Object.keys(uidsObj || {}).length;
                        if (count === 0) return null;
                        const iReacted = uidsObj?.[user?.uid];
                        return (
                          <button key={emo}
                            onClick={() => toggleMessageReaction(msg.id, emo)}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${
                              iReacted ? "bg-amber-500/30 border border-amber-400/50" : "bg-white/10 border border-white/10"
                            }`}>
                            <span>{emo}</span><span className="text-white/60">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={chatBottomRef} />
          </div>

          {Object.keys(typingUsers).length > 0 && (
            <div className="px-3 pb-1 flex items-center gap-1.5 flex-shrink-0">
              <div className="flex -space-x-1">
                {Object.entries(typingUsers).map(([uid, name]) => (
                  <div key={uid}
                    className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] font-black text-white border border-black"
                    style={{ backgroundColor: collaborators?.[uid]?.color || "#6366F1" }}>
                    {name?.[0] || "?"}
                  </div>
                ))}
              </div>
              <p className="text-[9px] italic text-white/40">
                {Object.values(typingUsers).join(", ")} typing...
              </p>
            </div>
          )}
          <div className={`p-3 border-t flex items-center gap-2 ${isDark ? "border-white/10" : "border-slate-200"}`}>
            <input
              value={chatInput}
              onChange={e => handleTypingChange(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") sendChatMessage(); }}
              placeholder="Type a message..."
              className={`flex-1 rounded-xl px-3 py-2 text-xs outline-none ${
                isDark ? "bg-white/5 text-white placeholder-white/30" : "bg-slate-100 text-slate-800 placeholder-slate-400"
              }`}
            />
            <button
              onClick={sendChatMessage}
              disabled={!chatInput.trim()}
              className="w-8 h-8 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white flex items-center justify-center text-xs">
              ➤
            </button>
          </div>
        </div>
      )}
      {showAITable && (
        <AITableModal
          isDark={isDark}
          isPremium={isPremium}
          aiTableUsage={aiTableUsage}
          onClose={() => setShowAITable(false)}
          onGenerate={handleAITableGenerate}
        />
      )}
      
      {showWelcome && (
        <WelcomeModal
          isDark={isDark}
          userName={user?.displayName?.split(" ")[0]}
          onClose={() => setShowWelcome(false)}
        />
      )}
      {showProGate && (
        <ProGateModal
          message={proGateMessage}
          tabCount={tabs.length}
          isDark={isDark}
          onClose={() => setShowProGate(false)}
          onUpgrade={() => { setShowProGate(false); setShowUpgrade(true); }}
        />
      )}

      {showAIAnalysis && <AIAnalysisModal columns={columns} rows={rows} isDark={isDark} onClose={() => setShowAIAnalysis(false)} />}

      {showTemplates && (
        <TemplateGallery
          isDark={isDark}
          onClose={() => setShowTemplates(false)}
          onSelect={(template) => {
            if (template.id === "blank") {
              addNewTab(null);
            } else {
              const choice = window.confirm(
                `Apply "${template.name}" template?\n\nOK = Apply to current sheet\nCancel = Open in new sheet`
              );
              if (choice) {
                applyTemplateToCurrentTab(template);
              } else {
                addNewTab(template);
              }
            }
            setShowTemplates(false);
          }}
        />
      )}

      {editingColType && (
        <ColumnTypeModal
          col={editingColType}
          currentType={columnTypes[editingColType]}
          currentOptions={columnTypes[editingColType + "__options"]}
          isDark={isDark}
          onClose={() => setEditingColType(null)}
          onSave={(type, opts) => {
            setColumnTypes(prev => ({ ...prev, [editingColType]: type, [editingColType + "__options"]: opts }));
            setEditingColType(null);
          }}
        />
      )}
      {/* BORDER MODAL */}
      {showBorderModal && selectedCell && (
        <BorderModal
          selectedCell={selectedCell}
          rows={rows}
          activeTabId={activeTabId}
          setTabs={setTabs}
          onClose={() => setShowBorderModal(false)}
          isDark={isDark}
        />
      )}

      {showConditional && (
        <ConditionalModal
          columns={columns}
          conditionalRules={conditionalRules}
          onApply={(rules) => setConditionalRules(rules)}
          onClose={() => setShowConditional(false)}
          isDark={isDark}
        />
      )}
      {/* CONDITIONAL FORMATTING MODAL */}
      {editingFormat && (
        <CellFormatModal
          col={editingFormat.col}
          rowIdx={editingFormat.rowIdx}
          scope={editingFormat.scope}
          currentCell={editingFormat.scope === "cell" ? (rows[editingFormat.rowIdx]?.[editingFormat.col] || {}) : null}
          currentColFormat={editingFormat.scope === "column" ? (columnFormats[editingFormat.col] || {}) : null}
          isDark={isDark}
          isPremium={isPremium}
          onClose={() => setEditingFormat(null)}
          onSave={(fmt) => {
            if (editingFormat.scope === "cell")        applyCellFormat(editingFormat.rowIdx, editingFormat.col, fmt);
            else if (editingFormat.scope === "column") applyColumnFormat(editingFormat.col, fmt);
            else if (editingFormat.scope === "tab") {
          const targetTabId = editingFormat.tabId || activeTabId;
            setTabs(prev => prev.map(t =>
              t.id === targetTabId ? { ...t, tabColor: fmt.bgColor || "" } : t
            ));
          }
            setEditingFormat(null);
          }}
        />
      )}

      {/* COLUMN CONTEXT MENU */}
      {menuConfig.show && (
        <div
          ref={menuRef}
          style={{ top: menuConfig.y, left: menuConfig.x }}
          className={`fixed z-[100] w-56 ${
            isDark ? "bg-[#0d1726] border-[#1e2d42]" : "bg-white border-slate-200"
          } border rounded-2xl shadow-2xl p-1.5 backdrop-blur-xl`}
        >
          {[
            { icon: "✏️", label: "Rename Column",   fn: renameColumn },
            { icon: "🔧", label: "Set Column Type", fn: (e) => { e.stopPropagation(); setEditingColType(menuConfig.col); setMenuConfig(p => ({ ...p, show: false })); } },
            { icon: "🎨", label: "Format Column",   fn: (e) => { e.stopPropagation(); setEditingFormat({ col: menuConfig.col, rowIdx: null, scope: "column" }); setMenuConfig(p => ({ ...p, show: false })); } },
            { icon: "←",  label: "Move Left",       fn: (e) => moveColumn(e, "left") },
            { icon: "→",  label: "Move Right",      fn: (e) => moveColumn(e, "right") },
            { icon: "🧹", label: "Clear Column",    fn: (e) => { e.stopPropagation(); clearColumn(menuConfig.col); } },
            { icon: "👁", label: hiddenCols.has(menuConfig.col) ? "Show Column" : "Hide Column",
              fn: (e) => {
                e.stopPropagation();
                setHiddenCols(prev => {
                  const next = new Set(prev);
                  if (next.has(menuConfig.col)) next.delete(menuConfig.col);
                  else next.add(menuConfig.col);
                  return next;
                });
                setMenuConfig(p => ({ ...p, show: false }));
              }
            },
          ].map((item, i) => (
            <button key={i} onClick={item.fn}
              className={`w-full text-left px-3.5 py-2.5 text-[11px] rounded-xl flex items-center gap-3 font-black uppercase tracking-wide transition-colors ${
                isDark ? "hover:bg-white/[0.06] text-slate-400 hover:text-slate-200" : "hover:bg-slate-100 text-slate-500 hover:text-slate-800"
              }`}>
              {item.icon} {item.label}
            </button>
          ))}
          <div className={`h-px my-1.5 ${isDark ? "bg-[#1e2d42]" : "bg-slate-100"}`} />
          <button onClick={(e) => deleteColumn(e, menuConfig.col)}
            className="w-full text-left px-3.5 py-2.5 text-[11px] rounded-xl flex items-center gap-3 font-black uppercase tracking-wide transition-colors text-red-400 hover:bg-red-500/15 hover:text-red-300">
             Delete Column
          </button>
        </div>
      )}

      {/* ======= MAIN LAYOUT ======= */}
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-4 gap-3 md:gap-5">
        <div className="lg:col-span-3 flex flex-col gap-3 md:gap-4">

          {/* ===== HEADER ===== */}
          <header className="header-flow-bg border border-white/10 rounded-[1.75rem] px-4 py-1.5 shadow-xl flex flex-col md:flex-row justify-between items-center gap-1.5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{ background: "linear-gradient(135deg, #6366f1, #22d3ee)" }}>
                {user?.photoURL ? (
                  <img src={user.photoURL} alt="" className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                ) : null}
                <span className="text-white text-xs font-black" style={{ display: user?.photoURL ? "none" : "flex" }}>SM</span>
              </div>
              <div>
                <h1 className="text-xl font-black bg-gradient-to-r from-[#6fb6ff] via-[#22d3ee] to-[#34d399] bg-clip-text text-transparent uppercase tracking-tight leading-none">
                  SheetMind
                </h1>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className={`text-[8px] font-black tracking-[0.3em] uppercase ${textSub}`}>Pro</span>
                  {syncBadge()}
                  {isPremium && (
                    <span className="text-[8px] font-black px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-400 uppercase border border-indigo-500/20">
                      ⭐ {isAdmin ? "Admin" : "Premium"}
                    </span>
                  )}
                  {/* 24h Cooldown hint */}
                  {showFreeLimitHint && (
                  <span className="text-[8px] font-black px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 uppercase border border-amber-500/20">
                    Free Tables: {freeTablesRemaining}/{FREE_TABLE_LIMIT}
                  </span>
                  )}
                </div>
              </div>
              {user && (
                <div className={`flex items-center gap-1.5 ml-1 pl-3 border-l ${isDark ? "border-white/10" : "border-slate-200"}`}>
                  <span className="text-[10px] font-black uppercase tracking-widest text-sky-400">{user.displayName?.split(" ")[0]}</span>
                  <button onClick={handleLogout} className="text-[9px] text-red-400 hover:text-red-300 font-black uppercase">Exit</button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-center md:justify-end gap-0.5 w-full md:max-w-[680px]">
              <button onClick={() => setIsDark(d => !d)} className={`px-2 py-1.5 rounded-lg font-black text-[9px] uppercase ${btnGhost}`} title="Toggle theme">
                {isDark ? "☀️" : "🌙"}
              </button>

              {!user && (
                <button onClick={handleGoogleLogin} className={`px-2 py-1.5 rounded-lg font-black text-[9px] uppercase ${btnPrimary}`}>Sign In</button>
              )}

              {(isAdmin || user?.email === ADMIN_EMAIL) && (
                <button onClick={() => setShowAdmin(true)}
                  className={`px-2 py-1.5 rounded-lg font-black text-[9px] uppercase ${btnGhost}`}>
                  Admin
                </button>
              )}
              <div className="flex items-center gap-1 pr-1.5 mr-0.5 px-1.5 py-1 rounded-xl border-r border-white/10 bg-black">
                <button
                  onClick={() => setShowHelpBot(true)}
                  title="Help / How to use — click to see what you can do here"
                  className="relative w-7 h-7 flex items-center justify-center rounded-full font-black text-xs text-white transition-transform duration-150 hover:scale-110"
                  style={{ background: "linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)", boxShadow: "0 0 10px rgba(99,102,241,0.6)" }}>
                  <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)", opacity: 0.5 }}></span>
                  <span className="relative">?</span>
                </button>
                <button
                  onClick={handleShareTab}
                  disabled={isSharingTab}
                  className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase disabled:opacity-50 disabled:cursor-wait flex items-center gap-1.5 ${btnGhost}`}
                  title="Share & Collaborate">
                  {isSharingTab && <div className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin"></div>}
                  {isSharingTab ? "Sharing..." : "Share"}
                </button>

                <button onClick={() => setShowTemplates(true)}
                  className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase ${btnGhost}`}
                  title="Template Gallery">
                  Templates
                </button>

                <button
                  onClick={() => setFreezeCol(f => !f)}
                  className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase transition-all duration-150 ${btnGhost}`}
                  title={freezeCol ? "Unfreeze first column" : "Freeze first column"}>
                  {freezeCol ? "Frozen" : "Freeze"}
                </button>

                <button onClick={() => fileInputRef.current.click()} className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase ${btnGhost}`}>Import</button>
              </div>

              <div className="flex items-center gap-1 pr-1.5 mr-0.5 px-1.5 py-1 rounded-xl border-r border-white/10 bg-black">
                <button
                  onClick={saveToHistory}
                  className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase ${btnPrimary}`}
                  title={isCurrentTableSaved ? "Update existing saved table" : "Save new table to history"}
                >
                  {isCurrentTableSaved ? "Update" : "Save"}
                </button>

                <button onClick={downloadExcel} className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase ${btnPrimary}`}>Excel</button>
                <button onClick={downloadPDF}   className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase ${btnPrimary}`}>PDF</button>
              </div>

              <div className={`flex items-center gap-1 pr-1.5 mr-0.5 border-r ${isDark ? "border-white/10" : "border-slate-200"}`}>
                <button onClick={() => setHideEmptyRows(v => !v)} className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase transition-all ${hideEmptyRows ? "bg-amber-500 text-white" : isDark ? "bg-slate-700 text-slate-300 hover:bg-slate-600" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}>{hideEmptyRows ? "Show All" : "Hide Empty"}</button>
                <button onClick={() => {
                  const cols = columns;
                  const visibleRows = filteredRows;
                  const colW = (100 / cols.length).toFixed(2) + "%";
                  let html = `<html><head><title>Print</title><style>
                    @page { size: A4 landscape; margin: 8mm; }
                    body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
                    h3 { font-size: 11px; margin-bottom: 5px; color: #333; }
                    table { border-collapse: collapse; width: 100%; font-size: 9px; table-layout: fixed; }
                    th, td { border: 1px solid #bbb; padding: 3px 4px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; width: ${colW}; }
                    thead th { background: #dde6f5; font-weight: bold; }
                    tr:nth-child(even) td { background: #f5f8ff; }
                  </style></head><body>`;
                  html += `<h3>${activeTab?.title || "Sheet"}</h3>`;
                  html += `<table><thead><tr>`;
                  cols.forEach(c => { html += `<th>${c}</th>`; });
                  html += `</tr></thead><tbody>`;
                  visibleRows.forEach(({ row }) => {
                    html += `<tr>`;
                    cols.forEach(c => {
                      const v = typeof row[c] === "object" ? (row[c]?.value ?? "") : (row[c] ?? "");
                      html += `<td>${v}</td>`;
                    });
                    html += `</tr>`;
                  });
                  html += `</tbody></table></body></html>`;
                  const w = window.open("", "_blank");
                  w.document.write(html);
                  w.document.close();
                  setTimeout(() => w.print(), 500);
                }} className={`px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase transition-all ${isDark ? "bg-slate-700 text-slate-300 hover:bg-slate-600" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}>🖨 Print</button>
              </div>

              <div className="flex items-center gap-1">
                {ENABLE_AI_TABLE && (
                  <button
                    onClick={() => setShowAITable(true)}
                    disabled={aiBuildLoading}
                    className="px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase bg-gradient-to-r from-[#2563eb] to-[#0ea5e9] hover:from-[#1d4ed8] hover:to-[#0284c7] text-white transition-all duration-150 shadow-[0_10px_24px_rgba(37,99,235,0.24)] disabled:opacity-50"
                  >
                    {aiBuildLoading ? "Building" : "AI Table"}
                  </button>
                )}
                <button onClick={() => setShowAIAnalysis(true)}
                  className="px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase bg-sky-500/10 hover:bg-sky-500 border border-sky-500/20 hover:border-sky-500 text-sky-400 hover:text-white transition-all duration-150">
                  AI
                </button>
                {!isPremium && (
                  <button onClick={() => setShowUpgrade(true)}
                    className="px-2.5 py-1.5 rounded-lg font-black text-[9px] uppercase bg-gradient-to-r from-pink-600 to-red-500 hover:from-pink-500 hover:to-red-400 text-white transition-all duration-150 shadow-sm shadow-pink-900/20">
                    Upgrade
                  </button>
                )}
              </div>
            </div>
          </header>

          <div className={`${bgPanel} border ${borderColor} rounded-[1.5rem] px-4 py-3 shadow-xl`}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {workspaceStats.map((stat) => (
                <div key={stat.label} className={`min-w-0 ${isDark ? "md:border-l md:first:border-l-0 md:border-[#1e3a5f]" : "md:border-l md:first:border-l-0 md:border-slate-200"} md:pl-4 md:first:pl-0`}>
                  <p className={`text-[8px] font-black uppercase tracking-[0.22em] ${textSub}`}>{stat.label}</p>
                  <p className={`mt-1 truncate text-sm font-black ${stat.accent}`}>{stat.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ===== SHEET TABS ===== */}
          <div className="flex items-end gap-1 overflow-x-auto" style={{ scrollbarWidth: "none", paddingBottom: 0 }}>
            {tabs.map((tab) => {
              const isActive  = activeTabId === tab.id;
              const tabColor  = tab.tabColor;
              return (
                <div
                  key={tab.id}
                  onClick={() => switchTab(tab.id)}
                  className={`group relative flex items-center gap-2 px-4 py-3 rounded-t-2xl cursor-pointer transition-all duration-150 min-w-[110px] max-w-[180px] border-t border-x select-none ${
                    isActive
                      ? `${bgPanel} ${borderColor} ${textMain} shadow-[0_12px_30px_rgba(37,99,235,0.14)]`
                      : `border-transparent ${textSub} hover:${isDark ? "text-slate-300" : "text-slate-600"}`
                  }`}
                  style={tabColor
                    ? { borderTopColor: tabColor + "80", borderLeftColor: tabColor + "30", borderRightColor: tabColor + "30" }
                    : {}}
                >
                  {tabColor && <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tabColor }} />}
                  <span className="text-[10px] font-black uppercase tracking-widest truncate flex-1">{tab.title}</span>

                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={e => { e.stopPropagation(); setEditingFormat({ col: "", rowIdx: null, scope: "tab", tabId: tab.id }); }}
                      className={`w-5 h-5 rounded flex items-center justify-center text-[9px] hover:text-indigo-400 transition-colors ${textSub}`}
                      title="Tab color">
                      🎨
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        const dupTab = {
                          ...JSON.parse(JSON.stringify(tab)),
                          id: `tab_${Date.now()}`,
                          title: `${tab.title} (Copy)`,
                        };
                        setTabs(prev => [...prev, dupTab]);
                        setActiveTabId(dupTab.id);
                      }}
                      className={`w-5 h-5 rounded flex items-center justify-center text-[9px] hover:text-emerald-400 transition-colors ${textSub}`}
                      title="Duplicate table">
                      ⧉
                    </button>
                    {tabs.length > 1 && (
                      <button onClick={e => closeTab(e, tab.id)}
                        className={`w-5 h-5 rounded flex items-center justify-center text-[10px] hover:text-red-400 transition-colors ${textSub}`}>
                        ✕
                      </button>
                    )}
                  </div>
                  {/* Collaborator avatars */}
                  {isActive && Object.keys(collaborators).length > 0 && (
                    <div className="flex -space-x-1 mr-1">
                      {Object.values(collaborators).slice(0, 3).map((c, i) => (
                        <div key={i}
                          className="w-4 h-4 rounded-full border border-white/20 flex items-center justify-center text-[6px] font-black text-white"
                          style={{ backgroundColor: c.color }}
                          title={c.name}>
                          {c.name?.[0] || "?"}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Chat toggle বাটন — শুধু শেয়ার করা টেবিলে দেখাবে */}
                  {isActive && shareId && Object.keys(stickyNotes).length > 0 && (
                    <button
                      onClick={e => { e.stopPropagation(); setShowNotesList(v => !v); }}
                      title="All Sticky Notes"
                      className={`relative w-5 h-5 rounded flex items-center justify-center text-[10px] mr-1 transition-colors ${
                        showNotesList ? "text-amber-400" : `${textSub} hover:text-amber-400`
                      }`}>
                      📌
                      <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-amber-500 text-black text-[8px] font-black flex items-center justify-center">
                        {Object.keys(stickyNotes).length}
                      </span>
                    </button>
                  )}
                  {isActive && shareId && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setShowChatSidebar(v => {
                          if (!v) setLastReadChatTime(Date.now());
                          return !v;
                        });
                      }}
                      title="Team Chat"
                      className={`relative w-5 h-5 rounded flex items-center justify-center text-[10px] mr-1 transition-colors ${
                        showChatSidebar ? "text-sky-400" : `${textSub} hover:text-sky-400`
                      }`}>
                      💬
                      {(() => {
                        const unreadCount = chatMessages.filter(m => m.time > lastReadChatTime && m.uid !== user?.uid).length;
                        return unreadCount > 0 && !showChatSidebar && (
                          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-white text-[8px] font-black flex items-center justify-center">
                            {unreadCount > 9 ? "9+" : unreadCount}
                          </span>
                        );
                      })()}
                    </button>
                  )}

                  {isActive && (
                    <div className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full"
                      style={{ background: tabColor
                        ? `linear-gradient(to right, ${tabColor}, ${tabColor}88)`
                        : "linear-gradient(to right, #6366f1, #38bdf8, #34d399)" }} />
                  )}
                </div>
              );
            })}

            {/* Add tab button */}
            <button
              onClick={() => {
                const check = canCreateTable();
                if (!check.allowed) {
                  setProGateMessage(check.reason);
                  setShowProGate(true);
                } else {
                  setShowTemplates(true);
                }
              }}
              className={`px-4 py-3 rounded-t-2xl font-black text-sm transition-all duration-150 ${
                isDark
                  ? "text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"
                  : "text-emerald-600 hover:bg-emerald-50"
              } ${(!isPremium && lifetimeTablesCreated >= FREE_TABLE_LIMIT) ? "opacity-40 cursor-not-allowed" : ""}`}
              title={!isPremium ? `Free Tables: ${freeTablesRemaining}/${FREE_TABLE_LIMIT}` : "New Sheet"}
            >
              {(!isPremium && lifetimeTablesCreated >= FREE_TABLE_LIMIT) ? `Limit` : `+`}
            </button>
          </div>

          {/* ===== MAIN SHEET PANEL ===== */}
          <div className={`${bgPanel} border ${borderColor} rounded-b-[2rem] rounded-tr-[2rem] shadow-xl`} style={{ borderTopWidth: 0 }}>
            <div className="px-3 py-2">

              {/* PANEL TOGGLE */}
              <div className="flex justify-end mb-1">
                <button onClick={() => setShowTopPanel(p => !p)}
                  className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-lg border transition-all ${isDark ? "border-[#1e3a5f] text-slate-500 hover:text-slate-300 hover:bg-white/5" : "border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50"}`}>
                  {showTopPanel ? "▲ Hide Tools" : "▼ Show Tools"}
                </button>
              </div>
              {showTopPanel && <div>
              {/* TOOLS ROW — compact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                {/* AI Generator */}
                <div className={`relative ${bgSubtle} border ${borderColor} px-3 py-2 rounded-xl`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse"></div>
                    <label className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.2em]">AI Generator</label>
                  </div>
                  <div className="flex gap-2">
                    <input type="text" className={`flex-1 border rounded-lg px-3 py-1.5 outline-none text-xs transition-all ${inputCls}`}
                      placeholder="e.g. 'Payroll 15 rows' or 'Student List'"
                      value={genText} onChange={e => setGenText(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleGenerateTable()} />
                    <button type="button"
                      onClick={() => startVoiceInput("gen", setGenText, genText)}
                      title={listeningField === "gen" ? "শোনা বন্ধ করো" : "ভয়েস দিয়ে বলো"}
                      className={`px-2.5 py-1.5 rounded-lg font-black text-[11px] transition-all flex-shrink-0 ${
                        listeningField === "gen"
                          ? "bg-red-600 text-white animate-pulse"
                          : isDark ? "bg-white/10 text-slate-300 hover:bg-white/20" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}>
                      🎤
                    </button>
                    <button type="button"
                      onClick={() => setVoiceLang(v => v === "bn-BD" ? "en-US" : "bn-BD")}
                      title="ভয়েস ভাষা বদলাও"
                      className={`px-2 py-1.5 rounded-lg text-[9px] font-black uppercase flex-shrink-0 ${isDark ? "bg-white/5 text-slate-400 hover:bg-white/10" : "bg-slate-50 text-slate-500 hover:bg-slate-100"}`}>
                      {voiceLang === "bn-BD" ? "বাং" : "EN"}
                    </button>
                    <button onClick={handleGenerateTable} disabled={isLoading || !genText}
                      className={`px-3 py-1.5 rounded-lg font-black text-[9px] uppercase tracking-widest transition-all flex items-center gap-1 ${
                        isLoading || !genText
                          ? "opacity-50 cursor-not-allowed bg-indigo-600/50 text-white"
                          : "bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white"
                      }`}>
                      {isLoading ? <><div className="w-2.5 h-2.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Building...</> : "Build"}
                    </button>
                  </div>
                  <p className={`mt-1.5 text-[9.5px] ${textSub}`}>
                    Try: <span className="italic">"Payroll 15 rows"</span> · <span className="italic"></span> · <span className="italic"></span>
                  </p>
                </div>

                {/* Calc Engine */}
                <div className={`${bgSubtle} border ${borderColor} px-3 py-2 rounded-xl`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                    <label className="text-[9px] font-black text-emerald-400 uppercase tracking-[0.2em]">Calc Engine</label>
                  </div>
                  <div className="flex gap-2">
                    <button type="button"
                      onClick={() => startVoiceInput("calc", setInputText, inputText)}
                      title={listeningField === "calc" ? "শোনা বন্ধ করো" : "ভয়েস দিয়ে বলো"}
                      className={`px-2.5 py-1.5 rounded-lg font-black text-[11px] transition-all flex-shrink-0 ${
                        listeningField === "calc"
                          ? "bg-red-600 text-white animate-pulse"
                          : isDark ? "bg-white/10 text-slate-300 hover:bg-white/20" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}>
                      🎤
                    </button>
                    <input type="text" className={`flex-1 border rounded-lg px-3 py-1.5 outline-none text-xs transition-all ${inputCls}`}
                      placeholder="Formula, sort, or search... (e.g. Item sort ascending)"
                      value={inputText} onChange={e => setInputText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key !== "Enter") return;
                        const lowerCmd = inputText.toLowerCase();
                        if (lowerCmd.includes("khali cell") || lowerCmd.includes("empty cell") || lowerCmd.includes("খালি সেল")) {
                          setHighlightEmpty(true);
                          setInputText("");
                          return;
                        }
                        if (lowerCmd.includes("hide empty") || lowerCmd.includes("highlight bondho")) {
                          setHighlightEmpty(false);
                          setDuplicateCol(null);
                          setInputText("");
                          return;
                        }
                        const daysCmd = parseDaysBetween(inputText, columns);
                        if (daysCmd) {
                          const resultColName = daysCmd.useToday ? `${daysCmd.colA} Days` : `${daysCmd.colA} ${daysCmd.colB} Days`;
                          const newColumnsList = columns.includes(resultColName) ? columns : [...columns, resultColName];
                          const updatedRows = rows.map((row) => {
                            const rawA = String(cellVal(row[daysCmd.colA])).trim();
                            const rawB = daysCmd.useToday ? "today" : String(cellVal(row[daysCmd.colB])).trim();
                            let diffDays = "";
                            if (rawA && rawB) {
                              const dateA = new Date(rawA);
                              const dateB = daysCmd.useToday ? new Date() : new Date(rawB);
                              if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                                diffDays = Math.round((dateB - dateA) / (1000 * 60 * 60 * 24));
                              }
                            }
                            return { ...row, [resultColName]: makeCellObj(diffDays) };
                          });
                          updateActiveTab({ columns: newColumnsList, rows: updatedRows });
                          setInputText("");
                          return;
                        }
                        const percentCmd = parsePercentOfTotal(inputText, columns);
                        if (percentCmd) {
                          const total = rows.reduce((sum, r) => sum + (parseFloat(String(cellVal(r[percentCmd.column])).replace(/[^0-9.]/g, "")) || 0), 0);
                          const resultColName = `${percentCmd.column} %`;
                          const newColumnsList = columns.includes(resultColName) ? columns : [...columns, resultColName];
                          const updatedRows = rows.map((row) => {
                            const v = parseFloat(String(cellVal(row[percentCmd.column])).replace(/[^0-9.]/g, "")) || 0;
                            const pct = total ? ((v / total) * 100).toFixed(1) + "%" : "0%";
                            return { ...row, [resultColName]: makeCellObj(pct) };
                          });
                          updateActiveTab({ columns: newColumnsList, rows: updatedRows });
                          setInputText("");
                          return;
                        }
                        const groupCmd = parseGroupCount(inputText, columns);
                        if (groupCmd) {
                          const counts = {};
                          rows.forEach((row) => {
                            const v = String(cellVal(row[groupCmd.column])).trim();
                            if (v) counts[v] = (counts[v] || 0) + 1;
                          });
                          const summary = Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(", ");
                          setCalculationResult({
                            label: "Group Count",
                            formula: `GROUP(${groupCmd.column})`,
                            value: summary || "No data",
                            count: Object.keys(counts).length,
                            columnsUsed: [],
                          });
                          setInputText("");
                          return;
                        }
                        const validateCmd = parseValidateCommand(inputText, columns);
                        if (validateCmd) {
                          setValidateCol(validateCmd);
                          setInputText("");
                          return;
                        }
                        const condRule = parseConditionalRule(inputText, columns);
                        if (condRule) {
                          setConditionalRules(prev => [...prev, condRule]);
                          setInputText("");
                          return;
                        }
                        const splitCmd = parseSplitColumn(inputText, columns);
                        if (splitCmd) {
                          const col1 = `${splitCmd.column} 1`;
                          const col2 = `${splitCmd.column} 2`;
                          const newColumnsList = [...columns];
                          if (!newColumnsList.includes(col1)) newColumnsList.push(col1);
                          if (!newColumnsList.includes(col2)) newColumnsList.push(col2);
                          const updatedRows = rows.map((row) => {
                            const val = cellVal(row[splitCmd.column]);
                            const parts = val.split(splitCmd.delimiter);
                            return {
                              ...row,
                              [col1]: makeCellObj((parts[0] || "").trim()),
                              [col2]: makeCellObj((parts.slice(1).join(splitCmd.delimiter) || "").trim()),
                            };
                          });
                          updateActiveTab({ columns: newColumnsList, rows: updatedRows });
                          setInputText("");
                          return;
                        }
                        const mergeCmd = parseMergeColumns(inputText, columns);
                        if (mergeCmd) {
                          const newColumnsList = columns.includes(mergeCmd.newColName) ? columns : [...columns, mergeCmd.newColName];
                          const updatedRows = rows.map((row) => {
                            const a = cellVal(row[mergeCmd.colA]);
                            const b = cellVal(row[mergeCmd.colB]);
                            return { ...row, [mergeCmd.newColName]: makeCellObj(`${a} ${b}`.trim()) };
                          });
                          updateActiveTab({ columns: newColumnsList, rows: updatedRows });
                          setInputText("");
                          return;
                        }
                        const serialCmd = parseAutoSerial(inputText, columns);
                        if (serialCmd && serialCmd.column) {
                          const updatedRows = rows.map((row, i) => ({ ...row, [serialCmd.column]: makeCellObj(i + 1) }));
                          updateActiveTab({ rows: updatedRows });
                          setInputText("");
                          return;
                        }
                        const frCmd = parseFindReplace(inputText, columns);
                        if (frCmd) {
                          const targetCols = frCmd.column ? [frCmd.column] : columns;
                          const updatedRows = rows.map((row) => {
                            const patched = { ...row };
                            targetCols.forEach((c) => {
                              const cur = cellVal(row[c]);
                              if (cur.includes(frCmd.find)) {
                                const existing = typeof row[c] === "object" ? row[c] : {};
                                patched[c] = { ...existing, value: cur.split(frCmd.find).join(frCmd.replaceWith) };
                              }
                            });
                            return patched;
                          });
                          updateActiveTab({ rows: updatedRows });
                          setInputText("");
                          return;
                        }
                        const colorInstructions = parseColorCommands(inputText);
                        if (colorInstructions.length > 0) {
                          colorInstructions.forEach((instr) => {
                            if (instr.type === "column") {
                              const col = columns[instr.index - 1];
                              if (col) applyColumnFormat(col, { ...(columnFormats[col] || {}), bgColor: instr.color });
                            } else if (instr.type === "row") {
                              const rIdx = instr.index - 1;
                              if (rows[rIdx]) {
                                columns.forEach((col) => {
                                  const existing = rows[rIdx][col];
                                  const existingFmt = typeof existing === "object" ? existing : {};
                                  applyCellFormat(rIdx, col, { ...existingFmt, bgColor: instr.color });
                                });
                              }
                            }
                          });
                          setInputText("");
                          return;
                        }
                        if (lowerCmd.includes("duplicate") || lowerCmd.includes("ডুপ্লিকেট")) {
                          if (lowerCmd.includes("remove") || lowerCmd.includes("soro") || lowerCmd.includes("delete") || lowerCmd.includes("বাদ")) {
                            const seen = new Set();
                            const dedupedRows = rows.filter((row) => {
                              const key = columns.map(c => String(cellVal(row[c])).trim().toLowerCase()).join("|");
                              const isBlankRow = columns.every(c => !String(cellVal(row[c])).trim());
                              if (isBlankRow) return true;
                              if (seen.has(key)) return false;
                              seen.add(key);
                              return true;
                            });
                            updateActiveTab({ rows: dedupedRows });
                            setInputText("");
                            return;
                          }
                          const targetCol = columns.find(c => lowerCmd.includes(c.toLowerCase()));
                          if (targetCol) {
                            setDuplicateCol(targetCol);
                            setInputText("");
                            return;
                          }
                        }
                        const cmd = runAICommand(inputText, { columns });
                        if (cmd.type === "sort_column") {
                          const sortedRows = [...rows].sort((a, b) => {
                            const av = cellVal(a[cmd.column]);
                            const bv = cellVal(b[cmd.column]);
                            const an = parseFloat(av), bn = parseFloat(bv);
                            let cmp;
                            if (!isNaN(an) && !isNaN(bn)) cmp = an - bn;
                            else cmp = av.localeCompare(bv);
                            return cmd.direction === "desc" ? -cmp : cmp;
                          });
                          updateActiveTab({ rows: sortedRows });
                          setInputText("");
                        } else if (cmd.type === "search_filter") {
                          setSearchTerm(cmd.query);
                          setInputText("");
                        }
                      }} />
                    {calculationResult && (
                      <div className={`px-2 py-1 rounded-lg border flex items-center gap-2 ${isDark ? "bg-emerald-500/5 border-emerald-500/20" : "bg-emerald-50 border-emerald-200"}`}>
                        <h3 className="text-sm font-black text-emerald-400">{calculationResult.value}</h3>
                      </div>
                    )}
                    {calculationResult && calculationResult.columnsUsed?.length >= 2 && (
                      <button
                        onClick={() => {
                          const resultColName = `${calculationResult.columnsUsed[0]} ${calculationResult.label}`;
                          const newColumnsList = columns.includes(resultColName) ? columns : [...columns, resultColName];
                          const [colA, colB] = calculationResult.columnsUsed;
                          const updatedRows = rows.map((row) => {
                            const a = parseFloat(String(cellVal(row[colA])).replace(/[^0-9.]/g, "")) || 0;
                            const b = parseFloat(String(cellVal(row[colB])).replace(/[^0-9.]/g, "")) || 0;
                            return { ...row, [resultColName]: makeCellObj(calculationResult.fn(a, b)) };
                          });
                          updateActiveTab({ columns: newColumnsList, rows: updatedRows });
                        }}
                        className="px-2 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wide bg-indigo-600 hover:bg-indigo-500 text-white transition-all flex-shrink-0"
                        title="Puro column hisebe shob row-e save koro"
                      >
                        + Add as Column
                      </button>
                    )}
                    {calculationResult && calculationResult.aggColumn && (
                      <button
                        onClick={() => {
                          const totalRow = makeBlankRow(columns, rows.length + 1);
                          totalRow[columns[0]] = makeCellObj("Total");
                          totalRow[calculationResult.aggColumn] = makeCellObj(calculationResult.aggValueRaw);
                          updateActiveTab({ rows: [...rows, totalRow] });
                        }}
                        className="px-2 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wide bg-amber-600 hover:bg-amber-500 text-white transition-all flex-shrink-0"
                        title="Shobar niche ekta Total row hisebe add koro"
                      >
                        + Add Total Row
                      </button>
                    )}
                  </div>
                  <p className={`mt-1.5 text-[9.5px] ${textSub}`}>
                     Try: <span className="italic">"Price × Qty"</span> · <span className="italic">"Name sort ascending"</span> (Enter) · <span className="italic"></span> (Enter)
                  </p>
                </div>
              </div>

              {/* ANALYTICS TOGGLE */}
              <div className="mb-5">
                <button onClick={() => setShowAnalytics(a => !a)}
                  className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl border transition-all duration-150 ${btnGhost}`}>
                  {showAnalytics ? "▲ Hide" : "▼ Show"} Analytics
                </button>
                {showAnalytics && analyticsData && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`${bgSubtle} border ${borderColor} p-5 rounded-[1.5rem]`}>
                      <p className={`text-[9px] font-black uppercase tracking-widest ${textSub} mb-3`}>Bar Chart · {analyticsData.col}</p>
                      <MiniBarChart data={analyticsData.vals} labels={analyticsData.labels} color={isDark ? "#6366f1" : "#4f46e5"} />
                    </div>
                    <div className={`${bgSubtle} border ${borderColor} p-5 rounded-[1.5rem]`}>
                      <p className={`text-[9px] font-black uppercase tracking-widest ${textSub} mb-3`}>Pie Chart · {analyticsData.col}</p>
                      <MiniPieChart data={analyticsData.vals} labels={analyticsData.labels} />
                    </div>
                  </div>
                )}
                {showAnalytics && !analyticsData && (
                  <div className={`mt-4 ${bgSubtle} border ${borderColor} p-5 rounded-[1.5rem] text-center`}>
                    <p className={`text-xs ${textSub}`}>Add numeric columns to see analytics.</p>
                  </div>
                )}
              </div>
              </div>}
              {/* ===== FORMULA BAR ===== */}
              {showFormulaBar && (
                <div className={`mb-3 flex items-center gap-2 px-3 py-2 rounded-xl border ${borderColor} ${isDark ? "bg-[#070f1e]" : "bg-white shadow-sm"}`} style={{ position: "sticky", top: 0, zIndex: 50 }}>
                  <div className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest flex-shrink-0 ${isDark ? "bg-[#0a1628] text-blue-400 border border-[#1e3a5f]" : "bg-blue-50 text-blue-600 border border-blue-200"}`}
                    style={{ minWidth: 60, textAlign: "center" }}>
                    {selectedCell ? `${selectedCell.col}${selectedCell.rIdx + 1}` : "fx"}
                  </div>
                  <span className={`text-sm font-black ${isDark ? "text-blue-400" : "text-blue-600"} flex-shrink-0`}>=</span>
                  <input
                    type="text"
                    value={formulaBarVal}
                    onChange={e => {
                      setFormulaBarVal(e.target.value);
                      if (selectedCell) handleCellChange(selectedCell.rIdx, selectedCell.col, e.target.value);
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" && selectedCell) {
                        handleCellChange(selectedCell.rIdx, selectedCell.col, formulaBarVal);
                        e.target.blur();
                      }
                      if (e.key === "Escape") {
                        setFormulaBarVal(selectedCell ? cellVal(rows[selectedCell.rIdx]?.[selectedCell.col]) : "");
                        e.target.blur();
                      }
                    }}
                    placeholder={selectedCell ? "Type value or =SUM(A1:A5)" : "Click a cell to edit..."}
                    className={`flex-1 bg-transparent outline-none text-sm font-mono ${isDark ? "text-slate-200 placeholder:text-slate-700" : "text-slate-800 placeholder:text-slate-400"}`}
                  />
                  {formulaBarVal.startsWith("=") && (
                    <span className={`text-[9px] font-black px-2 py-1 rounded-lg flex-shrink-0 ${isDark ? "bg-emerald-500/10 text-emerald-400" : "bg-emerald-50 text-emerald-600"}`}>
                      FORMULA
                    </span>
                  )}
                </div>
              )}
              {/* ===== FORMATTING TOOLBAR ===== */}
              {(() => {
                const fmt = getSelectedCellFormat();
                const isBold       = fmt.fontWeight     === "bold";
                const isItalic     = fmt.fontStyle      === "italic";
                const isUnderline  = fmt.textDecoration === "underline";
                const isLeft       = fmt.textAlign      === "left"   || !fmt.textAlign;
                const isCenter     = fmt.textAlign      === "center";
                const isRight      = fmt.textAlign      === "right";
 
                const tbBtn = (active, onClick, children, tooltip) => (
                  <button
                    onClick={onClick}
                    title={tooltip}
                    className={`h-8 px-2.5 rounded-lg text-xs font-black transition-all duration-150 flex items-center justify-center min-w-[32px] ${
                      active
                        ? isDark
                          ? "bg-blue-600 text-white shadow-sm shadow-blue-900/40"
                          : "bg-blue-600 text-white shadow-sm"
                        : isDark
                          ? "text-slate-400 hover:bg-white/8 hover:text-slate-200"
                          : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    }`}
                  >
                    {children}
                  </button>
                );
 
                return (
                  <div className={`mb-4 flex items-center gap-1 px-3 py-2 rounded-2xl border ${borderColor} ${isDark ? "bg-[#0a1628]" : "bg-white shadow-md"} flex-wrap`} style={{ position: "sticky", top: showFormulaBar ? 48 : 0, zIndex: 49 }}>

                    {tbBtn(false, () => handleUndo(),
                      <span style={{ fontSize: 11, fontWeight: 900 }}>↩</span>, "Undo (Ctrl+Z)"
                    )}
                    {tbBtn(false, () => handleRedo(),
                      <span style={{ fontSize: 11, fontWeight: 900 }}>↪</span>, "Redo (Ctrl+Y)"
                    )}
                    {tbBtn(showFindReplace, () => setShowFindReplace(f => !f),
                      <span style={{ fontSize: 11, fontWeight: 900 }}>🔍</span>, "Find & Replace (Ctrl+H)"
                    )}
                    {tbBtn(false, () => autoFitAllRows(),
                      <span style={{ fontSize: 10, fontWeight: 900 }}>Auto H</span>, "Auto Fit Row Height"
                    )}
                    <select value={frozenCols} onChange={e => setFrozenCols(Number(e.target.value))}
                      className={`h-8 px-2 rounded-lg text-[10px] font-black border outline-none ${isDark ? "bg-[#0a1628] border-[#1e3a5f] text-slate-300" : "bg-white border-slate-200 text-slate-700 shadow-sm"}`}>
                      <option value={0}>No Freeze</option>
                      <option value={1}>Freeze 1</option>
                      <option value={2}>Freeze 2</option>
                      <option value={3}>Freeze 3</option>
                    </select>
                    <div className={`w-px h-6 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />
 
                    {/* Cell indicator */}
                    <div className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest mr-2 ${
                      selectedCell
                        ? isDark ? "bg-blue-600/20 text-blue-400" : "bg-blue-50 text-blue-600"
                        : isDark ? "bg-slate-800 text-slate-600" : "bg-slate-100 text-slate-400"
                    }`}>
                      {selectedCell ? `${selectedCell.col} · R${selectedCell.rIdx + 1}` : "No cell"}
                    </div>
 
                    {/* Divider */}
                    <div className={`w-px h-6 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />
 
                    {/* Font Size */}
                    <select
                      value={fmt.fontSize || 14}
                      onChange={e => applyToolbarFormat({ fontSize: Number(e.target.value) })}
                      disabled={!selectedCell}
                      className={`h-8 px-2 rounded-lg text-[11px] font-black border outline-none transition-all cursor-pointer ${
                        isDark
                          ? "bg-[#0a1628] border-[#1e3a5f] text-slate-300 disabled:opacity-30"
                          : "bg-white border-slate-200 text-slate-700 disabled:opacity-30 shadow-sm"
                      }`}
                    >
                      {[10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24].map(s => (
                        <option key={s} value={s}>{s}px</option>
                      ))}
                    </select>
 
                    {/* Divider */}
                    <div className={`w-px h-6 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />
 
                    {/* Bold */}
                    {tbBtn(isBold, () => applyToolbarFormat({ fontWeight: isBold ? "normal" : "bold" }),
                      <span style={{ fontWeight: 900, fontSize: 13 }}>B</span>
                    )}
 
                    {/* Italic */}
                    {tbBtn(isItalic, () => applyToolbarFormat({ fontStyle: isItalic ? "normal" : "italic" }),
                      <span style={{ fontStyle: "italic", fontWeight: 700, fontSize: 13 }}>I</span>
                    )}
 
                    {/* Underline */}
                    {tbBtn(isUnderline, () => applyToolbarFormat({ textDecoration: isUnderline ? "none" : "underline" }),
                      <span style={{ textDecoration: "underline", fontWeight: 700, fontSize: 13 }}>U</span>
                    )}
 
                    {/* Divider */}
                    <div className={`w-px h-6 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />
 
                    {/* Align Left */}
                    {tbBtn(isLeft, () => applyToolbarFormat({ textAlign: "left" }),
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                        <rect x="0" y="1" width="14" height="2" rx="1"/>
                        <rect x="0" y="5" width="10" height="2" rx="1"/>
                        <rect x="0" y="9" width="14" height="2" rx="1"/>
                        <rect x="0" y="13" width="8" height="2" rx="1"/>
                      </svg>
                    )}
 
                    {/* Align Center */}
                    {tbBtn(isCenter, () => applyToolbarFormat({ textAlign: "center" }),
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                        <rect x="0" y="1" width="14" height="2" rx="1"/>
                        <rect x="2" y="5" width="10" height="2" rx="1"/>
                        <rect x="0" y="9" width="14" height="2" rx="1"/>
                        <rect x="3" y="13" width="8" height="2" rx="1"/>
                      </svg>
                    )}
 
                    {/* Align Right */}
                    {tbBtn(isRight, () => applyToolbarFormat({ textAlign: "right" }),
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                        <rect x="0" y="1" width="14" height="2" rx="1"/>
                        <rect x="4" y="5" width="10" height="2" rx="1"/>
                        <rect x="0" y="9" width="14" height="2" rx="1"/>
                        <rect x="6" y="13" width="8" height="2" rx="1"/>
                      </svg>
                    )}
 
                    {/* Divider */}
                    <div className={`w-px h-6 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />

                    {/* Wrap Text */}
                    {tbBtn(fmt.whiteSpace === "normal", () => applyToolbarFormat({ whiteSpace: fmt.whiteSpace === "normal" ? "nowrap" : "normal" }),
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                        <rect x="0" y="1" width="14" height="2" rx="1"/>
                        <rect x="0" y="5" width="14" height="2" rx="1"/>
                        <path d="M0 10h9.5a2.5 2.5 0 010 5H8m2-2l-2 2 2 2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                      </svg>
                    )}

                    {/* Divider */}
                    <div className={`w-px h-6 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />

                    {/* Font Color */}
                    <div className="relative flex items-center">
                      <label className={`h-8 px-2 rounded-lg flex items-center gap-1.5 cursor-pointer transition-all text-[10px] font-black ${
                        isDark ? "text-slate-400 hover:bg-white/8 hover:text-slate-200" : "text-slate-500 hover:bg-slate-100"
                      } ${!selectedCell ? "opacity-30 pointer-events-none" : ""}`}>
                        <span>A</span>
                        <div className="w-4 h-1.5 rounded-full" style={{ backgroundColor: fmt.color || (isDark ? "#e2e8f0" : "#0f172a") }} />
                        <input type="color"
                          value={fmt.color || (isDark ? "#e2e8f0" : "#0f172a")}
                          onChange={e => applyToolbarFormat({ color: e.target.value })}
                          className="absolute opacity-0 w-0 h-0" />
                      </label>
                    </div>
 
                    {/* Bg Color */}
                    <div className="relative flex items-center">
                      <label className={`h-8 px-2 rounded-lg flex items-center gap-1.5 cursor-pointer transition-all text-[10px] font-black ${
                        isDark ? "text-slate-400 hover:bg-white/8 hover:text-slate-200" : "text-slate-500 hover:bg-slate-100"
                      } ${!selectedCell ? "opacity-30 pointer-events-none" : ""}`}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                          <rect width="12" height="12" rx="2"/>
                        </svg>
                        <div className="w-4 h-1.5 rounded-full border" style={{
                          backgroundColor: fmt.bgColor || "transparent",
                          borderColor: isDark ? "#334155" : "#e2e8f0"
                        }} />
                        <input type="color"
                          value={fmt.bgColor || "#ffffff"}
                          onChange={e => applyToolbarFormat({ bgColor: e.target.value })}
                          className="absolute opacity-0 w-0 h-0" />
                      </label>
                    </div>
 
                    {/* Number Format */}
                    <select
                      disabled={!selectedCell}
                      value={fmt.numberFormat || "none"}
                      onChange={e => applyToolbarFormat({ numberFormat: e.target.value })}
                      className={`h-8 px-2 rounded-lg text-[10px] font-black border outline-none transition-all ${isDark ? "bg-[#0a1628] border-[#1e3a5f] text-slate-300" : "bg-white border-slate-200 text-slate-600"} ${!selectedCell ? "opacity-30 pointer-events-none" : ""}`}
                      title="Number Format"
                    >
                      <option value="none">123</option>
                      <option value="currency">$ USD</option>
                      <option value="percent">% PCT</option>
                      <option value="decimal2">.00</option>
                      <option value="integer">#</option>
                    </select>

                    {/* Insert Row */}
                    <button
                      onClick={() => addRow()}
                      className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ${isDark ? "text-slate-400 hover:bg-white/8 hover:text-emerald-400" : "text-slate-500 hover:bg-slate-100 hover:text-emerald-600"}`}
                      title="Insert Row"
                    >+R</button>

                    {/* Insert Column */}
                    <button
                      onClick={() => addColumn()}
                      className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ${isDark ? "text-slate-400 hover:bg-white/8 hover:text-emerald-400" : "text-slate-500 hover:bg-slate-100 hover:text-emerald-600"}`}
                      title="Insert Column"
                    >+C</button>

                    {/* Divider */}
                    <div className={`w-px h-6 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />

                    {/* Auto Number */}
                    <button onClick={autoNumberRows}
                      className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ${isDark ? "text-slate-400 hover:bg-white/8 hover:text-slate-200" : "text-slate-500 hover:bg-slate-100"}`}
                      title="Auto Number">№</button>

                    {/* Clean Spaces */}
                    <button onClick={cleanData}
                      className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ${isDark ? "text-slate-400 hover:bg-white/8 hover:text-slate-200" : "text-slate-500 hover:bg-slate-100"}`}
                      title="Clean Spaces">⌥</button>

                    {/* Remove Empty */}
                    <button onClick={removeEmptyRows}
                      className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ${isDark ? "text-slate-400 hover:bg-white/8 hover:text-red-400" : "text-slate-500 hover:bg-slate-100 hover:text-red-500"}`}
                      title="Remove Empty Rows">✕R</button>

                    {/* Clear Format */}
                    <button
                      onClick={() => {
                        if (!selectedCell) return;
                        applyToolbarFormat({ color: "", bgColor: "", fontSize: 14, fontWeight: "normal", fontStyle: "normal", textAlign: "left", textDecoration: "none" });
                      }}
                      disabled={!selectedCell}
                      className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ml-1 ${
                        isDark
                          ? "text-red-400/60 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-20"
                          : "text-red-400 hover:bg-red-50 disabled:opacity-20"
                      }`}
                      title="Clear all formatting"
                    >
                      Clear
                    </button>

                    <div className={`w-px h-6 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />

                    {selectionStart && selectionEnd && !(selectionStart.rIdx === selectionEnd.rIdx && selectionStart.cIdx === selectionEnd.cIdx) && (
                      <div className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase ${
                        isDark ? "bg-blue-600/20 text-blue-400" : "bg-blue-50 text-blue-600"
                      }`}>
                        {Math.abs(selectionEnd.rIdx - selectionStart.rIdx) + 1} × {Math.abs(selectionEnd.cIdx - selectionStart.cIdx) + 1} selected
                      </div>
                    )}

                    {selectionStart && selectionEnd && !(selectionStart.rIdx === selectionEnd.rIdx && selectionStart.cIdx === selectionEnd.cIdx) && (
                      <button
                        onClick={() => applyMultiCellFormat({ fontWeight: fmt.fontWeight, color: fmt.color, bgColor: fmt.bgColor, fontSize: fmt.fontSize })}
                        className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ${
                          isDark ? "bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white" : "bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white"
                        }`}>
                        Apply All
                      </button>
                    )}

                    <button
                      onClick={() => setShowBorderModal(true)}
                      disabled={!selectedCell}
                      className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ${
                        isDark ? "text-slate-400 hover:bg-white/8 hover:text-slate-200 disabled:opacity-30" : "text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                      }`}>
                      Border
                    </button>

                    <button
                      onClick={() => setShowConditional(true)}
                      className={`h-8 px-2.5 rounded-lg text-[10px] font-black transition-all ${
                        conditionalRules.length > 0
                          ? isDark ? "bg-purple-600/20 text-purple-400" : "bg-purple-50 text-purple-600"
                          : isDark ? "text-slate-400 hover:bg-white/8 hover:text-slate-200" : "text-slate-500 hover:bg-slate-100"
                      }`}>
                      {conditionalRules.length > 0 ? `Rules (${conditionalRules.length})` : "Conditions"}
                    </button>

                  </div>
                );
              })()}
              {/* FIND & REPLACE */}
              {showFindReplace && (
                <div className={`mb-4 p-4 rounded-2xl border ${borderColor} ${isDark ? "bg-[#070f1e]" : "bg-slate-50"} flex flex-wrap items-center gap-3`}>
                  <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <input
                      type="text"
                      value={findText}
                      onChange={e => setFindText(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleFind()}
                      placeholder="Find..."
                      className={`flex-1 px-3 py-2 rounded-xl text-xs border outline-none ${inputCls}`}
                    />
                    <input
                      type="text"
                      value={replaceText}
                      onChange={e => setReplaceText(e.target.value)}
                      placeholder="Replace with..."
                      className={`flex-1 px-3 py-2 rounded-xl text-xs border outline-none ${inputCls}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={handleFind}
                      className={`px-3 py-2 rounded-xl font-black text-[10px] uppercase ${btnPrimary}`}>
                      Find
                    </button>
                    <button onClick={handleReplaceOne}
                      className={`px-3 py-2 rounded-xl font-black text-[10px] uppercase ${btnGhost}`}
                      disabled={findResults.length === 0}>
                      Replace
                    </button>
                    <button onClick={handleReplaceAll}
                      className={`px-3 py-2 rounded-xl font-black text-[10px] uppercase ${btnGhost}`}
                      disabled={findResults.length === 0}>
                      Replace All
                    </button>
                    {findResults.length > 0 && (
                      <span className={`text-[10px] font-black ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                        {findIndex + 1}/{findResults.length} found
                      </span>
                    )}
                    {findText && findResults.length === 0 && (
                      <span className="text-[10px] font-black text-red-400">Not found</span>
                    )}
                    <button onClick={() => { setShowFindReplace(false); setFindText(""); setReplaceText(""); setFindResults([]); }}
                      className="text-slate-400 hover:text-slate-200 text-lg">✕</button>
                  </div>
                </div>
              )}

              {/* TITLE + SEARCH */}
              <div className={`${bgSubtle} border ${borderColor} px-4 py-3.5 rounded-2xl mb-4 flex flex-col md:flex-row justify-between items-center gap-3`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                <input type="text"
                  className={`bg-transparent border-none outline-none placeholder:text-slate-700/30 flex-1 min-w-0 text-${activeTab?.titleSize || "xl"}`}
                  placeholder="Untitled Document"
                  value={tableTitle}
                  onChange={e => setTableTitle(e.target.value)}
                  style={{
                    color: activeTab?.titleColor || "#f1f5f9",
                    fontWeight: activeTab?.titleBold ? "900" : "700",
                    fontStyle: activeTab?.titleItalic ? "italic" : "normal",
                  }}
                />
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Color picker */}
                  <div className="relative">
                    <input
                      type="color"
                      value={activeTab?.titleColor || "#6366f1"}
                      onChange={e => updateActiveTab({ titleColor: e.target.value })}
                      className="w-6 h-6 rounded-lg border-0 cursor-pointer opacity-0 absolute inset-0"
                      title="Title color"
                    />
                    <div className="w-6 h-6 rounded-lg border-2 cursor-pointer transition-all"
                      style={{ backgroundColor: activeTab?.titleColor || "#6366f1", borderColor: activeTab?.titleColor || "#6366f1" }}>
                    </div>
                  </div>
                  {/* Bold */}
                  <button
                    onClick={() => updateActiveTab({ titleBold: !activeTab?.titleBold })}
                    className={`w-6 h-6 rounded-lg text-[10px] font-black border transition-all ${activeTab?.titleBold ? "bg-indigo-600 border-indigo-500 text-white" : isDark ? "border-[#1e3a5f] text-slate-400 hover:bg-white/5" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    B
                  </button>
                  {/* Italic */}
                  <button
                    onClick={() => updateActiveTab({ titleItalic: !activeTab?.titleItalic })}
                    className={`w-6 h-6 rounded-lg text-[10px] italic border transition-all ${activeTab?.titleItalic ? "bg-indigo-600 border-indigo-500 text-white" : isDark ? "border-[#1e3a5f] text-slate-400 hover:bg-white/5" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    I
                  </button>
                  {/* Font size */}
                  <select
                    value={activeTab?.titleSize || "xl"}
                    onChange={e => updateActiveTab({ titleSize: e.target.value })}
                    className={`h-6 px-1 rounded-lg text-[9px] font-black border outline-none ${isDark ? "bg-[#050d1f] border-[#1e3a5f] text-slate-300" : "bg-white border-slate-200"}`}>
                    <option value="sm">S</option>
                    <option value="base">M</option>
                    <option value="xl">L</option>
                    <option value="3xl">XL</option>
                    <option value="5xl">XXL</option>
                  </select>
                </div>
              </div>
                <input type="text" className={`border rounded-xl px-4 py-2 outline-none text-xs w-full md:w-52 transition-all flex-shrink-0 ${inputCls}`}
                  placeholder="🔍 Search records..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>

              {/* BULK ACTIONS */}
              {selectedRows.size > 0 && (
  <div className={`mb-4 flex flex-wrap items-center gap-2 px-5 py-3 rounded-2xl border ${isDark ? "bg-[#132844] border-[#29456b]" : "bg-[#f5f8fd] border-[#c8d7ec]"}`}>
    <span className="text-[11px] font-black text-[#3978d8] mr-2">
      {selectedRows.size} row(s) selected
    </span>

    <button onClick={duplicateSelectedRows} className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase ${btnGhost}`}>
      Duplicate
    </button>

    <button onClick={fillDownSelectedRows} className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase ${btnGhost}`}>
      Fill Down
    </button>

    <button onClick={deleteSelectedRows} className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase ${btnDanger}`}>
      Delete
    </button>

    <button onClick={() => setSelectedRows(new Set())} className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase ${btnGhost}`}>
      Clear
    </button>
  </div>
)}

              {/* ===== TABLE ===== */}
              <div className="print-table-area bg-white rounded-2xl border border-slate-300 shadow-inner overflow-hidden" style={{ width: "100%", maxWidth: "100%" }}>
                <div className="ss-scroll-container" ref={tableRef} onScroll={e => setGridScrollTop(e.currentTarget.scrollTop)} style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh", WebkitOverflowScrolling: "touch" }}>
                  <table className="border-collapse" style={{ tableLayout: "auto", width: "100%", minWidth: "100%" }}>
                    <thead className={`${tableHeadCls} sticky top-0 z-10 border-b ${borderColor}`}>
                      <tr>
                        <th className={`px-3 py-2 border-r ${borderColor} sticky left-0 z-20`}
                          style={{ background: isDark ? "#070c17" : "#f8fafc", width: "44px" }}>
                          <input type="checkbox"
                            checked={selectedRows.size === rows.length && rows.length > 0}
                            onChange={e => setSelectedRows(e.target.checked ? new Set(rows.map((_, i) => i)) : new Set())}
                            className="accent-indigo-500" />
                        </th>
                        {columns.map((col, i) => {
                          const colFmt = columnFormats[col];
                          if (hiddenCols.has(col)) return null;
                          return (
                            <th key={i} draggable={col !== "ID"}
                              onDragStart={() => handleColDragStart(i)}
                              onDragOver={e => handleColDragOver(e, i)}
                              onDrop={() => handleColDrop(i)}
                              className={`py-2 font-black text-[10px] uppercase border-r ${borderColor} tracking-widest transition-all ${dragOverColIdx === i ? "bg-indigo-500/10" : ""}`}
                              style={{
                                minWidth: colWidths[col] ? `${colWidths[col]}px` : getColMinWidth(col),
                                width: col === "ID" ? "60px" : (colWidths[col] ? `${colWidths[col]}px` : undefined),
                                backgroundColor: colFmt?.bgColor ? colFmt.bgColor + "18" : undefined,
                                padding: "0 6px",
                                cursor: col !== "ID" ? "grab" : "default",
                                color: "#1a1a1a",
                                position: "relative",
                                textAlign: colFmt?.textAlign || "left",
                              }}>
                              <span onClick={e => handleColumnHeaderClick(e, col)}
                                className="cursor-pointer transition-colors flex items-center gap-2 hover:text-indigo-400 group select-none"
                                onMouseDown={e => e.stopPropagation()}
                                style={{ userSelect: "none", display: "flex", visibility: "visible" }}>
                                <span
                                      style={{
                                      fontWeight: 900,
                                      letterSpacing: "0.04em",
                                      color: "#1a1a1a",
                                      visibility: "visible",
                                      opacity: 1,
                                      display: "inline-block"
                                    }}
                                >
                                    {col}
                                </span>
                                {columnTypes[col] && (
                                  <span className="text-indigo-400/60 text-[10px]">
                                    {columnTypes[col] === "dropdown" ? "⊞" : columnTypes[col] === "date" ? "◷" : columnTypes[col] === "checkbox" ? "☑" : columnTypes[col] === "email" ? "@" : ""}
                                  </span>
                                )}
                                {col !== "ID" && <span className="opacity-0 group-hover:opacity-50 text-indigo-500 text-[9px]">▾</span>}
                              </span>                              
                              {col !== "ID" && (
                                <div
                                  onMouseDown={e => handleColResizeStart(e, col)}
                                  className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize group/resize flex items-center justify-center"
                                  style={{ zIndex: 20 }}>
                                  <div className="w-0.5 h-4 rounded-full opacity-0 group-hover/resize:opacity-100 transition-opacity"
                                    style={{ backgroundColor: "#d4af37" }} />
                                </div>
                              )}
                            </th>
                          );
                        })}
                        <th className={`${tableHeadCls}`} style={{ width: "0px", padding: 0 }}></th>      
                      </tr>
                    </thead>

                    <tbody className={`divide-y ${dividerCls}`}>
                      {gridTopSpacerH > 0 && (
                        <tr aria-hidden="true" style={{ height: `${gridTopSpacerH}px` }}>
                          <td colSpan={gridVisibleColCount} style={{ padding: 0, border: "none" }} />
                        </tr>
                      )}
                      {gridVisibleRows.map(({ row, originalIndex }) => (
                        <tr key={originalIndex} draggable
                          onDragStart={() => handleRowDragStart(originalIndex)}
                          onDragOver={e => handleRowDragOver(e, originalIndex)}
                          onDrop={() => handleRowDrop(originalIndex)}
                          onDoubleClick={() => autoFitRowHeight(originalIndex)}
                          className={`transition-all group/row ${getRowStyle(row)} ${dragOverRowIdx === originalIndex ? "opacity-40" : ""} ${isDark ? "hover:bg-white/[0.012]" : "hover:bg-slate-50/80"}`}
                          style={{
                            cursor: "grab",
                            height: `${rowHeights[originalIndex] || 26}px`,
                            maxHeight: `${rowHeights[originalIndex] || 26}px`,
                            overflow: "hidden",
                            transition: "height 0.15s ease, max-height 0.15s ease"
                          }}>
                          <td className={`px-3 py-0 border-r ${borderColor} sticky left-0`}
                            style={{ background: isDark ? "#0a1018" : "#ffffff", zIndex: 5, width: "44px", position: freezeCol ? "sticky" : "relative", left: freezeCol ? 0 : undefined }}>
                            <input type="checkbox" checked={selectedRows.has(originalIndex)}
                              onChange={e => { const s = new Set(selectedRows); e.target.checked ? s.add(originalIndex) : s.delete(originalIndex); setSelectedRows(s); }}
                              className="accent-indigo-500" />
                          </td>
                          {columns.map((col, cIdx) => {
                            const cell    = row[col];
                            const cellObj = cell && typeof cell === "object" ? cell : null;
                            const colFmt  = columnFormats[col];
                            const tdBg    = cellObj?.bgColor || (colFmt?.bgColor ? colFmt.bgColor + "12" : undefined);
                            if (hiddenCols.has(col)) return null;
                            return (
                              <td key={cIdx} data-cell={`r${originalIndex}_${col}`} className={`border-r ${borderColor} p-0 relative group/cell transition-colors align-top ${
                                isCellInSelection(originalIndex, cIdx)
                                  ? isDark
                                    ? "bg-blue-600/20 ring-1 ring-inset ring-blue-500/40"
                                    : "bg-blue-100/60 ring-1 ring-inset ring-blue-400/40"
                                  : ""
                              }`}
                                style={{ minWidth: getColMinWidth(col), backgroundColor: tdBg,position: cIdx < frozenCols ? "sticky" : "relative",
                                left: cIdx < frozenCols ? `${44 + cIdx * (colWidths[columns[cIdx]] || 120)}px` : undefined,
                                zIndex: cIdx < frozenCols ? 4 : undefined,
                                background: cIdx < frozenCols ? (isDark ? "#0a1018" : "#ffffff") : (tdBg || undefined), overflow: "visible", whiteSpace: (cell && typeof cell === "object" ? cell.whiteSpace : null) || "nowrap", userSelect: "text", WebkitUserSelect: "text", cursor: "text", verticalAlign: "middle", lineHeight: "1" }}
                                onMouseDown={e => {
                                  e.stopPropagation();
                                  if (e.shiftKey && selectionStart) {
                                    e.preventDefault();
                                    setSelectionEnd({ rIdx: originalIndex, cIdx });
                                  } else {
                                    setSelectionStart({ rIdx: originalIndex, cIdx });
                                    setSelectionEnd({ rIdx: originalIndex, cIdx });
                                    setIsSelecting(true);
                                  }
                                  setSelectedCell({ rIdx: originalIndex, col });
                                }}
                                onMouseEnter={() => {
                                  if (isSelecting) setSelectionEnd({ rIdx: originalIndex, cIdx });
                                }}
                                onMouseUp={() => setIsSelecting(false)}
                                onContextMenu={e => {
                                  e.preventDefault();
                                  const menu = document.createElement("div");
                                  menu.style.cssText = `position:fixed;top:${e.clientY}px;left:${e.clientX}px;z-index:9999;background:${isDark?"#08122a":"#fff"};border:1px solid ${isDark?"#d4af3722":"#e2e8f0"};border-radius:12px;padding:6px;min-width:140px;box-shadow:0 8px 32px rgba(0,0,0,0.4)`;
                                  const btnStyle = `display:block;width:100%;text-align:left;padding:8px 12px;font-size:10px;font-weight:900;letter-spacing:0.08em;text-transform:uppercase;border-radius:8px;cursor:pointer;color:${isDark?"#94a3b8":"#475569"};background:transparent;border:none;`;
                                  const existingComment = (typeof row[col] === "object" ? row[col]?.comment : "") || "";
                                  menu.innerHTML = `<button style="${btnStyle}" id="nm-copy">📋 Copy</button><button style="${btnStyle}" id="nm-paste">📌 Paste</button><button style="${btnStyle}" id="nm-clear">🧹 Clear</button><hr style="border:none;border-top:1px solid ${isDark?"#1e3a5f":"#e2e8f0"};margin:4px 0"/><button style="${btnStyle}" id="nm-comment">💬 ${existingComment ? "Edit Comment" : "Add Comment"}</button>${existingComment ? `<button style="${btnStyle};color:#ef4444" id="nm-del-comment">🗑 Remove Comment</button>` : ""}`;
                                  document.body.appendChild(menu);
                                  document.getElementById("nm-copy").onclick = () => { handleCopyCell(originalIndex, col); document.body.removeChild(menu); };
                                  document.getElementById("nm-paste").onclick = () => { handlePasteCell(originalIndex, col); document.body.removeChild(menu); };
                                  document.getElementById("nm-clear").onclick = () => { handleCellChange(originalIndex, col, ""); document.body.removeChild(menu); };
                                  document.getElementById("nm-comment").onclick = () => { setCommentCell({ rIdx: originalIndex, col }); setCommentText(existingComment); document.body.removeChild(menu); };
                                  if (existingComment) document.getElementById("nm-del-comment").onclick = () => { handleSaveComment(originalIndex, col, ""); document.body.removeChild(menu); };
                                  const close = () => { if(document.body.contains(menu)) document.body.removeChild(menu); document.removeEventListener("click", close); };
                                  setTimeout(() => document.addEventListener("click", close), 100);
                                }}>
                                {renderCell(row, originalIndex, col, cIdx)}
                                {getCellCollaborators(originalIndex, col).map(c => (
                                  <div key={c.uid} style={{ position: "absolute", inset: 0, pointerEvents: "none", border: `2px solid ${c.color || "#6366F1"}`, borderRadius: "2px", zIndex: 6 }}>
                                    <span style={{ position: "absolute", top: "-18px", left: "-2px", background: c.color || "#6366F1", color: "#fff", fontSize: "9px", fontWeight: 900, padding: "1px 5px", borderRadius: "4px 4px 4px 0", whiteSpace: "nowrap" }}>
                                      {(c.name || "U").slice(0, 8)}
                                    </span>
                                  </div>
                                ))}
                                {col !== "ID" && (
                                  <button title="Format cell"
                                    onClick={e => { e.stopPropagation(); setEditingFormat({ col, rowIdx: originalIndex, scope: "cell" }); }}
                                    className="absolute top-1 right-1 opacity-0 group-hover/cell:opacity-100 transition-opacity bg-indigo-500/15 hover:bg-indigo-500 text-indigo-300 hover:text-white rounded-md px-1.5 text-[8px] font-black z-10 uppercase tracking-wider"
                                    style={{ pointerEvents: "auto" }}>
                                    fx
                                  </button>
                                )}
                                {(typeof row[col] === "object" && row[col]?.comment) && (
                                  <div
                                    className="absolute top-0 right-0 w-0 h-0 z-20"
                                    style={{ borderStyle: "solid", borderWidth: "0 8px 8px 0", borderColor: "transparent #f59e0b transparent transparent" }}
                                    onMouseEnter={e => setHoverComment({ rIdx: originalIndex, col, text: row[col].comment, x: e.clientX, y: e.clientY })}
                                    onMouseLeave={() => setHoverComment(null)}
                                  />
                                )}
                                {shareId && stickyNotes[`r${originalIndex}_${col}`] && (
                                  <div
                                    title={`${stickyNotes[`r${originalIndex}_${col}`].author}: ${stickyNotes[`r${originalIndex}_${col}`].text}`}
                                    onClick={e => {
                                      e.stopPropagation();
                                      setActiveStickyNote({ rIdx: originalIndex, col });
                                      setStickyNoteText(stickyNotes[`r${originalIndex}_${col}`].text || "");
                                    }}
                                    className="absolute -top-1.5 -left-1.5 z-20 cursor-pointer"
                                    style={{
                                      width: 16, height: 16, borderRadius: "3px",
                                      background: stickyNotes[`r${originalIndex}_${col}`].color || "#F59E0B",
                                      boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      fontSize: "9px", transform: "rotate(-6deg)",
                                    }}>
                                    📌
                                  </div>
                                )}
                                {shareId && !stickyNotes[`r${originalIndex}_${col}`] && (
                                  <button
                                    title="Sticky Note যোগ করো"
                                    onClick={e => { e.stopPropagation(); setActiveStickyNote({ rIdx: originalIndex, col }); setStickyNoteText(""); }}
                                    className="absolute -top-1.5 -left-1.5 opacity-0 group-hover/cell:opacity-100 transition-opacity z-20 text-[10px]"
                                    style={{ width: 16, height: 16, borderRadius: "3px", background: isDark ? "#1e293b" : "#e2e8f0", pointerEvents: "auto" }}>
                                    📌
                                  </button>
                                )}
                              </td>
                            );
                          })}
                          <td className={`border-r ${borderColor} p-2`}></td>
                          
                          
                        </tr>
                      ))}
                      {gridBottomSpacerH > 0 && (
                        <tr aria-hidden="true" style={{ height: `${gridBottomSpacerH}px` }}>
                          <td colSpan={gridVisibleColCount} style={{ padding: 0, border: "none" }} />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Table Footer */}
                <div className={`px-3 py-1 border-t ${borderColor} ${bgSubtle} flex flex-wrap justify-between items-center gap-2`}>
                <div className="flex flex-wrap items-center gap-2">             
                  {(hiddenRows.size > 0 || hiddenCols.size > 0) && (
                    <div className="flex items-center gap-2">
                      {hiddenRows.size > 0 && (
                        <button onClick={() => setHiddenRows(new Set())}
                          className={`px-3 py-2 rounded-xl font-black text-[10px] uppercase ${isDark ? "bg-amber-500/10 text-amber-400 hover:bg-amber-500/20" : "bg-amber-50 text-amber-600"}`}>
                          Show {hiddenRows.size} hidden rows
                        </button>
                      )}
                      {hiddenCols.size > 0 && (
                        <button onClick={() => setHiddenCols(new Set())}
                          className={`px-3 py-2 rounded-xl font-black text-[10px] uppercase ${isDark ? "bg-amber-500/10 text-amber-400 hover:bg-amber-500/20" : "bg-amber-50 text-amber-600"}`}>
                          Show {hiddenCols.size} hidden cols
                        </button>
                      )}
                    </div>
                  )}
                  
                </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-[9px] ${textSub} font-mono`}>{rows.length} rows · {columns.length} cols</span>
                    {copiedCell && (
                      <span className="text-[9px] text-yellow-400 font-black flex items-center gap-1">
                        📋 Copied: <span className="font-mono">{String(copiedCell.value).slice(0, 12)}</span>
                        <button onClick={() => setCopiedCell(null)} className="text-[8px] hover:opacity-60">✕</button>
                      </span>
                    )}
                    {searchTerm && <span className="text-[9px] text-indigo-400 font-black">{filteredRows.length} match{filteredRows.length !== 1 ? "es" : ""}</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ======== SIDEBAR ======== */}
        <div className="lg:col-span-1 flex flex-col gap-3 md:gap-4 order-last lg:order-none">

          {/* History Panel */}
          <div className={`${bgPanel} border ${borderColor} rounded-[2rem] p-5 shadow-xl flex flex-col flex-1`}>
            <div className={`flex justify-between items-center mb-5 pb-4 border-b ${borderColor}`}>
              <div>
                <h2 className="text-[11px] font-black text-sky-400 uppercase tracking-[0.2em]">Vault History</h2>
                {!isPremium && (
                  <p className={`text-[8px] ${textSub} font-black mt-0.5`}>{history.length}/{FREE_TABLE_LIMIT} saved</p>
                )}
              </div>
              {history.length > 0 && (
                <button onClick={clearAllHistory} className="text-[9px] font-black uppercase text-red-400 hover:text-red-300 transition-colors">Clear</button>
              )}
            </div>

            <div className="space-y-2 overflow-y-auto flex-1" style={{ scrollbarWidth: "none" }}>
              {history.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-[10px] font-black uppercase tracking-widest ${isDark ? "bg-[#132844] text-[#9fb3d1]" : "bg-[#e8f0fb] text-[#36516f]"}`}>
                    Empty
                  </div>
                  <p className={`text-[10px] ${textSub} font-black uppercase tracking-widest text-center`}>No saved tables</p>
                  <p className={`text-[9px] ${textSub} text-center`}>Press Save to store your current sheet</p>
                </div>
              ) : (
                history.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    
                    <div
                      onClick={() => handleHistoryClick(item)}
                      className={`p-3.5 rounded-2xl border transition-all duration-150 cursor-pointer ${
                        currentTableId === item.id
                          ? `border-sky-500/40 ${isDark ? "bg-sky-500/5" : "bg-sky-50"}`
                          : `${borderColor} ${isDark ? "bg-[#0a1018]/60 hover:border-slate-700" : "bg-slate-50 hover:border-slate-300"}`
                      }`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0 pr-2">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <h4 className={`font-black text-xs truncate uppercase ${currentTableId === item.id ? "text-sky-400" : textMain}`}>
                              {item.title}
                            </h4>
                            {/* Badge indicating if this is the currently loaded + saved table */}
                            {currentTableId === item.id && (
                              <span className="text-[7px] font-black px-1.5 py-0.5 rounded-full bg-sky-500/20 text-sky-400 uppercase flex-shrink-0">Active</span>
                            )}
                          </div>
                          <p className={`text-[8px] ${textSub} mt-0.5 font-mono`}>{item.date}</p>
                          <p className={`text-[8px] ${textSub} mt-0.5`}>{item.columns?.length} cols · {item.rows?.length} rows</p>
                        </div>
                        <button onClick={e => deleteHistoryItem(e, item.id)}
                          className="text-red-500/25 hover:text-red-500 transition-colors text-sm flex-shrink-0">Delete</button>
                      </div>
                    </div>
                  </React.Fragment>
                ))
              )}
            </div>

            {!isPremium && history.length >= FREE_TABLE_LIMIT && (
              <button onClick={() => setShowUpgrade(true)}
                className="mt-4 w-full py-3 rounded-2xl font-black text-[11px] uppercase bg-gradient-to-r from-pink-600 to-red-500 hover:from-pink-500 hover:to-red-400 text-white transition-all shadow-sm shadow-pink-900/20">
                ⭐ Upgrade for Unlimited
              </button>
            )}
          </div>

          {/* Template Quick-Access */}
          <div className={`${bgPanel} border ${borderColor} rounded-[2rem] p-5 shadow-xl`}>
            <h2 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-4 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
              Quick Templates
            </h2>
            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.slice(0, 6).map(tmpl => (
                <button key={tmpl.id}
                  onClick={() => { if (tmpl.id === "blank") addNewTab(null); else addNewTab(tmpl); }}
                  className={`p-2.5 rounded-xl border text-left transition-all duration-150 group ${borderColor} ${isDark ? "bg-[#0a1018] hover:bg-[#0f1929]" : "bg-slate-50 hover:bg-white"}`}>
                  <div className="text-[8px] font-black uppercase tracking-widest mb-1">{tmpl.icon}</div>
                  <p className={`text-[9px] font-black uppercase tracking-wide ${textMain} truncate`}>{tmpl.name}</p>
                </button>
              ))}
            </div>
            <button onClick={() => setShowTemplates(true)}
              className={`mt-3 w-full py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest border transition-all duration-150 ${btnGhost}`}>
              View All Templates
            </button>
          </div>
        </div>
      </div>

      {/* ============================================================
          GLOBAL STYLES
          ============================================================ */}
      <style>{`
        * { -webkit-user-select: none !important; -moz-user-select: none !important; user-select: none !important; }
        td, td input, td select { -webkit-user-select: text !important; -moz-user-select: text !important; user-select: text !important; }
        th { -webkit-user-select: none !important; user-select: none !important; }
        th span.select-none { -webkit-user-select: none !important; user-select: none !important; }
        .flex-1.overflow-y-auto::-webkit-scrollbar { display: none; }
        .ss-scroll-container tbody tr td { padding-top: 0 !important; padding-bottom: 0 !important; vertical-align: middle !important; }
        .ss-scroll-container tbody tr td input { padding-top: 0 !important; padding-bottom: 0 !important; }
        /* Mobile Responsive */
        @media (max-width: 768px) {
          .mobile-hide { display: none !important; }
          .mobile-full { width: 100% !important; }
          .mobile-stack { flex-direction: column !important; }
          .mobile-small { font-size: 9px !important; padding: 4px 8px !important; }
          .mobile-scroll { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
        }

        .ss-scroll-container {
          scrollbar-width: thin;
          scrollbar-color: rgba(99,102,241,0.3) transparent;
        }
        .ss-scroll-container::-webkit-scrollbar { display: block !important; height: 6px; width: 6px; }
        .ss-scroll-container::-webkit-scrollbar-track {
          background: ${isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"};
          border-radius: 99px; margin: 0 12px;
        }
        .ss-scroll-container::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #6366f1aa, #22d3eeaa);
          border-radius: 99px;
        }
        .ss-scroll-container::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(90deg, #6366f1, #22d3ee);
        }
        .ss-scroll-container::-webkit-scrollbar-corner { background: transparent; }

        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: ${isDark ? "invert(1) opacity(0.35)" : "opacity(0.4)"};
          cursor: pointer;
        }
        select option {
          background: ${isDark ? "#0f1929" : "#fff"};
          color: ${isDark ? "#e2e8f0" : "#0f172a"};
        }
        table input, table select { line-height: 1.6; letter-spacing: 0.01em; }
        table td input[type="text"], table td input[type="date"] { cursor: text; }
        table td { overflow: visible !important; }
        table td input { white-space: nowrap; }
        thead th span { visibility: visible !important; opacity: 1 !important; display: flex !important; }
        button:active { transform: scale(0.97); }
        button:active.no-scale { transform: none; }

        /* Row hover highlight */
        .ss-scroll-container tbody tr:hover td {
          background-color: ${isDark ? "rgba(99,130,255,0.07)" : "rgba(59,100,220,0.06)"} !important;
          transition: background-color 0.12s ease;
        }

        /* Print styles */
        @media print {
          body * { visibility: hidden !important; }
          .ss-scroll-container { overflow: visible !important; max-height: none !important; height: auto !important; }
          .print-table-area, .print-table-area * { visibility: visible !important; }
          .print-table-area {
            position: fixed !important;
            top: 0; left: 0;
            width: 100vw !important;
            padding: 20px !important;
            background: white !important;
          }
          .print-table-area table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 11px !important;
          }
          .print-table-area th, .print-table-area td {
            border: 1px solid #ccc !important;
            padding: 4px 8px !important;
            color: #000 !important;
            background: white !important;
          }
          .print-table-area thead th {
            background: #f0f0f0 !important;
            font-weight: bold !important;
          }
          .print-table-area input {
            border: none !important;
            background: transparent !important;
            font-size: 11px !important;
            color: #000 !important;
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
}

export default App;






